import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import InterviewSchedulePopup from './InterviewSchedulePopup';
import VoiceScreenPopup from './VoiceScreenPopup';
import MeetingCreateDialog from './MeetingCreateDialog';
import MeetingSchedulingDialog from './MeetingSchedulingDialog';
import InterviewRoomDialog from './InterviewRoomDialog';
import { downloadResume } from './ResumePreview';
import {
  cancelLiveMeetBooking,
  fetchLiveMeetBooking,
  fetchLiveMeetInterviewRecording,
  fetchLiveMeetTranscriptPdfUrl,
} from './liveMeetApi';
import {
  generateLiveInterviewFinalSummary,
  getLiveInterviewEligibleApplications,
} from '../../../LiveInterview/api/liveInterviewApi';
import {
  getInterviewRoomStatusBadgeClassName,
  isInstantLiveMeetBooking,
  resolveInterviewRoomStatus,
} from './interviewRoomBookingUtils';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import TestCardsMinimal from './TestCards';
import { API_BASE_URL, getApiUrl, getApiUrlsWithFallback } from '../../../config/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { DateTimePicker } from '@/Components/ui/date-time-picker';
import { Textarea } from '@/Components/ui/textarea';
import InterviewerFeedbackSummary from './InterviewerFeedbackSummary';
import {
  fetchInterviewerFeedbackByBooking,
  getInterviewerFeedbackActorEmail,
} from './interviewerFeedbackApi';
import { Badge } from '@/Components/ui/badge';
import { Heading, Text } from '@/Components/ui/typography';
import { Separator } from '@/Components/ui/separator';
import { Stack } from '@/Components/ui/layout';
import { Inline } from '@/Components/ui/layout';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/Components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/Components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/Components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Components/ui/tooltip';
import { Avatar, AvatarFallback } from '@/Components/ui/avatar';
import {
  CheckCircle2, XCircle, AlertCircle, Video, FileText, Calendar,
  UserCheck, X, Copy, ExternalLink, Loader2, Edit, Save, Phone,
  PhoneCall,
  Mail, User, Check, ChevronDown, ChevronUp, Clock, MoreHorizontal,
  Download, TrendingUp, AlertTriangle, Sparkles, Circle, CalendarClock,
  Target, Zap, Info, CheckCircle, Gamepad2, ShieldCheck, ShieldAlert, RefreshCw, MessageSquare,
  GraduationCap, ClipboardList
} from 'lucide-react';
import { Checkbox } from '@/Components/ui/checkbox';
// Applications Copilot — `useCopilot()` returns a no-op default outside the
// provider, so the "Ask Copilot" button simply does not render on other pages.
import { useCopilot } from './copilot/CopilotContext';
import { COPILOT_POSTER_SRC } from './copilot/CopilotAvatar';
// "Uploaded by …" chip — viewer-aware (shows "Myself" when the current
// company-admin is the same person who uploaded the resume).
import UploadSourceBadge from '@/Components/UploadSourceBadge';
// 8-category AI Score Breakdown panel — shown at the TOP of the AI
// Insights modal (full version) AND inline on every candidate card
// (CompactScoreBreakdown — just name + bar + score, no descriptions).
// Renders nothing when the candidate has no breakdown data, so legacy
// candidates' cards stay identical.
import ScoreBreakdownSection, {
  CompactScoreBreakdown,
} from './components/ScoreBreakdownSection';
import { collegeGateStatus } from './collegeGate';
// AIInsightCardExpandable was previously defined inline below in this
// file; it's now extracted to a shared component so the company-side
// Resume Details modal can render the same card style without
// duplicating the variant config and expand-toggle logic.
import AIInsightCardExpandable from './components/AIInsightCardExpandable';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/Components/ui/select';
import { cn } from '@/lib/utils';
import { getColor } from '@/config/theme';
import { toast } from 'sonner';
import { useCredits } from '../../Hooks/useCredits';
import { creditsApi } from '../../api/creditsApi';
import { useCompanyAccess } from '@/HireWithUs/Context/CompanyAccessContext';

const resolveGameBaseUrl = () => {
  if (process.env.REACT_APP_GAME_BASE_URL) {
    return process.env.REACT_APP_GAME_BASE_URL;
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:5173';
    }
  }
  return API_BASE_URL;
};

// AI review summary rendered under the video-resume player. The analysis is
// generated when the CANDIDATE records their video (Whisper transcript +
// rubric review); every metric shown is computed from the real transcript.
// Renders nothing for candidates who never ran the analysis.
const VideoAiSummary = ({ analysis }) => {
  if (!analysis || analysis.score == null) return null;
  const strengths = analysis.strengths || [];
  const improvements = analysis.improvements || [];
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-foreground">AI review of this video</span>
        <span className="text-sm font-mono font-semibold text-primary">{analysis.score}/100</span>
      </div>
      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
        {analysis.duration_seconds != null && <span>{Math.round(analysis.duration_seconds)}s</span>}
        {analysis.wpm != null && <span>· {Math.round(analysis.wpm)} words/min</span>}
        {analysis.filler_count != null && <span>· {analysis.filler_count} filler words</span>}
      </div>
      {strengths.length > 0 && (
        <ul className="space-y-0.5">
          {strengths.map((s, i) => (
            <li key={`s${i}`} className="text-xs text-muted-foreground">
              <span className="text-green-600 mr-1">+</span>{s}
            </li>
          ))}
        </ul>
      )}
      {improvements.length > 0 && (
        <ul className="space-y-0.5">
          {improvements.map((s, i) => (
            <li key={`i${i}`} className="text-xs text-muted-foreground">
              <span className="text-amber-600 mr-1">→</span>{s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const getStoredMeetingOwnerEmail = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  const activeUserCandidates = [
    sessionStorage.getItem('recruiter_email'),
    sessionStorage.getItem('admin_email'),
  ]
    .map((value) => String(value || '').trim().toLowerCase())
    .filter(Boolean);

  if (activeUserCandidates.length) {
    return activeUserCandidates[0];
  }

  return '';
};

const HUMAN_INTERVIEW_TABS = [
  { id: 'scheduling', label: 'Interview Scheduling' },
  { id: 'activity', label: 'Activity' },
  { id: 'insights', label: 'Insights' },
];

const TERMINAL_CANCEL_BOOKING_STATUSES = new Set([
  'cancelled',
  'canceled',
  'declined_by_candidate',
  'declined_by_interviewer',
  'completed',
  'ended',
  'closed',
]);

// Same cancel-eligibility rules as InterviewRoomDialog Activity actions.
function canCancelLiveMeetFromCard(booking) {
  if (!booking?.booking_id) return false;
  if (isInstantLiveMeetBooking(booking)) return false;
  const roomStatus = resolveInterviewRoomStatus(booking);
  if (['cancelled', 'completed', 'expired', 'not_joined'].includes(roomStatus.key)) {
    return false;
  }
  if (booking.meeting_completed) return false;
  const normalizedStatus = String(booking.status || '').trim().toLowerCase();
  return !TERMINAL_CANCEL_BOOKING_STATUSES.has(normalizedStatus);
}

function HumanInterviewTrigger({ variant = 'full', onOpen }) {
  const triggerClassName =
    variant === 'compact'
      ? 'h-7 px-2 text-xs'
      : 'h-8 sm:h-7 text-xs px-2 w-full sm:w-auto';

  return (
    <Button
      variant={variant === 'compact' ? 'ghost' : 'outline'}
      size="sm"
      className={triggerClassName}
      onClick={onOpen}
    >
      <User className={cn('w-3.5 h-3.5', variant === 'full' && 'mr-1')} />
      schedule Interview
    </Button>
  );
}

function HumanInterviewHub({
  open,
  onClose,
  activeTab,
  onTabChange,
  candidate,
  jobDetails,
  liveMeetBooking,
  refreshLiveMeetBooking,
  onBookingCreated,
  onOpenAutomatedFlow,
}) {
  const insightsBookingId = String(liveMeetBooking?.booking_id || '').trim();
  const candidateDisplayName =
    candidate?.name || candidate?.full_name || candidate?.candidate_email || 'Candidate';
  const candidateInitial = String(candidateDisplayName).charAt(0).toUpperCase() || 'C';
  const { can } = useCompanyAccess();
  // Without candidates.contact the screens never show an email (the server already
  // leaves out phone numbers).
  const candidateEmailLine = can('candidates.contact')
    ? candidate?.email || candidate?.candidate_email || ''
    : '';
  const jobTitleLine =
    jobDetails?.job_title || jobDetails?.title || candidate?.job_title || '';

  useEffect(() => {
    if (!open || (activeTab !== 'activity' && activeTab !== 'insights')) return;
    refreshLiveMeetBooking?.(undefined, { includeTimeline: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeTab]);

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose?.()}>
      <DialogContent
        className="flex max-h-[92vh] w-[min(100%,960px)] max-w-none flex-col gap-0 overflow-hidden p-0"
      >
        <DialogTitle className="sr-only">
          Human Interview · {candidateDisplayName}
        </DialogTitle>

        {/* Header — candidate identity (no live-status pill; see Activity tab). */}
        <div className="shrink-0 border-b border-slate-200 bg-white px-6 py-4">
          <div className="flex items-start gap-4">
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-slate-800 to-slate-600 text-base font-semibold text-white"
              aria-hidden
            >
              {candidateInitial}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-700">
                <User className="h-3.5 w-3.5" aria-hidden />
                Human Interview
              </div>
              <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2">
                <h2 className="truncate text-lg font-semibold leading-tight text-slate-900">
                  {candidateDisplayName}
                </h2>
                {jobTitleLine ? (
                  <span className="truncate text-sm text-slate-500">
                    · {jobTitleLine}
                  </span>
                ) : null}
              </div>
              {candidateEmailLine ? (
                <div className="mt-0.5 truncate text-xs text-slate-500">
                  {candidateEmailLine}
                </div>
              ) : null}
            </div>
          </div>

          {/* Clean underline tab nav — sits inside the header for a calmer
              two-tier look (Linear / Notion / Stripe pattern). */}
          <div
            role="tablist"
            aria-label="Human interview sections"
            className="mt-4 -mb-4 flex gap-1 border-b border-transparent"
          >
            {HUMAN_INTERVIEW_TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => onTabChange(tab.id)}
                  className={cn(
                    'relative -mb-px border-b-2 px-3 pb-3 pt-1 text-sm font-semibold transition-colors',
                    isActive
                      ? 'border-emerald-600 text-slate-900'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  )}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50/40">
          <div className={activeTab === 'scheduling' ? 'block' : 'hidden'}>
            <MeetingCreateDialog
              embedded
              open
              candidate={candidate}
              jobDetails={jobDetails}
              onClose={onClose}
              onBookingCreated={(booking) => {
                onBookingCreated?.(booking);
                // Instant Live Meet: stay on Scheduling so host sees links / join actions here.
                // Manual scheduled meet: move to Activity for booking details.
                if (!isInstantLiveMeetBooking(booking)) {
                  onTabChange('activity');
                }
              }}
              onOpenAutomatedFlow={onOpenAutomatedFlow}
            />
          </div>
          {activeTab === 'activity' ? (
            <div>
              <InterviewRoomDialog
                embedded
                open
                booking={liveMeetBooking}
                candidate={candidate}
                jobDetails={jobDetails}
                viewerRole="interviewer"
                onClose={onClose}
                onBookingUpdated={refreshLiveMeetBooking}
              />
            </div>
          ) : null}
          <div className={activeTab === 'insights' ? 'block' : 'hidden'}>
            <MeetingInsightsDialog
              embedded
              candidate={candidate}
              jobDetails={jobDetails}
              onClose={onClose}
              isPanelActive={activeTab === 'insights'}
              activeBooking={liveMeetBooking}
              bookingRefreshKey={insightsBookingId}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * College-tier badge — shows when the candidate's parsed college resolves to a
 * platform tier (backend services/college_tiers.py → serialize_candidate).
 * Display-only signal; never gates a candidate. Hidden for unrecognised
 * colleges so the card stays clean.
 */
function CollegeTierBadge({ candidate, className = '' }) {
  const tier = candidate?.college_tier;
  if (tier !== 1 && tier !== 2) return null;
  const label = candidate?.college_tier_label || (tier === 1 ? 'Tier 1' : 'Tier 2');
  const canonical = candidate?.college_canonical;
  const cls = tier === 1
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900'
    : 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium leading-none',
        cls, className,
      )}
      title={candidate?.college ? `${label} college — ${candidate.college}` : `${label} college`}
    >
      <GraduationCap className="w-3 h-3 shrink-0" />
      {label}{canonical ? ` · ${canonical}` : ''}
    </span>
  );
}

/**
 * "Below college bar" flag — shows on the card only when the JD is set to
 * REQUIRE premium colleges AND this candidate falls short. The candidate is
 * never rejected or zeroed; this just makes the reason visible (they're also
 * hidden by default in the list, revealable via "Show all"). Hidden entirely
 * for "any" / "prefer" JDs and for candidates who meet the bar.
 */
function CollegeGateFlag({ candidate, jobDetails, className = '' }) {
  const gate = collegeGateStatus(candidate, jobDetails?.college_requirement);
  if (!gate.required || gate.meets) return null;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium leading-none',
        'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300',
        className,
      )}
      title={gate.reason}
    >
      <AlertTriangle className="w-3 h-3 shrink-0" />
      Below college bar
    </span>
  );
}

function getCandidateRejectReason(candidate) {
  return String(candidate?.reject_reason || candidate?.rejectReason || '').trim();
}

function CandidateCard({
  candidate,
  onViewProfile,
  onViewResume = () => {},
  jobDetails,
  viewMode = 'grid',
  // {has_video, score?, verdict?} from ApplicationList's bulk lookup.
  // Drives the 🎥 chip in the dropdown; undefined = not loaded (no chip).
  videoStatus = undefined,
  nextRoundCandidates = [],
  evaluating = {},
  handleEvaluate = () => {},
  costPerResume = '-',
  // Optional. When the parent (ApplicationList) wants the Cheating
  // Evidence dashboard to render INLINE in the same page shell instead
  // of navigating to /hirewithus/cheating-evidence/<id>, it passes this
  // callback. The chip then calls onOpenCheatingEvidence(coding_test_id)
  // instead of rendering as an <a href>. When the prop isn't provided,
  // the chip falls back to the old anchor-link behaviour so other
  // surfaces (e.g. the company-admin All.jsx) keep working unchanged.
  onOpenCheatingEvidence = null,
}) {
  // Applications Copilot context (no-op default when rendered outside ApplicationList).
  const copilot = useCopilot();
  const [showDetails, setShowDetails] = useState(false);
  const [showSchedulePopup, setShowSchedulePopup] = useState(false);
  const [showVoiceScreenPopup, setShowVoiceScreenPopup] = useState(false);
  const { can } = useCompanyAccess();
  const canChangeStatus = can('candidates.status');
  const canSelect = can('onboarding.manage');
  const canScheduleTest = can('tests.schedule');
  const canSeeContact = can('candidates.contact');
  const canScheduleInterview = can('interviews.schedule');
  const canPhoneScreen = can('screening.phone');
  const canRequestVideo = can('screening.video');
  const canRescore = can('candidates.rescore');
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [showVideoPopup, setShowVideoPopup] = useState(false);
  const [showNoVideoPopup, setShowNoVideoPopup] = useState(false);
  // 'idle' | 'sending' | 'sent' | 'cooldown' | 'already_has_video' | 'error'
  const [videoRequestState, setVideoRequestState] = useState('idle');
  const [videoLimitMessage, setVideoLimitMessage] = useState('');

  // Employer asks this candidate (usually recruiter-uploaded, so they've
  // never logged in) for a video via a tokenized no-login email link.
  // Server enforces the anti-spam rules; we just surface the outcome.
  const handleRequestVideoResume = async () => {
    setVideoRequestState('sending');
    try {
      const resp = await axios.post(
        getApiUrl('/api/video-resume/request'),
        {
          candidate_email: candidate.candidate_email,
          company_name: jobDetails?.company_name || jobDetails?.companyName || null,
        },
        { withCredentials: true }
      );
      const outcome = resp.data?.outcome;
      if (outcome === 'sent') setVideoRequestState('sent');
      else if (outcome === 'cooldown') setVideoRequestState('cooldown');
      else if (outcome === 'already_has_video') setVideoRequestState('already_has_video');
      else setVideoRequestState('error');
    } catch (e) {
      // Custom monthly allowance used up (e.g. Balio) → show the server's
      // message (renewal date + support email) instead of "try again".
      const body = e?.response?.data;
      if (e?.response?.status === 403 && body?.reason === 'monthly_limit_reached') {
        setVideoLimitMessage(body.error || 'Your monthly video-resume allowance has been used. Please contact contact@searchjob.ai.');
        setVideoRequestState('limit');
      } else {
        setVideoRequestState('error');
      }
    }
  };

  // Already requested recently (from the bulk status) → start in cooldown
  // display so the button doesn't invite a pointless click.
  const priorRequestAt = videoStatus?.requested_at || null;
  const [videoData, setVideoData] = useState(null);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);
  const [isDownloadingResume, setIsDownloadingResume] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  // "Mark as Selected" — same transition as the Interview Schedule screen; a
  // selected candidate flows into the Offer + Onboarding stages.
  const [markedSelected, setMarkedSelected] = useState(
    Boolean(candidate?.is_hired) || Boolean(candidate?.isSelected)
    || String(candidate?.status || '').trim().toLowerCase() === 'selected'
  );
  const [isSelecting, setIsSelecting] = useState(false);
  const [showScreenRecordingPopup, setShowScreenRecordingPopup] = useState(false);
  const [screenRecordingData, setScreenRecordingData] = useState(null);
  const [isLoadingScreenRecording, setIsLoadingScreenRecording] = useState(false);
  const [screenRecordingTestType, setScreenRecordingTestType] = useState(null);
  const [showGamePopup, setShowGamePopup] = useState(false);
  const [showHumanInterviewHub, setShowHumanInterviewHub] = useState(false);
  const [humanInterviewTab, setHumanInterviewTab] = useState('scheduling');
  const [showMeetingPopup, setShowMeetingPopup] = useState(false);
  const [liveMeetBooking, setLiveMeetBooking] = useState(null);
  const [liveMeetBookingLoading, setLiveMeetBookingLoading] = useState(false);
  const [isCancellingLiveMeet, setIsCancellingLiveMeet] = useState(false);
  const [showCancelReasonDialog, setShowCancelReasonDialog] = useState(false);
  const [cancelReasonDraft, setCancelReasonDraft] = useState('');
  const [cancelReasonError, setCancelReasonError] = useState('');
  const [showRejectReasonDialog, setShowRejectReasonDialog] = useState(false);
  const [rejectReasonDraft, setRejectReasonDraft] = useState('');
  const [rejectReasonError, setRejectReasonError] = useState('');
  const [meetingSetup, setMeetingSetup] = useState(null);
  const [automatedMeetingProvider, setAutomatedMeetingProvider] = useState('google_meet');
  const [showAIInsights, setShowAIInsights] = useState(false);
  // Proctoring verdict per non-coding test (screening/personality/behavioral/
  // communication/aptitude) so each pill can flag suspicious/high at a glance,
  // the same way coding's pill shows the plagiarism shield. One lightweight
  // call; fail-soft — an empty result just means no flags and the card renders
  // exactly as before.
  const [proctorFlags, setProctorFlags] = useState({});
  useEffect(() => {
    const ids = [
      candidate.screening_test_id,
      candidate.personality_test_id,
      candidate.behavioral_test_id,
      candidate.communication_test_id,
      candidate.aptitude_test_id,
    ].filter(Boolean);
    if (ids.length === 0) return undefined;
    let cancelled = false;
    fetch(getApiUrl('/api/cheating/flags'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ test_ids: ids }),
    })
      .then((r) => (r.ok ? r.json() : { flags: {} }))
      .then((d) => { if (!cancelled) setProctorFlags(d?.flags || {}); })
      .catch(() => { /* fail-soft: no flags, card unaffected */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate.screening_test_id, candidate.personality_test_id, candidate.behavioral_test_id, candidate.communication_test_id, candidate.aptitude_test_id]);

  // testType → proctoring verdict ('suspicious' | 'high_confidence'), or null
  // when clean / not flagged. Comma-separated ids resolve to the last (latest)
  // one, matching the evidence link's resolution.
  const proctorVerdictFor = (testType) => {
    const field = {
      screening: 'screening_test_id',
      personality: 'personality_test_id',
      behavioral: 'behavioral_test_id',
      communication: 'communication_test_id',
      aptitude: 'aptitude_test_id',
    }[testType];
    if (!field) return null;
    const raw = candidate[field];
    if (!raw) return null;
    const id = String(raw).split(',').map((s) => s.trim()).filter(Boolean).pop();
    const v = id ? proctorFlags[id]?.verdict : null;
    return v === 'suspicious' || v === 'high_confidence' ? v : null;
  };

  // Click a flagged pill's shield → open that test's proctoring evidence.
  // Uses the parent's inline opener when wired (same as the coding chip), and
  // passes the type so the dashboard shows the dedicated (non-coding) view;
  // falls back to a full-page, type-aware route otherwise. stopPropagation so
  // it never triggers the pill's own recording click.
  const openProctorEvidence = (testType, e) => {
    if (e) e.stopPropagation();
    const field = {
      screening: 'screening_test_id',
      personality: 'personality_test_id',
      behavioral: 'behavioral_test_id',
      communication: 'communication_test_id',
      aptitude: 'aptitude_test_id',
    }[testType];
    const type = {
      screening: 'screening', personality: 'pnb', behavioral: 'pnb',
      communication: 'communication', aptitude: 'apt',
    }[testType];
    if (!field) return;
    const raw = candidate[field];
    const id = raw ? String(raw).split(',').map((s) => s.trim()).filter(Boolean).pop() : null;
    if (!id) return;
    if (typeof onOpenCheatingEvidence === 'function') {
      onOpenCheatingEvidence(id, type);
    } else {
      navigate(`/hirewithus/cheating-evidence/${encodeURIComponent(id)}${type ? `?type=${type}` : ''}`);
    }
  };
  const [expandedCards, setExpandedCards] = useState({});
  const [loading, setLoading] = useState(false);
  
  // Contact edit state
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [editedContact, setEditedContact] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSavingContact, setIsSavingContact] = useState(false);

  const navigate = useNavigate();

  const candidateEmailForMeeting = candidate?.email || candidate?.candidate_email || '';
  const jobIdForMeeting = String(candidate?.job_id || jobDetails?.job_id || '').trim();
  const normalizeLiveMeetBookingForCard = (booking) => {
    const provider = String(booking?.meeting_provider || '').trim().toLowerCase();
    const isLiveMeetProvider = provider === 'live_meet' || provider === 'search_job_meet';
    return isLiveMeetProvider ? booking : null;
  };

  const refreshLiveMeetBooking = useCallback(
    async (nextBooking, options = {}) => {
      if (nextBooking) {
        setLiveMeetBooking(normalizeLiveMeetBookingForCard(nextBooking));
        return nextBooking;
      }
      if (!candidateEmailForMeeting || !jobIdForMeeting) {
        setLiveMeetBooking(null);
        return;
      }
      setLiveMeetBookingLoading(true);
      try {
        const data = await fetchLiveMeetBooking({
          candidateEmail: candidateEmailForMeeting,
          jobId: jobIdForMeeting,
          includeTimeline: Boolean(options.includeTimeline),
        });
        const booking = data?.has_booking ? data.booking : null;
        setLiveMeetBooking(normalizeLiveMeetBookingForCard(booking));
        return booking;
      } catch {
        setLiveMeetBooking(null);
      } finally {
        setLiveMeetBookingLoading(false);
      }
    },
    [candidateEmailForMeeting, jobIdForMeeting]
  );

  // Booking poll — heavily rate-limited.
  //
  // Why: this card is rendered once per candidate on the Applications page.
  // The previous code fetched on mount + every 60s. When the parent list
  // re-rendered (filters / status updates / inserted children) the cards
  // re-mounted and each re-mount fired an immediate fetch. With N cards on
  // screen this hammered /api/meeting/live-meet/booking 5–10× per second and
  // starved the backend's `ensure_meeting_tables` DDL of its lock, producing
  // the `[INIT] canceling statement due to lock timeout` spam.
  //
  // Now:
  //   1. Per-card request is gated by sessionStorage so re-mounts within
  //      `MIN_FETCH_INTERVAL_MS` reuse the in-flight result.
  //   2. The interval poll fires only when the tab is visible.
  //   3. The interval is much slower (3 min) — booking status doesn't change
  //      that often, and any user-driven action (schedule / reschedule)
  //      already calls refreshLiveMeetBooking directly.
  useEffect(() => {
    if (!candidateEmailForMeeting || !jobIdForMeeting) return undefined;

    const MIN_FETCH_INTERVAL_MS = 30_000;
    const POLL_INTERVAL_MS = 180_000;
    const cacheKey = `liveMeetBookingFetchedAt::${jobIdForMeeting}::${candidateEmailForMeeting}`;

    const lastFetchedAt = (() => {
      try {
        const raw = window.sessionStorage.getItem(cacheKey);
        return raw ? Number(raw) : 0;
      } catch {
        return 0;
      }
    })();
    const sinceLast = Date.now() - lastFetchedAt;
    if (sinceLast >= MIN_FETCH_INTERVAL_MS) {
      try {
        window.sessionStorage.setItem(cacheKey, String(Date.now()));
      } catch {
        // sessionStorage unavailable — proceed without coalescing.
      }
      refreshLiveMeetBooking();
    }

    const tick = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      try {
        window.sessionStorage.setItem(cacheKey, String(Date.now()));
      } catch {
        // ignore
      }
      refreshLiveMeetBooking();
    };

    const intervalId = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateEmailForMeeting, jobIdForMeeting]);

  const openHumanInterview = (tab = 'scheduling') => {
    setHumanInterviewTab(tab);
    setShowHumanInterviewHub(true);
  };

  const renderHumanInterviewSuite = () => (
    <>
      <HumanInterviewHub
        open={showHumanInterviewHub}
        onClose={() => setShowHumanInterviewHub(false)}
        activeTab={humanInterviewTab}
        onTabChange={setHumanInterviewTab}
        candidate={candidate}
        jobDetails={jobDetails}
        liveMeetBooking={liveMeetBooking}
        refreshLiveMeetBooking={refreshLiveMeetBooking}
        onBookingCreated={(booking) => {
          setLiveMeetBooking(normalizeLiveMeetBookingForCard(booking));
          refreshLiveMeetBooking(booking);
        }}
        onOpenAutomatedFlow={({ meetingProvider }) => {
          setShowHumanInterviewHub(false);
          setAutomatedMeetingProvider(meetingProvider || 'google_meet');
          setShowMeetingPopup(true);
        }}
      />
      {showMeetingPopup && (
        <MeetingInterviewerDialog
          candidate={candidate}
          initialMeetingProvider={automatedMeetingProvider}
          onClose={() => setShowMeetingPopup(false)}
          onContinue={(payload) => {
            setMeetingSetup({ ...payload, meetingProvider: automatedMeetingProvider });
            setShowMeetingPopup(false);
          }}
        />
      )}
      {meetingSetup && (
        <MeetingSchedulingDialog
          candidate={candidate}
          jobDetails={jobDetails}
          meetingSetup={meetingSetup}
          selectedCandidates={nextRoundCandidates}
          onBack={() => {
            setMeetingSetup(null);
            setShowMeetingPopup(true);
          }}
          onClose={() => setMeetingSetup(null)}
        />
      )}
      <Dialog
        open={showCancelReasonDialog}
        onOpenChange={(open) => {
          if (isCancellingLiveMeet) return;
          setShowCancelReasonDialog(open);
          if (!open) {
            setCancelReasonDraft('');
            setCancelReasonError('');
          }
        }}
      >
        <DialogContent
          className="max-w-md"
          onClick={(event) => event.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle>
              {liveMeetBooking?.interview_round_label
                ? `Do you want to cancel the scheduled ${liveMeetBooking.interview_round_label} interview?`
                : 'Do you want to cancel the scheduled interview?'}
            </DialogTitle>
            <DialogDescription>
              Please provide a reason for cancellation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="live-meet-cancel-reason">Cancel reason</Label>
            <Textarea
              id="live-meet-cancel-reason"
              value={cancelReasonDraft}
              onChange={(event) => {
                setCancelReasonDraft(event.target.value);
                if (cancelReasonError) setCancelReasonError('');
              }}
              placeholder="Why are you cancelling this interview?"
              rows={4}
              maxLength={500}
              disabled={isCancellingLiveMeet}
            />
            {cancelReasonError ? (
              <p className="text-xs text-rose-600">{cancelReasonError}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCancelReasonDialog(false)}
              disabled={isCancellingLiveMeet}
            >
              Keep interview
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={submitCancelLiveMeetWithReason}
              disabled={isCancellingLiveMeet}
            >
              {isCancellingLiveMeet ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : null}
              Cancel interview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  const getResumeScoreText = (rawScore) => {
    if (rawScore === null || rawScore === undefined || rawScore === '') return { text: 'N/A', numeric: null };
    const num = Number(rawScore);
    if (Number.isNaN(num)) return { text: 'N/A', numeric: null };
    return { text: `${Math.round(num)}%`, numeric: num };
  };

  const formatExperienceValue = (expValue) => {
    if (expValue === null || expValue === undefined || expValue === '') return 'N/A';
    const num = Number(expValue);
    if (Number.isNaN(num) || num <= 0) {
      return num === 0 ? '0 mo' : 'N/A';
    }
    const years = Math.floor(num);
    const months = Math.round((num - years) * 12);
    const parts = [];
    if (years > 0) parts.push(`${years} yr${years > 1 ? 's' : ''}`);
    if (months > 0) parts.push(`${months} mo${months > 1 ? 's' : ''}`);
    if (parts.length === 0) return '0 mo';
    return parts.join(' ');
  };

  // Render experience as "<total> exp · <relevant> relevant" whenever the
  // JD-aware relevant figure exists. The relevant number is ALWAYS shown
  // (neutral styling) — the closer relevant is to total, the better the fit,
  // and hiding it in the good case threw away useful signal.
  //
  // The amber ⚠ is reserved for the case that actually matters to a recruiter:
  // relevant experience is BELOW the minimum this JD asks for (jobDetails.min_exp).
  // A long-career candidate with plenty of relevant experience gets the clean
  // number with no scary triangle, even if a chunk of their total is unrelated
  // (e.g. 4y11m total / 2y10m relevant against a 2-yr JD → no warning). When the
  // JD states no minimum, there is nothing to be "below", so no warning fires.
  const renderExperience = (candidate) => {
    const totalRaw = candidate?.exp_yrs ?? candidate?.exp_years ?? candidate?.experience;
    const relRaw   = candidate?.relevant_exp_yrs;
    const relNum   = Number(relRaw);
    const minExp   = parseFloat(jobDetails?.min_exp);

    const hasRelevant = !Number.isNaN(relNum) && relNum > 0;
    if (!hasRelevant) {
      return <>{formatExperienceValue(totalRaw)} exp</>;
    }

    // Warn only when the JD sets a minimum AND relevant experience falls short.
    const belowMin = !Number.isNaN(minExp) && relNum < minExp;

    const totalLabel = formatExperienceValue(totalRaw);
    const relLabel   = formatExperienceValue(relRaw);
    const tooltip = belowMin
      ? `Only ${relLabel} of the candidate's experience is relevant to this JD — ` +
        `below the ${minExp}+ yrs this role requires.`
      : `${relLabel} of the candidate's ${totalLabel} total experience is ` +
        `relevant to this JD.`;

    return (
      <span
        className="inline-flex items-center gap-1 flex-wrap"
        title={tooltip}
      >
        <span>{totalLabel} exp</span>
        <span className={belowMin ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}>·</span>
        <span className={
          belowMin
            ? "text-amber-700 dark:text-amber-300 font-semibold"
            : "text-muted-foreground"
        }>
          {relLabel} relevant
        </span>
        {belowMin && (
          <AlertTriangle className="w-3 h-3 text-amber-600 dark:text-amber-400 shrink-0" />
        )}
      </span>
    );
  };

  const hasTestResults = (candidate) => {
    return !!(
      candidate.screening_overall_score ||
      candidate.personality_overall_score ||
      candidate.behavioral_overall_score ||
      candidate.communication_overall_score ||
      candidate.coding_overall_score ||
      candidate.aptitude_overall_score
    );
  };

  // handleViewResult — drop this into your candidate list component
  // ─────────────────────────────────────────────────────────────────────────────
  const handleViewResult = async (candidate) => {
    setLoading(true);
    try {
      const response = await fetch(getApiUrl('/api/view_test_details_candidate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          candidate_id:          candidate.candidate_email,
          job_id:                candidate.job_id,
          screening_test_id:     candidate.screening_test_id,
          personality_test_id:   candidate.personality_test_id,
          behavioral_test_id:    candidate.behavioral_test_id,
          coding_test_id:        candidate.coding_test_id,
          communication_test_id: candidate.communication_test_id,   // ← new
          aptitude_test_id:      candidate.aptitude_test_id,
        }),
      });
  
      if (!response.ok) return;
      const testData = await response.json();
      const result   = testData.candidate_result;
  
      const scores = [
        {
          label: 'Screening',
          score: result.screening_score != null
            ? `${Math.round(result.screening_score)}/100`
            : candidate.screening_scheduled_time ? 'Scheduled' : 'Not Scheduled',
        },
        {
          label: 'Personality',
          score: result.personality_score != null
            ? `${Math.round(result.personality_score)}/100`
            : candidate.personality_scheduled_time ? 'Scheduled' : 'Not Scheduled',
        },
        {
          label: 'Behavioural',
          score: result.behavioral_score != null
            ? `${Math.round(result.behavioral_score)}/100`
            : candidate.behavioral_scheduled_time ? 'Scheduled' : 'Not Scheduled',
        },
        {
          label: 'Coding',
          score: result.coding_score != null
            ? `${Math.round(result.coding_score)}/100`
            : candidate.coding_scheduled_time ? 'Scheduled' : 'Not Scheduled',
        },
        {
          label: 'Communication',
          score: result.communication_score != null
            ? `${Math.round(result.communication_score)}/100`
            : candidate.communication_scheduled_time ? 'Scheduled' : 'Not Scheduled',
        },
        {
          label: 'Aptitude',
          score: result.aptitude_score != null
            ? `${Math.round(result.aptitude_score)}/100`
            : candidate.aptitude_scheduled_time ? 'Scheduled' : 'Not Scheduled',
        },
      ];
  
      navigate(`/reports?jobID=${candidate.job_id}`, {
        state: {
          candidate: {
            details:       { ...candidate, ...result },
            scores,
            rank:          result.rank             || 0,
            total_results: result.total_candidates || 0,
            tests: {
              screening:     result.screening_details     || null,
              coding:        result.coding_details        || null,
              personality:   result.personality_details   || null,
              behavioral:    result.behavioral_details    || null,
              communication: result.communication_details || null,  // ← new
              aptitude:      result.aptitude_details      || null,
            },
          },
          jobDetails: testData.job_details,
        },
      });
    } catch (err) {
      console.error('Error fetching test result:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatInterviewDateTime = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;
    const dateLabel = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const timeLabel = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `${dateLabel} · ${timeLabel}`;
  };

  const formatUploadDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getGameAssessmentSummary = () => {
    if (!candidate.game_status) {
      return { text: 'Not Started', status: 'pending' };
    }
    if (candidate.game_status === 'completed') {
      if (candidate.game_score != null && candidate.game_total_score != null) {
        return { 
          text: `${candidate.game_score}/${candidate.game_total_score}`, 
          status: 'completed' 
        };
      }
      return { text: 'Completed', status: 'completed' };
    }
    if (candidate.game_status === 'started') {
      return { text: 'In Progress', status: 'in-progress' };
    }
    if (candidate.game_status === 'pending') {
      return { 
        text: candidate.game_scheduled_at ? formatDateTime(candidate.game_scheduled_at) : 'Scheduled',
        status: 'scheduled'
      };
    }
    return { text: candidate.game_status, status: 'pending' };
  };

  const getHumanInterviewAssessmentSummary = () => {
    const roundLabel = String(liveMeetBooking?.interview_round_label || '').trim() || null;
    const canCancel = canCancelLiveMeetFromCard(liveMeetBooking);
    const roomStatus = liveMeetBooking ? resolveInterviewRoomStatus(liveMeetBooking) : null;
    const withTileMeta = (summary, dateTime = null) => ({
      ...summary,
      roundLabel: liveMeetBooking ? roundLabel : null,
      statusKey: roomStatus?.key || null,
      statusLabel: roomStatus?.label || null,
      dateTime: dateTime || null,
      canCancel,
      cancelReason: String(liveMeetBooking?.cancel_reason || '').trim() || null,
    });

    if (!liveMeetBooking) {
      return withTileMeta({
        status: 'pending',
        date: null,
        hasRecording: false,
        isClickable: canScheduleInterview,
        tooltipText: canScheduleInterview ? 'Click to schedule a live interview' : 'No interview scheduled',
      });
    }

    const endedAt =
      liveMeetBooking.live_room_ended_at ||
      liveMeetBooking.scheduled_end_time ||
      liveMeetBooking.scheduled_start_time;
    const scheduledAt = liveMeetBooking.scheduled_start_time;

    if (
      roomStatus.key === 'completed' ||
      (liveMeetBooking.meeting_completed &&
        liveMeetBooking.meeting_had_session !== false &&
        roomStatus.key !== 'cancelled')
    ) {
      const hasRecording =
        liveMeetBooking.has_recording === true ||
        liveMeetBooking.has_recording === 'true' ||
        liveMeetBooking.has_recording === 1;
      return withTileMeta(
        {
          status: 'completed',
          date: endedAt ? formatDateTime(endedAt) : null,
          hasRecording,
          isClickable: true,
          tooltipText: hasRecording
            ? 'Interview completed - click to view recording'
            : 'Interview completed - click for details',
        },
        formatInterviewDateTime(endedAt)
      );
    }

    if (roomStatus.key === 'cancelled') {
      return withTileMeta(
        {
          status: 'pending',
          date: null,
          hasRecording: false,
          isClickable: true,
          tooltipText: 'Interview was cancelled - click to reschedule',
        },
        formatInterviewDateTime(scheduledAt)
      );
    }

    if (roomStatus.key === 'not_joined' || roomStatus.key === 'expired') {
      return withTileMeta(
        {
          status: 'pending',
          date: scheduledAt ? formatDateTime(scheduledAt) : null,
          hasRecording: false,
          isClickable: true,
          tooltipText:
            roomStatus.helper ||
            'No one joined before the scheduled time ended — click to reschedule',
        },
        formatInterviewDateTime(scheduledAt)
      );
    }

    if (
      [
        'scheduled',
        'starting_soon',
        'active',
        'awaiting_confirmation',
        'confirmed_by_candidate',
        'confirmed_by_interviewer',
        'confirmed',
        'reschedule_pending',
      ].includes(roomStatus.key)
    ) {
      const instantMeet = isInstantLiveMeetBooking(liveMeetBooking);
      const dateTime = instantMeet
        ? roomStatus.key === 'active'
          ? 'Live now'
          : 'Instant meet'
        : formatInterviewDateTime(scheduledAt);
      return withTileMeta(
        {
          status: 'scheduled',
          date: instantMeet
            ? dateTime
            : scheduledAt
              ? formatDateTime(scheduledAt)
              : null,
          hasRecording: false,
          isClickable: true,
          tooltipText: instantMeet
            ? roomStatus.key === 'active'
              ? 'Instant meet is live — click to join'
              : 'Instant meet — click to open the room'
            : roomStatus.key === 'active'
              ? 'Interview is live - click to open'
              : 'Interview scheduled - click to manage',
        },
        dateTime
      );
    }

    if (
      liveMeetBooking.has_transcript &&
      (liveMeetBooking.meeting_had_session ||
        Number(liveMeetBooking.transcript_segment_count || 0) >= 2)
    ) {
      const hasRecording =
        liveMeetBooking.has_recording === true ||
        liveMeetBooking.has_recording === 'true' ||
        liveMeetBooking.has_recording === 1;
      return withTileMeta(
        {
          status: 'completed',
          date: endedAt ? formatDateTime(endedAt) : null,
          hasRecording,
          isClickable: true,
          tooltipText: hasRecording
            ? 'Interview completed - click to view recording'
            : 'Interview completed - click for details',
        },
        formatInterviewDateTime(endedAt)
      );
    }

    return withTileMeta({
      status: 'pending',
      date: null,
      hasRecording: false,
      isClickable: canScheduleInterview,
      tooltipText: canScheduleInterview ? 'Click to schedule a live interview' : 'No interview scheduled',
    });
  };

  const handleCancelLiveMeetFromTile = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!canCancelLiveMeetFromCard(liveMeetBooking) || isCancellingLiveMeet) return;
    if (!liveMeetBooking?.booking_id) return;
    setCancelReasonDraft('');
    setCancelReasonError('');
    setShowCancelReasonDialog(true);
  };

  const submitCancelLiveMeetWithReason = async () => {
    const bookingId = liveMeetBooking?.booking_id;
    const reason = String(cancelReasonDraft || '').trim();
    if (!bookingId || isCancellingLiveMeet) return;
    if (!reason) {
      setCancelReasonError('Please enter a reason for cancelling this interview.');
      return;
    }

    setIsCancellingLiveMeet(true);
    setCancelReasonError('');
    try {
      const response = await cancelLiveMeetBooking({ bookingId, reason });
      toast.success(response?.message || 'Interview cancelled successfully.');
      const warningMessages = Array.isArray(response?.warnings)
        ? response.warnings.filter((item) => typeof item === 'string' && item.trim())
        : [];
      if (warningMessages.length > 0) {
        toast.warning(warningMessages.join(' '), { duration: 8000 });
      }
      setShowCancelReasonDialog(false);
      setCancelReasonDraft('');
      await refreshLiveMeetBooking(response?.booking || undefined);
    } catch (error) {
      toast.error(error?.message || 'Unable to cancel this interview right now.');
    } finally {
      setIsCancellingLiveMeet(false);
    }
  };

  const handleInterviewAssessmentClick = async () => {
    const interview = getHumanInterviewAssessmentSummary();

    if (interview.status !== 'completed') {
      if (interview.status === 'pending' && !canScheduleInterview) return;
      openHumanInterview(interview.status === 'pending' ? 'scheduling' : 'activity');
      return;
    }

    setIsLoadingScreenRecording(true);
    setScreenRecordingTestType('interview');

    try {
      const data = await fetchLiveMeetInterviewRecording({
        candidateEmail: candidateEmailForMeeting,
        jobId: jobIdForMeeting,
      });

      if (data?.hasRecording && data?.videoUrl) {
        setScreenRecordingData({
          videoUrl: data.videoUrl,
          filename: data.filename || null,
          testType: 'interview',
          testId: data.recording_id || null,
        });
        setShowScreenRecordingPopup(true);
        return;
      }

      toast.warning(data?.message || 'Interview recording is not available yet.');
      openHumanInterview('activity');
    } catch (error) {
      console.error('Error fetching interview recording:', error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to load interview recording';
      toast.error(errorMessage);
      openHumanInterview('activity');
    } finally {
      setIsLoadingScreenRecording(false);
    }
  };

  const getTestStatus = (testType) => {
    const scoreKey = `${testType}_overall_score`;
    const detailedScoreKey = `${testType}_score`; // For detailed results (e.g., coding_score)
    const scheduledKey = `${testType}_scheduled_time`;
    
    // PRIORITY 1: Check detailed results first (from /api/view_test_details_candidate)
    // This ensures consistency with Reports.jsx
    const detailedScore = candidate.details?.[detailedScoreKey];
    const listScore = candidate[scoreKey];
    
    // Prefer detailed score if available, otherwise use list score
    const score = detailedScore !== null && detailedScore !== undefined && detailedScore !== ''
      ? detailedScore
      : listScore;
    
    const scheduledTime = candidate[scheduledKey] || candidate.details?.[`${testType}_test_scheduled_time`];
    const hasScore = score !== null && score !== undefined && score !== '';
    
    if (hasScore) {
      const numScore = Number(score);
      if (!isNaN(numScore)) {
        // Test is completed - show score and date if available
        return {
          status: 'completed',
          value: Math.round(numScore),
          display: `${Math.round(numScore)}%`,
          date: scheduledTime ? formatDateTime(scheduledTime) : null
        };
      }
    }
    
    // PRIORITY 2: Check if test is scheduled (but no score yet)
    if (scheduledTime) {
      return {
        status: 'scheduled',
        value: null,
        display: formatDateTime(scheduledTime),
        date: formatDateTime(scheduledTime)
      };
    }
    
    // PRIORITY 3: Not scheduled yet
    return {
      status: 'pending',
      value: null,
      display: 'Not Scheduled',
      date: null
    };
  };

  const hasRecordingAvailable = (testType, status) => {
    const availabilityKey = `${testType}_recording_available`;
    const recordingUrlKey = `${testType}_recording_url`;

    const availabilityFlag = candidate?.[availabilityKey];
    const hasExplicitAvailability = availabilityFlag === true 
      || availabilityFlag === 'true' 
      || availabilityFlag === 1 
      || availabilityFlag === '1';

    if (hasExplicitAvailability || candidate?.[recordingUrlKey]) {
      return true;
    }

    return status === 'completed';
  };

  const getRecordingUiState = (testType) => {
    const test = getTestStatus(testType);
    const isClickable = test.status !== 'pending';
    const hasRecording = hasRecordingAvailable(testType, test.status);
    const tooltipText = test.status === 'pending'
      ? 'Not scheduled yet'
      : hasRecording
        ? 'Recording available - click to view'
        : 'Recording not available';

    return { test, isClickable, hasRecording, tooltipText };
  };

  const handleDownloadCV = async (e) => {
    // Prevent event propagation, especially when called from dropdown menu
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Prevent multiple simultaneous downloads
    if (isDownloadingResume) {
      return;
    }
    
    setIsDownloadingResume(true);
    
    try {
      let resumeUrl = null;

      // Use resume_path directly from candidate response (already available in API response)
      if (candidate.resume_path) {
        resumeUrl = candidate.resume_path;
      } else if (candidate.candidate_email && candidate.job_id) {
        // Fallback: fetch if resume_path is not available
        const urls = getApiUrlsWithFallback(`/api/get_candidate_resume?email=${encodeURIComponent(candidate.candidate_email)}&jobId=${candidate.job_id}`);
        
        let success = false;
        for (const url of urls) {
          try {
            const response = await fetch(url);
            
            if (response.ok) {
              const data = await response.json();
              resumeUrl = data.resume_url;
              success = true;
              break;
            }
          } catch (fetchError) {
            // Try next URL
            continue;
          }
        }
        
        if (!success) {
          toast.error('Resume not found for this candidate.');
          setIsDownloadingResume(false);
          return;
        }
      } else {
        toast.error('Resume not available for this candidate.');
        setIsDownloadingResume(false);
        return;
      }

      // Use the resume URL directly (same as profile popup)
      if (resumeUrl) {
        // The employer list supplies this to reuse the existing profile-view
        // tracking API. It intentionally does not change application status.
        void onViewResume(candidate);
        // Download through the authenticated backend — never the raw S3 link
        // (a permanent public URL). Keeps the real file type (.pdf/.docx).
        if (await downloadResume(resumeUrl, candidate.name || candidate.candidate_email)) {
          toast.success('Resume download started');
        }
      } else {
        toast.error('Resume URL not available.');
      }
    } catch (error) {
      console.error('Error downloading CV:', error);
      toast.error('Failed to download CV. Please try again.');
    } finally {
      setIsDownloadingResume(false);
    }
  };

  const handleVideoResume = async () => {
    setIsLoadingVideo(true);
    try {
      const urls = getApiUrlsWithFallback(`/api/check-video?email=${candidate.candidate_email}`);
      let response;
      let lastError;
      
      for (const url of urls) {
        try {
          response = await axios.get(url);
          if (response.data) break;
        } catch (error) {
          lastError = error;
          if (url !== urls[urls.length - 1]) {
            // Debug log removed — was leaking API URL fallback chain to console.
            continue;
          }
        }
      }
      
      if (!response && lastError) {
        throw lastError;
      }
      
      if (response.data.hasVideo && response.data.videoUrl) {
        setVideoData({
          videoUrl: response.data.videoUrl,
          filename: response.data.filename || null
        });
        setShowVideoPopup(true);
        // Best-effort: attach the AI review of this candidate's video
        // (transcript-based rubric run when they recorded it). Non-blocking —
        // the player opens regardless.
        try {
          const aResp = await axios.get(
            getApiUrl(`/api/video-resume/analysis?email=${encodeURIComponent(candidate.candidate_email)}`),
            { withCredentials: true }
          );
          if (aResp.data?.analysis) {
            setVideoData(prev => ({ ...(prev || {}), analysis: aResp.data.analysis }));
          }
        } catch (e) {
          // No analysis available — player still works.
        }
      } else {
        setShowNoVideoPopup(true);
      }
    } catch (error) {
      console.error('Error fetching video resume:', error);
      setShowNoVideoPopup(true);
    } finally {
      setIsLoadingVideo(false);
    }
  };

  const handleScreenRecording = async (testType) => {
    setIsLoadingScreenRecording(true);
    setScreenRecordingTestType(testType);
    
    try {
      const response = await axios.get(getApiUrl('/api/get_screen_recording'), {
        params: {
          candidate_email: candidate.candidate_email,
          job_id: candidate.job_id,
          test_type: testType
        }
      });
      
      if (response.data.hasRecording && response.data.videoUrl) {
        setScreenRecordingData({
          videoUrl: response.data.videoUrl,
          filename: response.data.filename || null,
          testType: testType,
          testId: response.data.test_id || null
        });
        setShowScreenRecordingPopup(true);
      } else {
        toast.warning(response.data.message || `Screen recording not available for ${testType} test.`);
      }
    } catch (error) {
      console.error('Error fetching screen recording:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      toast.error(`Failed to fetch screen recording for ${testType} test: ${errorMessage}`);
    } finally {
      setIsLoadingScreenRecording(false);
    }
  };

  const handleAction = async (action, extra = {}) => {
    if (isUpdating) return false;
    
    setIsUpdating(true);
    const jobId = candidate.job_id;
    const adminEmail = sessionStorage.getItem('admin_email') || localStorage.getItem('admin_email') || '';
    
    try {
      let endpoint = '';
      let data = {};
      const newStatus = action === 'shortlist' ? !candidate.isShortlisted : !candidate.isRejected;
      
      switch (action) {
        case 'shortlist':
          endpoint = `/api/update-shortlist?jobId=${jobId}`;
          data = {
            candidateId: candidate.candidate_email,
            isShortlisted: newStatus
          };
          break;
        case 'reject':
          if (candidate.isRejected) {
            endpoint = `/api/update-current?jobId=${jobId}`;
            data = {
              candidateId: candidate.candidate_email
            };
          } else {
            endpoint = `/api/update-reject?jobId=${jobId}`;
            data = {
              candidateId: candidate.candidate_email,
              reason: extra.reason
            };
          }
          break;
        case 'schedule':
          endpoint = `/api/update-contact-info?jobId=${jobId}`;
          data = {
            candidateId: candidate.candidate_email,
            isInterviewScheduled: !candidate.isInterviewScheduled
          };
          break;
        default:
          return false;
      }
      
      const response = await axios.post(getApiUrl(endpoint), data, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Email': adminEmail
        }
      });
      
      const actionText = action === 'shortlist' 
        ? (newStatus ? 'Candidate shortlisted!' : 'Removed from shortlist')
        : action === 'reject'
          ? (candidate.isRejected ? 'Rejection removed' : 'Candidate rejected')
          : 'Action completed';
      
      toast.success(response.data.message || actionText, {
        description: action === 'shortlist' 
          ? (newStatus ? `${candidate.name} has been added to your shortlist` : `${candidate.name} has been removed from shortlist`)
          : action === 'reject'
            ? (candidate.isRejected ? `${candidate.name} is no longer rejected` : `${candidate.name} has been rejected`)
            : undefined
      });
      window.dispatchEvent(new CustomEvent('refreshCandidates'));
      return true;
    } catch (error) {
      console.error('Error performing action:', error);
      toast.error('Failed to perform action', {
        description: 'Please try again or contact support if the issue persists.'
      });
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  // Mark this candidate as Selected (same backend transition as the Interview
  // Schedule screen). On success the candidate becomes eligible for Offer +
  // Onboarding; the list refreshes via the shared refreshCandidates event.
  const handleMarkSelected = async () => {
    const jid = String(candidate?.job_id || jobDetails?.job_id || '').trim();
    const email = candidate?.candidate_email || candidate?.email || '';
    if (!email || !jid) {
      toast.error('Missing candidate or job details. Please refresh and try again.');
      return;
    }
    if (markedSelected || isSelecting) return;
    setIsSelecting(true);
    try {
      const response = await fetch(getApiUrl('/api/mark_candidate_hired'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ candidate_email: email, job_id: jid }),
      });
      if (response.ok) {
        setMarkedSelected(true);
        const displayName = candidate?.name || candidate?.full_name || candidate?.candidate_email || 'Candidate';
        toast.success(`${displayName} has been marked as Selected.`, {
          description: 'They now appear in the Offer and Onboarding stages.',
        });
        window.dispatchEvent(new CustomEvent('refreshCandidates'));
      } else {
        const body = await response.json().catch(() => ({}));
        toast.error(body.error || body.message || 'Could not mark the candidate as Selected.');
      }
    } catch (error) {
      toast.error('Could not mark the candidate as Selected. Please try again.');
    } finally {
      setIsSelecting(false);
    }
  };

  const storedRejectReason = getCandidateRejectReason(candidate);
  const rejectHoverText = candidate.isRejected
    ? (storedRejectReason || 'Rejected')
    : 'Reject';

  const openRejectFlow = () => {
    if (isUpdating) return;
    if (candidate.isRejected) {
      handleAction('reject');
      return;
    }
    setRejectReasonDraft('');
    setRejectReasonError('');
    setShowRejectReasonDialog(true);
  };

  const submitRejectWithReason = async () => {
    const reason = String(rejectReasonDraft || '').trim();
    if (!reason) {
      setRejectReasonError('Please enter a reason for rejecting this candidate.');
      return;
    }
    setRejectReasonError('');
    const ok = await handleAction('reject', { reason });
    if (ok) {
      setShowRejectReasonDialog(false);
      setRejectReasonDraft('');
    }
  };

  const rejectReasonDialog = (
    <Dialog
      open={showRejectReasonDialog}
      onOpenChange={(open) => {
        setShowRejectReasonDialog(open);
        if (!open) {
          setRejectReasonDraft('');
          setRejectReasonError('');
        }
      }}
    >
      <DialogContent onClick={(e) => e.stopPropagation()} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reject candidate</DialogTitle>
          <DialogDescription>
            Enter a reason for rejecting this candidate.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor={`candidate-reject-reason-${candidate.candidate_email || candidate.id || 'unknown'}`}>
            Reject reason
          </Label>
          <Input
            id={`candidate-reject-reason-${candidate.candidate_email || candidate.id || 'unknown'}`}
            value={rejectReasonDraft}
            onChange={(event) => {
              setRejectReasonDraft(event.target.value);
              if (rejectReasonError) setRejectReasonError('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submitRejectWithReason();
              }
            }}
            placeholder="Why is this candidate being rejected?"
            autoFocus
          />
          {rejectReasonError ? (
            <p className="text-xs text-rose-600">{rejectReasonError}</p>
          ) : null}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowRejectReasonDialog(false)}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={submitRejectWithReason}
            disabled={isUpdating}
          >
            {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reject'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const handleEditContact = () => {
    setEditedContact({
      name: candidate.name || '',
      email: candidate.candidate_email || '',
      phone: candidate.phone || ''
    });
    setIsEditingContact(true);
  };

  const handleSaveContact = async () => {
    if (!editedContact.email || !editedContact.name) {
      toast.error('Name and Email are required');
      return;
    }

    setIsSavingContact(true);
    const jobId = candidate.job_id;
    const adminEmail = sessionStorage.getItem('admin_email') || localStorage.getItem('admin_email') || '';

    try {
      const response = await axios.post(
        getApiUrl(`/api/update-contact-info?jobId=${jobId}`),
        {
          candidateId: candidate.candidate_email,
          email: editedContact.email,
          name: editedContact.name
        },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'X-Admin-Email': adminEmail
          }
        }
      );

      toast.success(response.data.message || 'Contact info updated successfully!');
      setIsEditingContact(false);
      window.dispatchEvent(new CustomEvent('refreshCandidates'));
    } catch (error) {
      console.error('Error saving contact:', error);
      toast.error('Failed to save contact info. Please try again.');
    } finally {
      setIsSavingContact(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingContact(false);
    setEditedContact({ name: '', email: '', phone: '' });
  };

  const status = candidate.isRejected 
    ? { text: 'Rejected', class: 'rejected' }
    : candidate.isInterviewScheduled 
    ? { text: 'Interview Scheduled', class: 'scheduled' }
    : candidate.isShortlisted 
    ? { text: 'Shortlisted', class: 'shortlisted' }
    : (candidate.is_profile_opened || candidate.profile_first_viewed_at)
    ? { text: 'Viewed', class: 'viewed' }
    : { text: 'New', class: 'new' };

  const { text: resumeText, numeric: resumeScore } = getResumeScoreText(candidate.Resume_Score);
  const cvUploadedDate = formatUploadDate(candidate.created_at);
  
  // Check for critical skills missing
  const hasCriticalMissing = (() => {
    const criticalMissing = candidate.critical_skills_missing;
    if (!criticalMissing) return false;
    // New API returns a parsed array
    if (Array.isArray(criticalMissing)) return criticalMissing.length > 0;
    // Legacy string fallback
    const str = String(criticalMissing).trim();
    if (!str || str === 'null' || str === 'undefined' || str === '[]') return false;
    try {
      const parsed = JSON.parse(str);
      return Array.isArray(parsed) ? parsed.length > 0 : false;
    } catch {
      return false;
    }
  })();

  // Check experience alignment vs JD min/max requirements.
  // Returns null when JD has no min/max set, or when candidate is in range.
  // Otherwise returns { type, label, tooltip } describing the misalignment.
  const experienceAlignment = (() => {
    const expRaw  = candidate.exp_yrs ?? candidate.exp_years ?? candidate.experience;
    const expYrs  = typeof expRaw === 'number' ? expRaw : parseFloat(expRaw);
    const minExp  = parseFloat(jobDetails?.min_exp);
    const maxExp  = parseFloat(jobDetails?.max_exp);

    if (isNaN(expYrs)) return null;

    if (!isNaN(minExp) && expYrs < minExp) {
      return {
        type:    'below_min',
        label:   'Below min experience',
        tooltip: `Has ${expYrs} yrs · JD requires ${minExp}+ yrs`,
      };
    }
    // Allow up to 3 years above max before flagging as over-qualified
    if (!isNaN(maxExp) && expYrs > maxExp + 3) {
      return {
        type:    'over_qualified',
        label:   'Over-qualified',
        tooltip: `Has ${expYrs} yrs · JD max is ${maxExp} yrs`,
      };
    }
    return null;
  })();

  // Parse study/job gaps detected by the AI. The backend stores this as either:
  //   • A real JSON array  ['gap1', 'gap2']
  //   • A Python-style stringified list with single quotes  "['gap1', 'gap2']"
  // Returns [] when the candidate has no gaps OR has_study_job_gaps is false.
  const gapsList = (() => {
    const flag = candidate.has_study_job_gaps;
    const truthy = flag === true || flag === 1 || flag === '1' || flag === 'true';
    if (!truthy) return [];

    const raw = candidate.study_job_gaps;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.filter(Boolean);
    if (typeof raw === 'string') {
      try {
        // Convert Python-style single-quoted list to valid JSON before parsing
        const normalised = raw.replace(/'/g, '"');
        const parsed = JSON.parse(normalised);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
      } catch {
        // If parse fails but the string itself looks like a gap description,
        // surface it as a single entry rather than dropping it.
        return raw.trim() ? [raw.trim()] : [];
      }
    }
    return [];
  })();
  const hasGaps = gapsList.length > 0;
  const gapsTooltip = hasGaps ? gapsList.join('  •  ') : '';

  // Detect qualification mismatch flagged by the AI ranking call.
  // The backend prompt instructs Gemini to:
  //   1. Set penalty_details.qualification_penalty > 0 when the candidate's degree
  //      doesn't match the JD's required qualifications, AND
  //   2. Add an entry to penalty_details.breakdown with a "Missing Required
  //      Qualification" key (or similar) explaining the mismatch.
  // We check both signals defensively because Gemini sometimes paraphrases keys.
  const qualificationMismatch = (() => {
    let pd = candidate?.skill_penalty_details;
    // Defensive: serialize_candidate parses this as JSON, but legacy rows might
    // still come through as a raw string.
    if (typeof pd === 'string') {
      try { pd = JSON.parse(pd); } catch { return null; }
    }
    if (!pd || typeof pd !== 'object') return null;

    // Primary signal: numeric qualification_penalty > 0
    const penaltyValue = parseFloat(pd.qualification_penalty || 0);
    const breakdown    = (pd.breakdown && typeof pd.breakdown === 'object') ? pd.breakdown : {};

    // Find any breakdown key that mentions "qualification" (case-insensitive)
    const matchingKey = Object.keys(breakdown).find(k => /qualif/i.test(k));

    // Trigger badge if EITHER the explicit penalty fired OR the breakdown
    // mentions a qualification entry.
    if (penaltyValue <= 0 && !matchingKey) return null;

    const reason = matchingKey
      ? String(breakdown[matchingKey])
      : `Penalty: -${penaltyValue}`;

    return {
      label:   'Qualification mismatch',
      tooltip: reason,
    };
  })();

  const handleToggleAIInsights = () => {
    setShowAIInsights(!showAIInsights);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black/30 z-50">
        <div className="bg-white px-6 py-4 rounded-xl shadow-lg flex items-center gap-3">
          <div className="animate-spin h-5 w-5 border-2 border-gray-500 border-t-transparent rounded-full"></div>
          <span className="text-gray-700 font-medium">Fetching Results...</span>
        </div>
      </div>
    )
  }

  // Grid View - Compact card for multi-column layout
  if (viewMode === 'grid') {
    return (
      <>
        {rejectReasonDialog}
        <Card className="hover:shadow-lg transition-all duration-200 h-full flex flex-col">
          <CardContent className="p-3 flex flex-col flex-1">
            {/* Header: Avatar, Name, Video Resume + Resume Score (top right) */}
            <div className="flex items-start gap-2 mb-2">
              <button
                onClick={() => onViewProfile(candidate)}
                className="cursor-pointer hover:opacity-80 transition-opacity"
              >
                <Avatar className={cn(
                  "w-10 h-10 shrink-0",
                  resumeScore >= 80 && "bg-success-foreground",
                  resumeScore >= 50 && resumeScore < 80 && "bg-warning-foreground",
                  resumeScore < 50 && resumeScore !== null && "bg-error-foreground"
                )}>
                  <AvatarFallback className={cn(
                    "text-sm font-bold",
                    resumeScore >= 80 && "bg-success-foreground text-success-text",
                    resumeScore >= 50 && resumeScore < 80 && "bg-warning-foreground text-warning-text",
                    resumeScore < 50 && resumeScore !== null && "bg-error-foreground text-error-text",
                    resumeScore === null && "bg-muted text-muted-foreground"
                  )}>
                    {candidate.name ? candidate.name.charAt(0).toUpperCase() : 'C'}
                  </AvatarFallback>
                </Avatar>
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onViewProfile(candidate)}
                    className="cursor-pointer hover:opacity-80 transition-opacity text-left min-w-0"
                  >
                    <Text className="font-semibold truncate text-sm block">
                      {candidate.name || 'Anonymous'}
                    </Text>
                  </button>
                  {/* Applications Copilot: "Ask Copilot" about this candidate (grid view) */}
                  {!copilot.isDefault && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0 text-muted-foreground hover:text-primary"
                            aria-label="Ask AI Assistant about this candidate"
                            onClick={(e) => { e.stopPropagation(); copilot.askAbout(candidate); }}
                          >
                            <img src={COPILOT_POSTER_SRC} alt="" aria-hidden="true" className="w-4 h-4 rounded-full object-cover" draggable={false} />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent><p>Ask AI Assistant</p></TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          className="p-0.5 rounded hover:bg-muted transition-colors shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVideoResume();
                          }}
                          disabled={isLoadingVideo}
                        >
                          {isLoadingVideo ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
                          ) : (
                            <Video className={cn(
                              "w-3.5 h-3.5 hover:text-primary",
                              videoStatus?.has_video ? "text-green-600" : "text-muted-foreground"
                            )} />
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {videoStatus?.has_video
                          ? `Video Resume ✓${videoStatus.score != null ? ` · AI ${videoStatus.score}/100` : ''}`
                          : 'Video Resume'}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {hasCriticalMissing && (
                    <AlertTriangle className="w-3 h-3 text-error shrink-0" title="Critical Skills Missing" />
                  )}
                  {experienceAlignment?.type === 'below_min' && (
                    <Clock
                      className="w-3 h-3 text-error shrink-0"
                      title={`Below min experience — ${experienceAlignment.tooltip}`}
                    />
                  )}
                  {experienceAlignment?.type === 'over_qualified' && (
                    <TrendingUp
                      className="w-3 h-3 text-warning shrink-0"
                      title={`Over-qualified — ${experienceAlignment.tooltip}`}
                    />
                  )}
                  {hasGaps && (
                    <CalendarClock
                      className="w-3 h-3 text-warning shrink-0"
                      title={`Career gap${gapsList.length > 1 ? 's' : ''} — ${gapsTooltip}`}
                    />
                  )}
                  {qualificationMismatch && (
                    <GraduationCap
                      className="w-3 h-3 text-error shrink-0"
                      title={`Qualification mismatch — ${qualificationMismatch.tooltip}`}
                    />
                  )}
                </div>
                {/* Compact "uploaded by" chip sits inline with the
                    experience line — same row reduces vertical density
                    in the grid card while still being scannable. */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Text size="xs" variant="muted" className="block">
                    {renderExperience(candidate)}
                  </Text>
                  <CollegeTierBadge candidate={candidate} />
                  <CollegeGateFlag candidate={candidate} jobDetails={jobDetails} />
                  <UploadSourceBadge
                    uploadedByEmail={candidate.uploaded_by_email}
                    uploadedByType={candidate.uploaded_by_type}
                    uploadedByCompanyName={candidate.uploaded_by_company_name}
                    currentViewerEmail={localStorage.getItem('employer_email') || ''}
                    currentViewerType="company_admin"
                    variant="compact"
                  />
                </div>
                {cvUploadedDate && (
                  <Text size="xs" variant="muted" className="block mt-1">
                    CV Uploaded: {cvUploadedDate}
                  </Text>
                )}
              </div>
              
              {/* Resume Score + AI Fit chip stack — top right.
                   The score is a number (computed). The fit label is the
                   AI's holistic call ("Good Fit" / "Moderate Fit" /
                   "Poor Fit"), shown as a colour chip so recruiters can
                   scan candidate cards in a single glance. */}
              <div className="flex flex-col items-end gap-1 shrink-0">
                <div
                  className={cn(
                    "text-sm font-bold px-2 py-1 rounded text-center min-w-[45px] select-none",
                    resumeScore >= 80 && "bg-success-foreground text-success-text",
                    resumeScore >= 50 && resumeScore < 80 && "bg-warning-foreground text-warning-text",
                    resumeScore < 50 && resumeScore !== null && "bg-error-foreground text-error-text",
                    resumeScore === null && "bg-muted text-muted-foreground"
                  )}
                  title="Resume match score"
                >
                  {resumeText}
                </div>
                {candidate.overall_fit && (
                  <span
                    className={cn(
                      "text-[10px] font-semibold px-1.5 py-0.5 rounded select-none whitespace-nowrap",
                      candidate.overall_fit === "Good Fit" &&
                        "bg-green-100 text-green-700 border border-green-200",
                      candidate.overall_fit === "Moderate Fit" &&
                        "bg-amber-100 text-amber-700 border border-amber-200",
                      candidate.overall_fit === "Poor Fit" &&
                        "bg-red-100 text-red-700 border border-red-200"
                    )}
                    title="AI's holistic fit assessment: judges skills, domain alignment, recency, and outcome quality together"
                  >
                    {candidate.overall_fit}
                  </span>
                )}
                {/* Cheating-verdict chip (Grid view).
                    Replaces the old raw "Plagiarism X%" chip with a
                    recruiter-friendly verdict label ("Suspicious" or
                    "Cheating Detected") so the at-a-glance meaning is
                    immediate. The % stays visible (muted) for context.
                    Same threshold + click-through behaviour as before:
                      < 30%  → no chip (clean candidates stay clean)
                      30–49% → amber "Suspicious 45%"
                      ≥ 50%  → red    "Cheating Detected 72%"
                    Click → /hirewithus/cheating-evidence/<coding_test_id>
                    opens the Phase 8 Evidence Dashboard (now including
                    the new Code Understanding tab from Phase 5). */}
                {(() => {
                  const plagRaw = candidate.details?.plagiarism_percentage
                    ?? candidate.plagiarism_percentage;
                  const plag = plagRaw != null && plagRaw !== '' && plagRaw !== 'null'
                    ? Math.round(parseFloat(plagRaw) || 0)
                    : null;
                  if (plag == null || plag < 30) return null;
                  const high = plag >= 50;
                  const ctid = candidate.coding_test_id;
                  const label = high ? 'Cheating Detected' : 'Suspicious';
                  const chipCls = cn(
                    "text-[10px] font-semibold px-1.5 py-0.5 rounded select-none whitespace-nowrap inline-flex items-center gap-1",
                    high
                      ? "bg-red-100 text-red-700 border border-red-200"
                      : "bg-amber-100 text-amber-700 border border-amber-200",
                    ctid && "hover:underline cursor-pointer",
                  );
                  const chipTitle = high
                    ? `Cheating Detected — ${plag}% plagiarism. Click to view per-signal evidence.`
                    : `Suspicious — ${plag}% plagiarism. Click to view per-signal evidence.`;
                  // Percent intentionally NOT rendered in the badge body —
                  // recruiter scanning a list sees just the verdict word
                  // ("Suspicious" / "Cheating Detected"). The exact % is
                  // available on hover via chipTitle and is also visible
                  // on the Evidence Dashboard one click away.
                  const chipBody = (
                    <>
                      <ShieldAlert className="w-3 h-3" />
                      {label}
                    </>
                  );
                  if (ctid) {
                    // When the parent supplied an inline-open callback,
                    // render the chip as a button that flips the parent's
                    // state instead of a full-page navigation. Keeps the
                    // recruiter inside the same layout shell (sidebar +
                    // header + filter rail), matching how Reports renders.
                    if (typeof onOpenCheatingEvidence === 'function') {
                      return (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenCheatingEvidence(ctid);
                          }}
                          className={chipCls}
                          title={chipTitle}
                        >
                          {chipBody}
                        </button>
                      );
                    }
                    // Fallback — no inline callback wired → behave as
                    // before with a real anchor so other surfaces (e.g.
                    // company-admin All.jsx) keep working unchanged.
                    return (
                      <a
                        href={`/hirewithus/cheating-evidence/${encodeURIComponent(ctid)}`}
                        onClick={(e) => e.stopPropagation()}
                        className={chipCls}
                        title={chipTitle}
                      >
                        {chipBody}
                      </a>
                    );
                  }
                  return (
                    <span className={chipCls} title={chipTitle}>
                      {chipBody}
                    </span>
                  );
                })()}
              </div>
            </div>

            {/* Status Badge (left) & AI Insights (right) */}
            <div className="flex items-center justify-between mb-3">
              <Badge 
                variant="outline"
                title={status.class === 'rejected' ? rejectHoverText : undefined}
                className={cn(
                  "text-xs px-1.5 py-0",
                  status.class === 'new' && "bg-amber-50 text-amber-700 border-amber-200",
                  (status.class === 'applied' || status.class === 'viewed') && "bg-blue-50 text-blue-700 border-blue-200",
                  status.class === 'shortlisted' && "bg-green-50 text-green-700 border-green-200",
                  status.class === 'scheduled' && "bg-purple-50 text-purple-700 border-purple-200",
                  status.class === 'rejected' && "bg-red-50 text-red-700 border-red-200"
                )}
              >
                {status.text}
              </Badge>
              
              <Popover 
                open={showAIInsights} 
                onOpenChange={setShowAIInsights}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="default"
                    size="sm"
                    className={cn(
                      "h-6 px-2 text-[10px] font-semibold",
                      "bg-gradient-to-r from-background via-muted to-muted",
                      "border border-border/60 text-foreground",
                      "shadow-sm hover:shadow-md hover:border-border",
                      "transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0",
                      "hover:from-muted hover:via-background hover:to-muted"
                    )}
                    onClick={handleToggleAIInsights}
                  >
                    <Sparkles 
                      className="w-3 h-3 mr-1 text-muted-foreground animate-pulse" 
                      style={{ animationDuration: '3s', animationIterationCount: 2 }}
                    />
                    CV AI Insights
                  </Button>
                </PopoverTrigger>
                <PopoverContent 
                  className="w-[360px] p-0"
                  align="end"
                  side="bottom"
                >
                  {/* Header */}
                  <div className="p-3 border-b bg-gradient-to-r from-muted/50 to-accent/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="p-1 rounded-md bg-muted">
                          <Sparkles className="w-3.5 h-3.5 text-foreground" />
                        </div>
                        <div>
                          <Text className="font-semibold text-xs">CV AI Insights</Text>
                          <Text size="xs" variant="muted" className="block">
                            {candidate.name || 'Candidate'}
                          </Text>
                        </div>
                      </div>
                      {hasCriticalMissing && (
                        <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                          <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />
                          Issues
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Insights Grid */}
                  <div className="max-h-[300px] overflow-y-auto p-3">
                    <div className="grid grid-cols-2 gap-2">
                      {hasCriticalMissing && (
                        <AIInsightCardExpandable
                          id="critical"
                          title="Critical Missing"
                          content={candidate.critical_skills_missing}
                          variant="error"
                          icon={AlertTriangle}
                          expanded={expandedCards.critical}
                          onToggle={() => setExpandedCards(prev => ({ ...prev, critical: !prev.critical }))}
                        />
                      )}
                      <AIInsightCardExpandable
                        id="matching"
                        title="Matching Skills"
                        content={candidate.skills_matching}
                        variant="success"
                        icon={CheckCircle}
                        expanded={expandedCards.matching}
                        onToggle={() => setExpandedCards(prev => ({ ...prev, matching: !prev.matching }))}
                      />
                      <AIInsightCardExpandable
                        id="missing"
                        title="Missing Skills"
                        content={candidate.missing_skills}
                        variant="error"
                        icon={XCircle}
                        expanded={expandedCards.missing}
                        onToggle={() => setExpandedCards(prev => ({ ...prev, missing: !prev.missing }))}
                      />
                      <AIInsightCardExpandable
                        id="strengths"
                        title="Strengths"
                        content={candidate.strengths}
                        variant="info"
                        icon={Zap}
                        expanded={expandedCards.strengths}
                        onToggle={() => setExpandedCards(prev => ({ ...prev, strengths: !prev.strengths }))}
                      />
                      <AIInsightCardExpandable
                        id="summary"
                        title="Summary"
                        content={candidate.overall_summary}
                        variant="neutral"
                        icon={Info}
                        expanded={expandedCards.summary}
                        onToggle={() => setExpandedCards(prev => ({ ...prev, summary: !prev.summary }))}
                      />
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Top-5 AI score breakdown — name + tiny bar + score for
                the 5 highest-importance categories (3 critical + 2
                important). The 3 "irrelevant"-severity categories
                (good-to-have / transferable / subjective fit) are
                hidden here; the full 8 stay visible in the AI Insights
                modal. Tooltip on hover shows the AI's full explanation.
                Renders nothing for legacy candidates with no breakdown. */}
            <CompactScoreBreakdown
              scoreBreakdown={candidate.score_breakdown}
              topOnly
              columns={2}
              className="mb-3"
            />

            {/* Test Status Indicators - Compact Grid with Recording Support */}
            <TooltipProvider>
              <div className="grid grid-cols-7 gap-1 mb-3">
                {['screening', 'personality', 'behavioral', 'communication', 'coding', 'aptitude'].map((testType) => {
                  const { test, isClickable, hasRecording, tooltipText } = getRecordingUiState(testType);
                  const shortLabel = testType === 'screening' ? 'S' : testType === 'personality' ? 'P' : testType === 'behavioral' ? 'B' : testType === 'communication' ? 'Cm' : testType === 'aptitude' ? 'A' : 'C';
                  
                  // Get plagiarism score for coding test
                  // Prefer detailed results (from Reports API) for consistency
                  const plagiarismScore = testType === 'coding' 
                    ? (candidate.details?.plagiarism_percentage ?? candidate.plagiarism_percentage)
                    : null;
                  const plagiarismNum = plagiarismScore != null && plagiarismScore !== '' && plagiarismScore !== 'null'
                    ? parseInt(plagiarismScore) || 0
                    : null;
                  // Proctoring flag for non-coding tests (coding uses plagiarism above).
                  const proctorVerdict = testType !== 'coding' ? proctorVerdictFor(testType) : null;

                  return (
                    <Tooltip key={testType}>
                      <TooltipTrigger asChild>
                        <button
                          className={cn(
                            "h-7 rounded flex items-center justify-center gap-0.5 text-xs font-medium transition-all relative",
                            test.status === 'completed' && "bg-green-100 text-green-700 hover:bg-green-200",
                            test.status === 'scheduled' && "bg-amber-100 text-amber-700 hover:bg-amber-200",
                            test.status === 'pending' && "bg-gray-100 text-gray-400 cursor-not-allowed",
                            isClickable && "cursor-pointer hover:shadow-sm"
                          )}
                          onClick={() => isClickable && handleScreenRecording(testType)}
                          disabled={!isClickable}
                        >
                          {test.status === 'completed' ? (
                            <>
                              <span>{test.value}%</span>
                              {hasRecording && <Video className="w-2.5 h-2.5 opacity-50" />}
                              {testType === 'coding' && plagiarismNum != null && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="ml-0.5 inline-flex items-center">
                                        {plagiarismNum > 40 ? (
                                          <ShieldAlert className={cn("w-3 h-3 text-red-600")} />
                                        ) : plagiarismNum > 20 ? (
                                          <AlertTriangle className={cn("w-3 h-3 text-amber-600")} />
                                        ) : (
                                          <ShieldCheck className={cn("w-3 h-3 text-green-600")} />
                                        )}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Plagiarism: {plagiarismNum}%</p>
                                      {plagiarismNum > 40 && <p className="text-[10px] opacity-70">High similarity detected</p>}
                                      {plagiarismNum <= 20 && <p className="text-[10px] opacity-70">Low similarity - Original</p>}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              {proctorVerdict && (
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(e) => openProctorEvidence(testType, e)}
                                  className="ml-0.5 inline-flex items-center cursor-pointer hover:scale-125 transition-transform"
                                  title={proctorVerdict === 'high_confidence' ? 'Cheating detected — click to view evidence' : 'Suspicious activity — click to view evidence'}
                                >
                                  <ShieldAlert className={cn("w-3 h-3", proctorVerdict === 'high_confidence' ? "text-red-600" : "text-amber-600")} />
                                </span>
                              )}
                            </>
                          ) : test.status === 'scheduled' ? (
                            <CalendarClock className="w-3.5 h-3.5" />
                          ) : (
                            <span>{shortLabel}</span>
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{testType.charAt(0).toUpperCase() + testType.slice(1)}: {test.display}</p>
                        {testType === 'coding' && plagiarismNum != null && (
                          <p className="text-[10px] opacity-70">
                            Plagiarism: {plagiarismNum}%
                            {plagiarismNum > 40 && ' (High similarity detected)'}
                          </p>
                        )}
                        {proctorVerdict && (
                          <p className="text-[10px] opacity-70">
                            Proctoring: {proctorVerdict === 'high_confidence' ? 'Cheating detected' : 'Suspicious'} — open the report for evidence
                          </p>
                        )}
                        {isClickable && <p className="text-[10px] opacity-70">{hasRecording ? 'Click to view recording' : 'Click to check recording'}</p>}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
                {(() => {
                  const interview = getHumanInterviewAssessmentSummary();
                  const interviewLoading =
                    isLoadingScreenRecording && screenRecordingTestType === 'interview';
                  return (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className={cn(
                            'h-7 rounded flex items-center justify-center gap-0.5 text-[10px] font-semibold transition-all',
                            interview.status === 'completed' && 'bg-green-100 text-green-700 hover:bg-green-200',
                            interview.status === 'scheduled' && 'bg-amber-100 text-amber-700 hover:bg-amber-200',
                            interview.status === 'pending' && 'bg-gray-100 text-gray-500 hover:bg-gray-200',
                            interview.isClickable && 'cursor-pointer hover:shadow-sm'
                          )}
                          onClick={handleInterviewAssessmentClick}
                          disabled={!interview.isClickable || interviewLoading || liveMeetBookingLoading}
                        >
                          {interviewLoading ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : interview.status === 'completed' ? (
                            <>
                              <span>IV</span>
                              {interview.hasRecording && <Video className="w-2.5 h-2.5 opacity-50" />}
                            </>
                          ) : interview.status === 'scheduled' ? (
                            <CalendarClock className="w-3.5 h-3.5" />
                          ) : (
                            <span>IV</span>
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Interview: {interview.tooltipText}</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })()}
              </div>
            </TooltipProvider>

            {/* Actions - Compact Row */}
            <div className="flex items-center justify-between gap-1 mt-auto pt-2 border-t">
              <div className="flex items-center gap-1">
                {hasTestResults(candidate) && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => handleViewResult(candidate)}
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          Results Summary
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>View Results Summary</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {canScheduleTest && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => setShowSchedulePopup(true)}
                      >
                        <Calendar className="w-3.5 h-3.5 mr-1" />
                        AI Interview
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Schedule AI screening and other automated tests</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                )}

                {canScheduleInterview && (
                  <HumanInterviewTrigger
                    variant="compact"
                    onOpen={() => openHumanInterview('scheduling')}
                  />
                )}

                {canChangeStatus && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={candidate.isShortlisted ? 'default' : 'ghost'}
                        size="sm"
                        className={cn(
                          "h-7 w-7 p-0",
                          candidate.isShortlisted && "bg-success-foreground hover:bg-success-foreground/80 text-success-text"
                        )}
                        onClick={() => handleAction('shortlist')}
                        disabled={isUpdating}
                      >
                        {candidate.isShortlisted ? (
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        ) : (
                          <UserCheck className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{candidate.isShortlisted ? 'Shortlisted' : 'Shortlist'}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                )}

                {canChangeStatus && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={candidate.isRejected ? 'destructive' : 'ghost'}
                        size="sm"
                        className={cn(
                          "h-7 w-7 p-0",
                          candidate.isRejected && "bg-error-foreground hover:bg-error-foreground/80 text-error-text"
                        )}
                        onClick={openRejectFlow}
                        disabled={isUpdating}
                      >
                        {candidate.isRejected ? (
                          <XCircle className="w-3.5 h-3.5" />
                        ) : (
                          <X className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      {candidate.isRejected && storedRejectReason ? (
                        <p>Reason: {storedRejectReason}</p>
                      ) : (
                        rejectHoverText
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                )}
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                    <MoreHorizontal className="w-3.5 h-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  {canSelect && (
                  <DropdownMenuItem
                    onClick={handleMarkSelected}
                    disabled={markedSelected || isSelecting}
                    className={`text-xs py-1.5 ${markedSelected ? 'text-green-600 font-medium' : 'text-green-700 focus:text-green-800'}`}
                  >
                    {markedSelected ? (
                      <><CheckCircle2 className="w-3 h-3 mr-2" />Selected</>
                    ) : (
                      <><UserCheck className="w-3 h-3 mr-2" />{isSelecting ? 'Marking…' : 'Mark as Selected'}</>
                    )}
                  </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleDownloadCV} className="text-xs py-1.5">
                    <Download className="w-3 h-3 mr-2" />
                    Resume
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleVideoResume} className="text-xs py-1.5">
                    <Video className="w-3 h-3 mr-2" />
                    Video Resume
                    {videoStatus?.has_video && (
                      <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-medium text-green-600">
                        ●{videoStatus.score != null ? ` ${videoStatus.score}` : ''}
                      </span>
                    )}
                    {videoStatus && !videoStatus.has_video && (
                      <span className="ml-auto text-[10px] text-muted-foreground">—</span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => hasTestResults(candidate) && handleViewResult(candidate)}
                    disabled={!hasTestResults(candidate)}
                    className="text-xs py-1.5"
                  >
                    <FileText className="w-3 h-3 mr-2" />
                    Test Results
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowContactPopup(true)} className="text-xs py-1.5">
                    <Phone className="w-3 h-3 mr-2" />
                    Contact Info
                  </DropdownMenuItem>
                  {canPhoneScreen && (
                  <DropdownMenuItem onClick={() => setShowVoiceScreenPopup(true)} className="text-xs py-1.5">
                    <PhoneCall className="w-3 h-3 mr-2" />
                    AI Phone Screen
                  </DropdownMenuItem>
                  )}
                  {canScheduleTest && (
                  <DropdownMenuItem onClick={() => setShowGamePopup(true)} className="text-xs py-1.5">
                    <Gamepad2 className="w-3 h-3 mr-2" />
                    Game Assessment
                  </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardContent>
        </Card>

        {/* All dialogs for grid view */}
        {/* Contact Information Popup */}
        <Dialog open={showContactPopup} onOpenChange={(open) => { 
          if (!open) {
            setShowContactPopup(false);
            handleCancelEdit();
          }
        }}>
          <DialogContent onClick={(e) => e.stopPropagation()}>
            <DialogHeader>
              <DialogTitle>Contact Information</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {isEditingContact ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="contact-name">Name</Label>
                    <Input
                      id="contact-name"
                      type="text"
                      value={editedContact.name}
                      onChange={(e) => setEditedContact({ ...editedContact, name: e.target.value })}
                      placeholder="Enter name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-email">Email</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      value={editedContact.email}
                      onChange={(e) => setEditedContact({ ...editedContact, email: e.target.value })}
                      placeholder="Enter email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-phone">Phone</Label>
                    <Input
                      id="contact-phone"
                      type="tel"
                      value={editedContact.phone}
                      onChange={(e) => setEditedContact({ ...editedContact, phone: e.target.value })}
                      placeholder="Enter phone number"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between py-2">
                    <Text size="sm" className="font-medium text-muted-foreground">Name:</Text>
                    <Text size="sm">{candidate.name || 'Anonymous'}</Text>
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between py-2">
                    <Text size="sm" className="font-medium text-muted-foreground">Email:</Text>
                    <Text size="sm">{canSeeContact ? candidate.candidate_email : 'Hidden for your role'}</Text>
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between py-2">
                    <Text size="sm" className="font-medium text-muted-foreground">Phone:</Text>
                    <Text size="sm">{candidate.phone || 'Not available'}</Text>
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              {isEditingContact ? (
                <>
                  <Button
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={isSavingContact}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveContact}
                    disabled={isSavingContact}
                    style={{
                      backgroundColor: getColor('primary'),
                      color: getColor('primaryForeground'),
                    }}
                  >
                    {isSavingContact ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save
                      </>
                    )}
                  </Button>
                </>
              ) : canChangeStatus ? (
                <Button
                  onClick={handleEditContact}
                  style={{
                    backgroundColor: getColor('primary'),
                    color: getColor('primaryForeground'),
                  }}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              ) : null}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Interview Scheduling Popup */}
        {showSchedulePopup && (
          <InterviewSchedulePopup
            candidate={candidate}
            onClose={() => setShowSchedulePopup(false)}
            jobDetails={jobDetails}
          />
        )}

        {/* AI telephonic screening (Exotel voice agent) */}
        {showVoiceScreenPopup && (
          <VoiceScreenPopup
            open
            candidate={candidate}
            jobId={candidate?.job_id || jobDetails?.job_id}
            onClose={() => setShowVoiceScreenPopup(false)}
          />
        )}

        {renderHumanInterviewSuite()}

        {showGamePopup && (
          <GameAssessmentPopup 
            candidate={candidate}
            jobDetails={jobDetails}
            onClose={() => setShowGamePopup(false)}
            onSuccess={() => window.location.reload()}
          />
        )}

        {/* Video Resume Popup */}
        <Dialog open={showVideoPopup && !!videoData} onOpenChange={setShowVideoPopup}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Video Resume - {candidate.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {videoData?.videoUrl && (
                <video 
                  controls 
                  autoPlay
                  className="w-full max-h-[70vh] rounded-lg"
                >
                  <source
                      src={videoData.videoUrl}
                      type={videoData.filename?.endsWith('.mp4') ? 'video/mp4' : 'video/webm'}
                    />
                  Your browser does not support the video tag.
                </video>
              )}
              <VideoAiSummary analysis={videoData?.analysis} />
              {videoData?.filename && (
                <Text size="xs" variant="muted" className="text-center">
                  {videoData.filename}
                </Text>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* No Video Resume Available Popup */}
        <Dialog open={showNoVideoPopup} onOpenChange={setShowNoVideoPopup}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Video Resume Not Available</DialogTitle>
              <DialogDescription>
                {candidate.name} has not uploaded a video resume yet.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center justify-center py-6 space-y-4">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center">
                <Video className="w-10 h-10 text-muted-foreground" />
              </div>
              <Text size="sm" variant="muted" className="text-center">
                {videoRequestState === 'sent'
                  ? 'Request sent! The candidate got an email with a no-login recording link (valid 7 days). The icon turns green once they upload.'
                  : videoRequestState === 'cooldown' || priorRequestAt
                  ? 'A video request was already sent to this candidate recently. To avoid spamming them, a new one can be sent after the 72-hour window.'
                  : videoRequestState === 'already_has_video'
                  ? 'Good news — this candidate uploaded a video just now. Close and reopen to view it.'
                  : videoRequestState === 'limit'
                  ? videoLimitMessage
                  : videoRequestState === 'error'
                  ? 'Could not send the request. Please try again in a moment.'
                  : 'Send them a one-tap recording link by email — they can record from their phone in 2 minutes, no login needed, with AI coaching included.'}
              </Text>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNoVideoPopup(false)}>
                Close
              </Button>
              {canRequestVideo && videoRequestState !== 'sent' && videoRequestState !== 'already_has_video' && !priorRequestAt && videoRequestState !== 'cooldown' && videoRequestState !== 'limit' && (
                <Button
                  onClick={handleRequestVideoResume}
                  disabled={videoRequestState === 'sending'}
                  className="gap-2"
                >
                  {videoRequestState === 'sending'
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Video className="w-4 h-4" />}
                  Request video resume
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Screen Recording Popup */}
        <Dialog open={showScreenRecordingPopup && !!screenRecordingData} onOpenChange={setShowScreenRecordingPopup}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>
                {screenRecordingData?.testType === 'interview'
                  ? 'Interview Recording'
                  : `Screen Recording - ${screenRecordingData?.testType?.charAt(0).toUpperCase() + screenRecordingData?.testType?.slice(1)} Test`}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {screenRecordingData?.videoUrl && (
                <video 
                  controls 
                  autoPlay
                  className="w-full max-h-[70vh] rounded-lg"
                >
                  <source
                      src={screenRecordingData.videoUrl}
                      type={screenRecordingData.filename?.endsWith('.mp4') ? 'video/mp4' : 'video/webm'}
                    />
                  Your browser does not support the video tag.
                </video>
              )}
              <div className="space-y-1 text-center">
                {screenRecordingData?.filename && (
                  <Text size="xs" variant="muted">
                    {screenRecordingData.filename}
                  </Text>
                )}
                {screenRecordingData?.testId && (
                  <Text size="xs" variant="muted">
                    Test ID: {screenRecordingData.testId}
                  </Text>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // List View - Original detailed layout (default)
  return (
    <>
      {rejectReasonDialog}
      <Card className="hover:shadow-lg transition-all duration-200">
        <CardContent className="p-3">
          {/* Compact Header: Everything in one line */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
            <button
              onClick={() => onViewProfile(candidate)}
              className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity text-left"
            >
              <Avatar className={cn(
                "w-9 h-9 shrink-0",
                resumeScore >= 80 && "bg-success-foreground",
                resumeScore >= 50 && resumeScore < 80 && "bg-warning-foreground",
                resumeScore < 50 && resumeScore !== null && "bg-error-foreground"
              )}>
                <AvatarFallback className={cn(
                  "text-sm font-bold",
                  resumeScore >= 80 && "bg-success-foreground text-success-text",
                  resumeScore >= 50 && resumeScore < 80 && "bg-warning-foreground text-warning-text",
                  resumeScore < 50 && resumeScore !== null && "bg-error-foreground text-error-text",
                  resumeScore === null && "bg-muted text-muted-foreground"
                )}>
                  {candidate.name ? candidate.name.charAt(0).toUpperCase() : 'C'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Text className="font-semibold truncate text-sm">
                    {candidate.name || 'Anonymous'}
                  </Text>
                  {/* List-view "uploaded by" chip — sits right after the
                      name so it's visible without horizontal scroll on
                      narrow viewports (the icon row that follows can
                      overflow). Same data + viewer-identity props as
                      the Grid view chip. */}
                  <UploadSourceBadge
                    uploadedByEmail={candidate.uploaded_by_email}
                    uploadedByType={candidate.uploaded_by_type}
                    uploadedByCompanyName={candidate.uploaded_by_company_name}
                    currentViewerEmail={localStorage.getItem('employer_email') || ''}
                    currentViewerType="company_admin"
                    variant="compact"
                  />
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          className="p-0.5 rounded hover:bg-muted transition-colors shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVideoResume();
                          }}
                          disabled={isLoadingVideo}
                        >
                          {isLoadingVideo ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
                          ) : (
                            <Video className={cn(
                              "w-3.5 h-3.5 hover:text-primary",
                              videoStatus?.has_video ? "text-green-600" : "text-muted-foreground"
                            )} />
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          {videoStatus?.has_video
                            ? `Video Resume ✓${videoStatus.score != null ? ` · AI ${videoStatus.score}/100` : ''}`
                            : 'Video Resume'}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {hasCriticalMissing && (
                    <AlertTriangle className="w-3 h-3 text-error shrink-0" title="Critical Skills Missing" />
                  )}
                  {experienceAlignment?.type === 'below_min' && (
                    <Badge
                      variant="outline"
                      className="text-xs px-1.5 py-0 shrink-0 bg-red-50 text-red-700 border-red-200"
                      title={experienceAlignment.tooltip}
                    >
                      <Clock className="w-3 h-3 mr-1" />
                      Below min exp
                    </Badge>
                  )}
                  {experienceAlignment?.type === 'over_qualified' && (
                    <Badge
                      variant="outline"
                      className="text-xs px-1.5 py-0 shrink-0 bg-amber-50 text-amber-700 border-amber-200"
                      title={experienceAlignment.tooltip}
                    >
                      <TrendingUp className="w-3 h-3 mr-1" />
                      Over-qualified
                    </Badge>
                  )}
                  {hasGaps && (
                    <Badge
                      variant="outline"
                      className="text-xs px-1.5 py-0 shrink-0 bg-amber-50 text-amber-700 border-amber-200"
                      title={gapsTooltip}
                    >
                      <CalendarClock className="w-3 h-3 mr-1" />
                      {gapsList.length > 1 ? `${gapsList.length} career gaps` : 'Career gap'}
                    </Badge>
                  )}
                  {qualificationMismatch && (
                    <Badge
                      variant="outline"
                      className="text-xs px-1.5 py-0 shrink-0 bg-red-50 text-red-700 border-red-200"
                      title={qualificationMismatch.tooltip}
                    >
                      <GraduationCap className="w-3 h-3 mr-1" />
                      Qualification mismatch
                    </Badge>
                  )}
                  <Badge
                    variant="outline"
                    title={status.class === 'rejected' ? rejectHoverText : undefined}
                    className={cn(
                      "text-xs px-1.5 py-0 shrink-0",
                      status.class === 'new' && "bg-amber-50 text-amber-700 border-amber-200",
                      (status.class === 'applied' || status.class === 'viewed') && "bg-blue-50 text-blue-700 border-blue-200",
                      status.class === 'shortlisted' && "bg-green-50 text-green-700 border-green-200",
                      status.class === 'scheduled' && "bg-purple-50 text-purple-700 border-purple-200",
                      status.class === 'rejected' && "bg-red-50 text-red-700 border-red-200"
                    )}
                  >
                    {status.text}
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground mt-0.5">
                  {canSeeContact && (
                    <>
                      <span className="break-all">
                        {candidate.candidate_email}
                      </span>
                      <span className="hidden sm:inline">•</span>
                    </>
                  )}
                  <span className="shrink-0">
                    Exp: {renderExperience(candidate)}
                  </span>
                  <CollegeTierBadge candidate={candidate} />
                  <CollegeGateFlag candidate={candidate} jobDetails={jobDetails} />
                  {cvUploadedDate && (
                    <>
                      <span className="hidden sm:inline">•</span>
                      <span className="shrink-0">
                        CV Uploaded: {cvUploadedDate}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </button>

            {/* Applications Copilot: "Ask Copilot" about this candidate (list view) */}
            {!copilot.isDefault && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-muted-foreground hover:text-primary"
                      aria-label="Ask AI Assistant about this candidate"
                      onClick={(e) => { e.stopPropagation(); copilot.askAbout(candidate); }}
                    >
                      <img src={COPILOT_POSTER_SRC} alt="" aria-hidden="true" className="w-[18px] h-[18px] rounded-full object-cover" draggable={false} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent><p>Ask AI Assistant</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Top-5 AI score breakdown — placed as a MIDDLE flex item
                between the name (left) and the Resume Score column
                (right). The parent flex container uses justify-between
                so the breakdown naturally occupies the empty horizontal
                gap the user highlighted. Hidden on small screens (it'd
                wrap unattractively) and for legacy candidates with no
                breakdown data. */}
            <div className="hidden lg:block shrink-0">
              <CompactScoreBreakdown
                scoreBreakdown={candidate.score_breakdown}
                topOnly
                columns={1}
                className="w-[230px]"
              />
            </div>

            {/* Resume Score & AI Insights - Right Side */}
            <div className="w-full sm:w-auto shrink-0 flex items-center justify-between sm:justify-start gap-3">
              <Dialog
                open={showAIInsights}
                onOpenChange={setShowAIInsights}
              >
                <Button
                  variant="default"
                  size="sm"
                  className={cn(
                    "relative px-3 py-1.5 text-sm font-semibold min-h-[38px] w-full sm:w-auto",
                    "bg-gradient-to-r from-background via-muted to-muted",
                    "border border-border/60 text-foreground text-center",
                    "shadow-sm hover:shadow-md hover:border-border",
                    "transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0",
                    "hover:from-muted hover:via-background hover:to-muted"
                  )}
                  onClick={() => setShowAIInsights(true)}
                >
                  <Sparkles
                    className="w-3.5 h-3.5 mr-1 text-muted-foreground animate-pulse"
                    style={{ animationDuration: '3s', animationIterationCount: 2 }}
                  />
                  CV AI Insights
                </Button>
                {/*
                  Switched from Popover → Dialog. Popover was tied to the
                  trigger button's position, so on long candidate cards (or
                  near the bottom of the viewport) the panel got vertically
                  clipped no matter how big we made it. Dialog is a
                  proper centered modal — viewport-managed, predictable
                  scroll, never anchored to a moving trigger. This is the
                  right primitive for content-heavy "deep dive" panels.

                  Sizing: width fills viewport up to 1100px, height capped
                  to 90vh. Body is the only overflow container, with the
                  header pinned via sticky + shrink-0 inside the dialog's
                  flex-col layout.
                */}
                <DialogContent
                  /* Width tuned for the "top score strip + 2-col body"
                     layout. 1200px gives the top tile grid 4 tiles ×
                     ~270px each (very comfortable for the score + chip
                     + bar) and the body 2 columns × ~570px each (room
                     for skill badge wraps + bullet lists without
                     awkward wrap). Respects 100vw-1rem on smaller
                     viewports where the tile grid + body both collapse
                     to single column. */
                  className="w-[min(100vw-1rem,1200px)] max-w-[1200px] max-h-[90vh] p-0 flex flex-col overflow-hidden gap-0"
                >
                  {/* Accessible title (visually hidden — we have our own
                       styled header below). Without this, Radix Dialog
                       throws a console warning about missing DialogTitle.  */}
                  <DialogTitle className="sr-only">
                    CV AI Insights — {candidate.name || 'Candidate'}
                  </DialogTitle>
                  {/* pr-10 reserves space for the Dialog's built-in X close
                       button at the top-right (Radix renders it absolutely
                       at top: 1rem / right: 1rem). Without this padding,
                       header badges (Critical Issues, Below min exp, etc.)
                       overlap the X — exactly the "crossover" bug. */}
                  <div className="p-4 pr-10 border-b bg-gradient-to-r from-muted/50 to-accent/50 backdrop-blur-sm shrink-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="p-1.5 rounded-md bg-muted shrink-0">
                          <Sparkles className="w-4 h-4 text-foreground" />
                        </div>
                        <div className="min-w-0">
                          <Text className="font-semibold text-sm">CV AI Insights</Text>
                          <Text size="xs" variant="muted" className="block mt-0.5 truncate">
                            {candidate.name || 'Candidate'} • {renderExperience(candidate)}
                          </Text>
                          <div className="mt-1 flex flex-wrap items-center gap-1.5">
                            <CollegeTierBadge candidate={candidate} />
                            <CollegeGateFlag candidate={candidate} jobDetails={jobDetails} />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap justify-end">
                        {hasCriticalMissing && (
                          <Badge variant="destructive" className="text-xs">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Critical Issues
                          </Badge>
                        )}
                        {experienceAlignment?.type === 'below_min' && (
                          <Badge
                            className="text-xs bg-red-100 text-red-700 hover:bg-red-100 border-red-200"
                            title={experienceAlignment.tooltip}
                          >
                            <Clock className="w-3 h-3 mr-1" />
                            Below min exp
                          </Badge>
                        )}
                        {experienceAlignment?.type === 'over_qualified' && (
                          <Badge
                            className="text-xs bg-amber-100 text-amber-700 hover:bg-amber-100 border-amber-200"
                            title={experienceAlignment.tooltip}
                          >
                            <TrendingUp className="w-3 h-3 mr-1" />
                            Over-qualified
                          </Badge>
                        )}
                        {hasGaps && (
                          <Badge
                            className="text-xs bg-amber-100 text-amber-700 hover:bg-amber-100 border-amber-200"
                            title={gapsTooltip}
                          >
                            <CalendarClock className="w-3 h-3 mr-1" />
                            {gapsList.length > 1 ? `${gapsList.length} career gaps` : 'Career gap'}
                          </Badge>
                        )}
                        {qualificationMismatch && (
                          <Badge
                            className="text-xs bg-red-100 text-red-700 hover:bg-red-100 border-red-200"
                            title={qualificationMismatch.tooltip}
                          >
                            <GraduationCap className="w-3 h-3 mr-1" />
                            Qualification mismatch
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* All Insights — body grows to fill remaining flex
                       space inside the 90vh container; only THIS element
                       owns the vertical scroll. Sticky header above stays
                       pinned.

                       "Top score strip + 2-col body" layout:

                         TOP  → 8-category Score Breakdown rendered as a
                                horizontal tile grid (4 per row on xl).
                                Acts as the at-a-glance "scoreboard" for
                                the whole modal — recruiters see all
                                quantitative scores in one scan before
                                diving into the prose insights below.

                         BELOW (2 columns):
                           LEFT  → Skills + Narrative cards
                                   (Critical Missing → Matching → Missing
                                    → Strengths → Summary)
                                   The positives + the candidate's story.
                           RIGHT → Gaps & Risks
                                   The risk column, kept on its own so
                                   recruiters can scan it next to the
                                   skills column without scrolling.

                       Below lg, the body grid collapses to single-column
                       and the order is: scores → skills → risks. Same
                       top-down priority a recruiter reads on mobile.  */}
                  {/* Body container.

                      Mobile (< lg): plain vertical scroll — the whole
                      body scrolls as one column.
                      Desktop (≥ lg): becomes a flex column so the inner
                      regions can manage their own scrolling. The TOP
                      scoreboard stays pinned, the BODY grid fills the
                      remaining height, and the LEFT column gets its own
                      hidden scrollbar so scrolling through skill cards
                      doesn't move the score strip or the Gaps & Risks
                      column. */}
                  <div className="flex-1 min-h-0 overflow-y-auto lg:overflow-hidden p-4 lg:flex lg:flex-col scroll-smooth">
                    {/* TOP scoreboard — horizontal tile grid spanning the
                        full modal width. The section internally handles
                        its own responsive 1/2/3/4-col layout. Stays
                        fixed at the top of the body on desktop. */}
                    <ScoreBreakdownSection scoreBreakdown={candidate.score_breakdown} />

                    {/* 2-col body — on desktop this fills the remaining
                        vertical space below the scoreboard so the LEFT
                        column has a constrained height it can scroll
                        within. On mobile it just stacks normally and the
                        outer container's scroll handles overflow. */}
                    <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch lg:flex-1 lg:min-h-0">

                      {/* ─────────── LEFT column: Skills + Narrative ─────────── */}
                      {/* Independent scroll container with HIDDEN
                          scrollbar (works in Chrome/Safari via the
                          ::-webkit-scrollbar variant and Firefox via
                          scrollbar-width: none). Recruiters scroll
                          naturally with wheel/trackpad/touch but the
                          column never shows a visible scrollbar — keeps
                          the cool, clean look the design is going for.
                          pr-1 nudges content off the (now invisible)
                          scroll edge so card borders don't appear to
                          touch the column's right edge. */}
                      <div className="space-y-3 min-w-0 lg:overflow-y-auto lg:pr-1 lg:[scrollbar-width:none] lg:[&::-webkit-scrollbar]:hidden">
                        {hasCriticalMissing && (
                          <AIInsightCardExpandable
                            id="critical"
                            title="Critical Missing"
                            content={candidate.critical_skills_missing}
                            variant="error"
                            icon={AlertTriangle}
                            expanded={expandedCards.critical}
                            onToggle={() => setExpandedCards(prev => ({ ...prev, critical: !prev.critical }))}
                          />
                        )}
                        <AIInsightCardExpandable
                          id="matching"
                          title="Matching Skills"
                          content={candidate.skills_matching}
                          variant="success"
                          icon={CheckCircle}
                          expanded={expandedCards.matching}
                          onToggle={() => setExpandedCards(prev => ({ ...prev, matching: !prev.matching }))}
                        />
                        <AIInsightCardExpandable
                          id="missing"
                          title="Missing Skills"
                          content={candidate.missing_skills}
                          variant="error"
                          icon={XCircle}
                          expanded={expandedCards.missing}
                          onToggle={() => setExpandedCards(prev => ({ ...prev, missing: !prev.missing }))}
                        />
                        <AIInsightCardExpandable
                          id="strengths"
                          title="Strengths"
                          content={candidate.strengths}
                          variant="info"
                          icon={Zap}
                          expanded={expandedCards.strengths}
                          onToggle={() => setExpandedCards(prev => ({ ...prev, strengths: !prev.strengths }))}
                        />
                        <AIInsightCardExpandable
                          id="summary"
                          title="Summary"
                          content={candidate.overall_summary}
                          variant="neutral"
                          icon={Info}
                          expanded={expandedCards.summary}
                          onToggle={() => setExpandedCards(prev => ({ ...prev, summary: !prev.summary }))}
                        />
                      </div>

                      {/* ─────────── RIGHT column: Gaps & Risks ─────────── */}
                      {/* AI's "Gaps & Risks" — short, specific risk
                          callouts generated by the AI for THIS resume vs
                          THIS JD. Inlined as a custom card because each
                          item is a full sentence, not a short skill
                          token. Stays anchored in view while the LEFT
                          column scrolls independently underneath the
                          scoreboard. Same hidden-scrollbar treatment as
                          the left column in case the risk list itself
                          grows long enough to overflow. */}
                      <div className="space-y-3 min-w-0 lg:overflow-y-auto lg:pr-1 lg:[scrollbar-width:none] lg:[&::-webkit-scrollbar]:hidden">
                        {Array.isArray(candidate.gaps_and_risks) && candidate.gaps_and_risks.length > 0 ? (
                          <Card className="border-warning/30 bg-warning-foreground/50 transition-all duration-200">
                            <CardHeader className="pb-2">
                              <div className="flex items-center gap-2">
                                <div className="p-1.5 rounded-md bg-warning-foreground">
                                  <AlertTriangle className="w-3.5 h-3.5 text-warning" />
                                </div>
                                <CardTitle className="text-xs font-semibold flex-1 text-warning-text">
                                  Gaps & Risks
                                </CardTitle>
                                <span className="text-[10px] font-medium text-warning-text/70 px-1.5 py-0.5 rounded bg-warning-foreground">
                                  {candidate.gaps_and_risks.length}
                                </span>
                              </div>
                            </CardHeader>
                            <CardContent className="pt-0">
                              <ul className="space-y-1.5 text-xs leading-relaxed text-warning list-disc pl-4">
                                {candidate.gaps_and_risks.map((risk, idx) => (
                                  <li key={idx}>{risk}</li>
                                ))}
                              </ul>
                            </CardContent>
                          </Card>
                        ) : (
                          /* Empty-state — green check card so the right
                             column doesn't visually collapse when the AI
                             surfaced no risks. */
                          <Card className="border-emerald-200 bg-emerald-50/50">
                            <CardContent className="p-4 text-center">
                              <CheckCircle className="w-5 h-5 text-emerald-600 mx-auto mb-2" />
                              <div className="text-xs font-semibold text-emerald-700">
                                No Gaps or Risks Flagged
                              </div>
                              <div className="text-[11px] text-emerald-600/80 mt-1">
                                AI didn&apos;t surface any concerns for this candidate vs the JD.
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </div>

                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Separator */}
              <div className="h-8 w-px bg-border/50 hidden sm:block" />
              
              <div className="flex flex-col items-center justify-start gap-1">
                {/* Resume Score chip — display only. Download lives in the
                     actions dropdown ("Download Resume" item) so clicking
                     the score itself doesn't accidentally trigger a
                     download anymore. */}
                <div
                  className={cn(
                    "text-lg font-bold px-3 py-1.5 rounded-md text-center min-w-[70px] select-none",
                    resumeScore >= 80 && "bg-success-foreground text-success-text ring-1 ring-success/30",
                    resumeScore >= 50 && resumeScore < 80 && "bg-warning-foreground text-warning-text ring-1 ring-warning/30",
                    resumeScore < 50 && resumeScore !== null && "bg-error-foreground text-error-text ring-1 ring-error/30",
                    resumeScore === null && "bg-muted text-muted-foreground"
                  )}
                  title="Resume match score"
                >
                  {resumeText}
                </div>
                <Text size="xs" variant="muted" className="text-center whitespace-nowrap">Resume Score</Text>
                {/* AI's holistic fit label — chip shown right under the
                     Resume Score so recruiters scanning the list view can
                     see "Good Fit / Moderate Fit / Poor Fit" at a glance. */}
                {candidate.overall_fit && (
                  <span
                    className={cn(
                      "text-[11px] font-semibold px-2 py-0.5 rounded select-none whitespace-nowrap border",
                      candidate.overall_fit === "Good Fit" &&
                        "bg-green-100 text-green-700 border-green-200",
                      candidate.overall_fit === "Moderate Fit" &&
                        "bg-amber-100 text-amber-700 border-amber-200",
                      candidate.overall_fit === "Poor Fit" &&
                        "bg-red-100 text-red-700 border-red-200"
                    )}
                    title="AI's holistic fit assessment: judges skills, domain alignment, recency, and outcome quality together"
                  >
                    {candidate.overall_fit}
                  </span>
                )}
                {/* Cheating-verdict chip (List view).
                    Same content + threshold rules as the Grid view chip
                    above. Slightly bigger sizing (text-[11px], px-2) to
                    match the sibling overall_fit chip in this row. */}
                {(() => {
                  const plagRaw = candidate.details?.plagiarism_percentage
                    ?? candidate.plagiarism_percentage;
                  const plag = plagRaw != null && plagRaw !== '' && plagRaw !== 'null'
                    ? Math.round(parseFloat(plagRaw) || 0)
                    : null;
                  if (plag == null || plag < 30) return null;
                  const high = plag >= 50;
                  const ctid = candidate.coding_test_id;
                  const label = high ? 'Cheating Detected' : 'Suspicious';
                  const chipCls = cn(
                    "text-[11px] font-semibold px-2 py-0.5 rounded select-none whitespace-nowrap border inline-flex items-center gap-1",
                    high
                      ? "bg-red-100 text-red-700 border-red-200"
                      : "bg-amber-100 text-amber-700 border-amber-200",
                    ctid && "hover:underline cursor-pointer",
                  );
                  const chipTitle = high
                    ? `Cheating Detected — ${plag}% plagiarism. Click to view per-signal evidence.`
                    : `Suspicious — ${plag}% plagiarism. Click to view per-signal evidence.`;
                  // Percent intentionally NOT rendered in the badge body —
                  // recruiter scanning a list sees just the verdict word
                  // ("Suspicious" / "Cheating Detected"). The exact % is
                  // available on hover via chipTitle and is also visible
                  // on the Evidence Dashboard one click away.
                  const chipBody = (
                    <>
                      <ShieldAlert className="w-3 h-3" />
                      {label}
                    </>
                  );
                  if (ctid) {
                    // Inline-open path — see Grid view chip above for the
                    // full rationale comment. When the parent supplied
                    // onOpenCheatingEvidence, swap the anchor for a
                    // button that flips the parent's state.
                    if (typeof onOpenCheatingEvidence === 'function') {
                      return (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenCheatingEvidence(ctid);
                          }}
                          className={chipCls}
                          title={chipTitle}
                        >
                          {chipBody}
                        </button>
                      );
                    }
                    return (
                      <a
                        href={`/hirewithus/cheating-evidence/${encodeURIComponent(ctid)}`}
                        onClick={(e) => e.stopPropagation()}
                        className={chipCls}
                        title={chipTitle}
                      >
                        {chipBody}
                      </a>
                    );
                  }
                  return (
                    <span className={chipCls} title={chipTitle}>
                      {chipBody}
                    </span>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* (Score breakdown moved up into the top-row flex container —
              between the name button and the Resume Score column —
              so it fills the empty middle slot the user highlighted.) */}

          {/* Assessment Status and Actions Container */}
          <div className="flex flex-col gap-2">
          {/* Assessment Status */}
          {(() => {
            // Check if any test has a date - if so, expand all pills
            const interviewSummary = getHumanInterviewAssessmentSummary();
            // Only the 6 automated tests drive the uniform 2-line pill height.
            // The live interview now lives in its own column/card, so its
            // schedule no longer forces blank date lines onto the test pills.
            const hasAnyDate =
              candidate.screening_scheduled_time ||
              candidate.personality_scheduled_time ||
              candidate.behavioral_scheduled_time ||
              candidate.communication_scheduled_time ||
              candidate.coding_scheduled_time ||
              candidate.aptitude_scheduled_time;
            
            return (
              <div className="mb-2">
                <TooltipProvider>
                  {/* 6 assessment pills laid out as a 3-column grid so they
                      always render in two rows of three (Screening, Personality,
                      Behavioral on top; Coding, Interview, Game below).
                      Constrained to roughly half the card width so the right
                      side has room for the CV Insights + Resume score area.
                      On narrow screens (< sm) we drop to a 2-col grid and lift
                      the width cap so pills stay tappable. */}
                  <div className="grid grid-cols-2 gap-2 items-stretch w-full sm:max-w-[74%] lg:max-w-[68%] sm:[grid-template-columns:repeat(3,minmax(0,1fr))_190px]">
                      {/* Screening */}
                      {(() => {
                        const { test, isClickable, hasRecording, tooltipText } = getRecordingUiState('screening');
                        
                        return (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className={cn(
                                  "h-auto transition-all px-2.5 py-1 text-xs font-medium min-w-[120px]",
                                  hasAnyDate && "flex-col items-start gap-0.5",
                                  test.status === 'completed' && "bg-green-50 text-green-700 border-green-300 hover:bg-green-100 hover:border-green-400",
                                  test.status === 'scheduled' && "bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100 hover:border-amber-400",
                                  test.status === 'pending' && "bg-gray-50 text-gray-500 border-gray-300 cursor-not-allowed opacity-60",
                                  isClickable && "cursor-pointer hover:shadow-md"
                                )}
                                onClick={() => isClickable && handleScreenRecording('screening')}
                                disabled={!isClickable}
                              >
                                <div className="flex items-center w-full justify-between">
                                  <div className="flex items-center">
                                    {test.status === 'completed' ? (
                                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                                    ) : test.status === 'scheduled' ? (
                                      <CalendarClock className="w-3.5 h-3.5 mr-1.5" />
                                    ) : (
                                      <Circle className="w-3.5 h-3.5 mr-1.5" />
                                    )}
                                    <span>Screening {test.status === 'completed' && `${test.value}%`}</span>
                                    {test.status === 'completed' && proctorVerdictFor('screening') && (
                                      <span
                                        role="button"
                                        tabIndex={0}
                                        onClick={(e) => openProctorEvidence('screening', e)}
                                        className="ml-1 inline-flex items-center cursor-pointer hover:scale-125 transition-transform flex-shrink-0"
                                        title={proctorVerdictFor('screening') === 'high_confidence' ? 'Cheating detected — click to view evidence' : 'Suspicious activity — click to view evidence'}
                                      >
                                        <ShieldAlert className={cn("w-3 h-3", proctorVerdictFor('screening') === 'high_confidence' ? "text-red-600" : "text-amber-600")} />
                                      </span>
                                    )}
                                  </div>
                                  {hasRecording && isClickable ? (
                                    <Video className="w-3 h-3 ml-1.5 opacity-50 flex-shrink-0" />
                                  ) : (
                                    <div className="w-3 h-3 ml-1.5 flex-shrink-0" />
                                  )}
                                </div>
                                {hasAnyDate && (
                                  <span className="text-[10px] opacity-60 ml-5 min-h-[12px]">
                                    {test.date || '\u00A0'}
                                  </span>
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{tooltipText}</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })()}

                      {/* Personality */}
                      {(() => {
                        const { test, isClickable, hasRecording, tooltipText } = getRecordingUiState('personality');
                        
                        return (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className={cn(
                                  "h-auto transition-all px-2.5 py-1 text-xs font-medium min-w-[120px]",
                                  hasAnyDate && "flex-col items-start gap-0.5",
                                  test.status === 'completed' && "bg-green-50 text-green-700 border-green-300 hover:bg-green-100 hover:border-green-400",
                                  test.status === 'scheduled' && "bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100 hover:border-amber-400",
                                  test.status === 'pending' && "bg-gray-50 text-gray-500 border-gray-300 cursor-not-allowed opacity-60",
                                  isClickable && "cursor-pointer hover:shadow-md"
                                )}
                                onClick={() => isClickable && handleScreenRecording('personality')}
                                disabled={!isClickable}
                              >
                                <div className="flex items-center w-full justify-between">
                                  <div className="flex items-center">
                                    {test.status === 'completed' ? (
                                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                                    ) : test.status === 'scheduled' ? (
                                      <CalendarClock className="w-3.5 h-3.5 mr-1.5" />
                                    ) : (
                                      <Circle className="w-3.5 h-3.5 mr-1.5" />
                                    )}
                                    <span>Personality {test.status === 'completed' && `${test.value}%`}</span>
                                    {test.status === 'completed' && proctorVerdictFor('personality') && (
                                      <span
                                        role="button"
                                        tabIndex={0}
                                        onClick={(e) => openProctorEvidence('personality', e)}
                                        className="ml-1 inline-flex items-center cursor-pointer hover:scale-125 transition-transform flex-shrink-0"
                                        title={proctorVerdictFor('personality') === 'high_confidence' ? 'Cheating detected — click to view evidence' : 'Suspicious activity — click to view evidence'}
                                      >
                                        <ShieldAlert className={cn("w-3 h-3", proctorVerdictFor('personality') === 'high_confidence' ? "text-red-600" : "text-amber-600")} />
                                      </span>
                                    )}
                                  </div>
                                  {hasRecording && isClickable ? (
                                    <Video className="w-3 h-3 ml-1.5 opacity-50 flex-shrink-0" />
                                  ) : (
                                    <div className="w-3 h-3 ml-1.5 flex-shrink-0" />
                                  )}
                                </div>
                                {hasAnyDate && (
                                  <span className="text-[10px] opacity-60 ml-5 min-h-[12px]">
                                    {test.date || '\u00A0'}
                                  </span>
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{tooltipText}</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })()}

                      {/* Behavioral */}
                      {(() => {
                        const { test, isClickable, hasRecording, tooltipText } = getRecordingUiState('behavioral');
                        
                        return (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className={cn(
                                  "h-auto transition-all px-2.5 py-1 text-xs font-medium min-w-[120px]",
                                  hasAnyDate && "flex-col items-start gap-0.5",
                                  test.status === 'completed' && "bg-green-50 text-green-700 border-green-300 hover:bg-green-100 hover:border-green-400",
                                  test.status === 'scheduled' && "bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100 hover:border-amber-400",
                                  test.status === 'pending' && "bg-gray-50 text-gray-500 border-gray-300 cursor-not-allowed opacity-60",
                                  isClickable && "cursor-pointer hover:shadow-md"
                                )}
                                onClick={() => isClickable && handleScreenRecording('behavioral')}
                                disabled={!isClickable}
                              >
                                <div className="flex items-center w-full justify-between">
                                  <div className="flex items-center">
                                    {test.status === 'completed' ? (
                                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                                    ) : test.status === 'scheduled' ? (
                                      <CalendarClock className="w-3.5 h-3.5 mr-1.5" />
                                    ) : (
                                      <Circle className="w-3.5 h-3.5 mr-1.5" />
                                    )}
                                    <span>Behavioral {test.status === 'completed' && `${test.value}%`}</span>
                                    {test.status === 'completed' && proctorVerdictFor('behavioral') && (
                                      <span
                                        role="button"
                                        tabIndex={0}
                                        onClick={(e) => openProctorEvidence('behavioral', e)}
                                        className="ml-1 inline-flex items-center cursor-pointer hover:scale-125 transition-transform flex-shrink-0"
                                        title={proctorVerdictFor('behavioral') === 'high_confidence' ? 'Cheating detected — click to view evidence' : 'Suspicious activity — click to view evidence'}
                                      >
                                        <ShieldAlert className={cn("w-3 h-3", proctorVerdictFor('behavioral') === 'high_confidence' ? "text-red-600" : "text-amber-600")} />
                                      </span>
                                    )}
                                  </div>
                                  {hasRecording && isClickable ? (
                                    <Video className="w-3 h-3 ml-1.5 opacity-50 flex-shrink-0" />
                                  ) : (
                                    <div className="w-3 h-3 ml-1.5 flex-shrink-0" />
                                  )}
                                </div>
                                {hasAnyDate && (
                                  <span className="text-[10px] opacity-60 ml-5 min-h-[12px]">
                                    {test.date || '\u00A0'}
                                  </span>
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{tooltipText}</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })()}

                      {/* Communication */}
                      {(() => {
                        const { test, isClickable, hasRecording, tooltipText } = getRecordingUiState('communication');

                        return (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className={cn(
                                  "h-auto transition-all px-2.5 py-1 text-xs font-medium min-w-[120px]",
                                  hasAnyDate && "flex-col items-start gap-0.5",
                                  test.status === 'completed' && "bg-green-50 text-green-700 border-green-300 hover:bg-green-100 hover:border-green-400",
                                  test.status === 'scheduled' && "bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100 hover:border-amber-400",
                                  test.status === 'pending' && "bg-gray-50 text-gray-500 border-gray-300 cursor-not-allowed opacity-60",
                                  isClickable && "cursor-pointer hover:shadow-md"
                                )}
                                onClick={() => isClickable && handleScreenRecording('communication')}
                                disabled={!isClickable}
                              >
                                <div className="flex items-center w-full justify-between">
                                  <div className="flex items-center">
                                    {test.status === 'completed' ? (
                                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                                    ) : test.status === 'scheduled' ? (
                                      <CalendarClock className="w-3.5 h-3.5 mr-1.5" />
                                    ) : (
                                      <Circle className="w-3.5 h-3.5 mr-1.5" />
                                    )}
                                    <span>Communication {test.status === 'completed' && `${test.value}%`}</span>
                                    {test.status === 'completed' && proctorVerdictFor('communication') && (
                                      <span
                                        role="button"
                                        tabIndex={0}
                                        onClick={(e) => openProctorEvidence('communication', e)}
                                        className="ml-1 inline-flex items-center cursor-pointer hover:scale-125 transition-transform flex-shrink-0"
                                        title={proctorVerdictFor('communication') === 'high_confidence' ? 'Cheating detected — click to view evidence' : 'Suspicious activity — click to view evidence'}
                                      >
                                        <ShieldAlert className={cn("w-3 h-3", proctorVerdictFor('communication') === 'high_confidence' ? "text-red-600" : "text-amber-600")} />
                                      </span>
                                    )}
                                  </div>
                                  {hasRecording && isClickable ? (
                                    <Video className="w-3 h-3 ml-1.5 opacity-50 flex-shrink-0" />
                                  ) : (
                                    <div className="w-3 h-3 ml-1.5 flex-shrink-0" />
                                  )}
                                </div>
                                {hasAnyDate && (
                                  <span className="text-[10px] opacity-60 ml-5 min-h-[12px]">
                                    {test.date || '\u00A0'}
                                  </span>
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{tooltipText}</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })()}

                      {/* Coding */}
                      {(() => {
                        const { test, isClickable, hasRecording, tooltipText } = getRecordingUiState('coding');
                        // Prefer detailed results (from Reports API) for consistency
                        const plagiarismScore = candidate.details?.plagiarism_percentage ?? candidate.plagiarism_percentage;
                        const plagiarismNum = plagiarismScore != null && plagiarismScore !== '' && plagiarismScore !== 'null'
                          ? parseInt(plagiarismScore) || 0
                          : null;
                        
                        return (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className={cn(
                                  "h-auto transition-all px-2.5 py-1 text-xs font-medium min-w-[120px]",
                                  hasAnyDate && "flex-col items-start gap-0.5",
                                  test.status === 'completed' && "bg-green-50 text-green-700 border-green-300 hover:bg-green-100 hover:border-green-400",
                                  test.status === 'scheduled' && "bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100 hover:border-amber-400",
                                  test.status === 'pending' && "bg-gray-50 text-gray-500 border-gray-300 cursor-not-allowed opacity-60",
                                  isClickable && "cursor-pointer hover:shadow-md"
                                )}
                                onClick={() => isClickable && handleScreenRecording('coding')}
                                disabled={!isClickable}
                              >
                                <div className="flex items-center w-full justify-between">
                                  <div className="flex items-center">
                                    {test.status === 'completed' ? (
                                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                                    ) : test.status === 'scheduled' ? (
                                      <CalendarClock className="w-3.5 h-3.5 mr-1.5" />
                                    ) : (
                                      <Circle className="w-3.5 h-3.5 mr-1.5" />
                                    )}
                                    <span className="flex items-center gap-1.5">
                                      <span>Coding {test.status === 'completed' && `${test.value}%`}</span>
                                      {test.status === 'completed' && plagiarismNum != null && (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <span className="inline-flex items-center">
                                                {plagiarismNum > 40 ? (
                                                  <ShieldAlert className={cn("w-3.5 h-3.5 text-red-600")} />
                                                ) : plagiarismNum > 20 ? (
                                                  <AlertTriangle className={cn("w-3.5 h-3.5 text-amber-600")} />
                                                ) : (
                                                  <ShieldCheck className={cn("w-3.5 h-3.5 text-green-600")} />
                                                )}
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p>Plagiarism: {plagiarismNum}%</p>
                                              {plagiarismNum > 40 && <p className="text-[10px] opacity-70">High similarity detected</p>}
                                              {plagiarismNum <= 20 && <p className="text-[10px] opacity-70">Low similarity - Original</p>}
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                    </span>
                                  </div>
                                  {hasRecording && isClickable ? (
                                    <Video className="w-3 h-3 ml-1.5 opacity-50 flex-shrink-0" />
                                  ) : (
                                    <div className="w-3 h-3 ml-1.5 flex-shrink-0" />
                                  )}
                                </div>
                                {hasAnyDate && (
                                  <span className="text-[10px] opacity-60 ml-5 min-h-[12px]">
                                    {test.date || '\u00A0'}
                                  </span>
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{tooltipText}</p>
                              {plagiarismNum != null && (
                                <p className="text-[10px] opacity-70">
                                  Plagiarism: {plagiarismNum}%
                                  {plagiarismNum > 40 && ' (High similarity detected)'}
                                </p>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        );
                      })()}

                      {/* Interview (Live Meet) */}
                      {(() => {
                        const interview = interviewSummary;
                        const interviewLoading =
                          isLoadingScreenRecording && screenRecordingTestType === 'interview';
                        const tileDisabled =
                          !interview.isClickable || interviewLoading || liveMeetBookingLoading;
                        const dateTimeLabel = interview.dateTime || interview.date || null;

                        return (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                role="button"
                                tabIndex={tileDisabled ? -1 : 0}
                                className={cn(
                                  "col-span-2 sm:[grid-column:4] sm:[grid-row:1/span_2] h-full min-h-[76px] transition-all px-2.5 py-2 text-xs font-medium rounded-md border flex flex-col gap-1",
                                  interview.status === 'completed' && "bg-green-50 text-green-700 border-green-300 hover:bg-green-100 hover:border-green-400",
                                  interview.status === 'scheduled' && "bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100 hover:border-amber-400",
                                  interview.status === 'pending' && "bg-gray-50/60 text-gray-500 border-gray-300 border-dashed hover:bg-gray-100 hover:border-gray-400",
                                  interview.isClickable && !tileDisabled && "cursor-pointer hover:shadow-md",
                                  tileDisabled && "opacity-60"
                                )}
                                onClick={() => {
                                  if (tileDisabled) return;
                                  handleInterviewAssessmentClick();
                                }}
                                onKeyDown={(event) => {
                                  if (tileDisabled) return;
                                  if (event.key !== 'Enter' && event.key !== ' ') return;
                                  if (event.target !== event.currentTarget) return;
                                  event.preventDefault();
                                  handleInterviewAssessmentClick();
                                }}
                              >
                                <div className="flex items-center w-full justify-between gap-1">
                                  <div className="flex items-center min-w-0">
                                    {interviewLoading ? (
                                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                    ) : interview.status === 'completed' ? (
                                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5 shrink-0" />
                                    ) : interview.status === 'scheduled' ? (
                                      <CalendarClock className="w-3.5 h-3.5 mr-1.5 shrink-0" />
                                    ) : (
                                      <Circle className="w-3.5 h-3.5 mr-1.5 shrink-0" />
                                    )}
                                    <span>Interview</span>
                                  </div>
                                  {interview.statusLabel ? (
                                    <Badge
                                      variant="outline"
                                      title={
                                        interview.statusKey === 'cancelled' && interview.cancelReason
                                          ? `Reason: ${interview.cancelReason}`
                                          : undefined
                                      }
                                      className={cn(
                                        "h-4 px-1 py-0 text-[9px] font-semibold leading-none shrink-0",
                                        getInterviewRoomStatusBadgeClassName(interview.statusKey)
                                      )}
                                    >
                                      {interview.statusLabel}
                                    </Badge>
                                  ) : interview.hasRecording && interview.status === 'completed' ? (
                                    <Video className="w-3 h-3 ml-1.5 opacity-50 flex-shrink-0" />
                                  ) : (
                                    <div className="w-3 h-3 ml-1.5 flex-shrink-0" />
                                  )}
                                </div>
                                {interview.roundLabel ? (
                                  <span className="text-[10px] font-medium ml-5 truncate">
                                    {interview.roundLabel}
                                  </span>
                                ) : null}
                                {dateTimeLabel && interview.status !== 'pending' && (
                                  <span className="text-[10px] opacity-60 ml-5 min-h-[12px]">
                                    {dateTimeLabel}
                                  </span>
                                )}
                                {interview.status === 'pending' && !interviewLoading ? (
                                  <div className="flex-1 flex flex-col items-center justify-center gap-1 py-1 text-center">
                                    <Calendar className="w-4 h-4 opacity-50" />
                                    <span className="text-[10px] font-medium opacity-70">{canScheduleInterview ? 'Schedule interview' : 'Not scheduled'}</span>
                                  </div>
                                ) : null}
                                {interview.canCancel && canScheduleInterview ? (
                                  <div className="mt-auto flex w-full gap-1 pointer-events-auto pt-1">
                                    <button
                                      type="button"
                                      className="flex-1 h-5 rounded border border-amber-300 bg-white px-1.5 text-[10px] font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                                      disabled={isCancellingLiveMeet || liveMeetBookingLoading}
                                      onClick={(e) => { e.stopPropagation(); openHumanInterview('scheduling'); }}
                                    >
                                      Reschedule
                                    </button>
                                    <button
                                      type="button"
                                      className="h-5 rounded border border-rose-200 bg-white px-2 text-[10px] font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                                      disabled={isCancellingLiveMeet || liveMeetBookingLoading}
                                      onClick={handleCancelLiveMeetFromTile}
                                    >
                                      {isCancellingLiveMeet ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                      ) : (
                                        'Cancel'
                                      )}
                                    </button>
                                  </div>
                                ) : null}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{interview.tooltipText}</p>
                              {interview.cancelReason ? (
                                <p className="mt-1 max-w-[220px]">Reason: {interview.cancelReason}</p>
                              ) : null}
                            </TooltipContent>
                          </Tooltip>
                        );
                      })()}

                      {/* Game — hidden for now so the 6 remaining pills align
                          as two clean rows of three (Screening, Personality,
                          Behavioral / Coding, Interview, Aptitude). Change
                          `false` to `true` below to bring it back. */}
                      {false && (() => {
                        const game = getGameAssessmentSummary();
                        const isClickable = true; // Game is always clickable to manage
                        const tooltipText = game.status === 'completed'
                          ? 'Click to view game results'
                          : game.status === 'in-progress'
                          ? 'Click to manage game assessment'
                          : game.status === 'scheduled'
                          ? 'Click to manage game assessment'
                          : 'Click to send game assessment';
                        const gameDate = candidate.game_scheduled_at || candidate.game_completed_at;
                        
                        return (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className={cn(
                                  "h-auto transition-all px-2.5 py-1 text-xs font-medium cursor-pointer hover:shadow-md min-w-[120px]",
                                  hasAnyDate && "flex-col items-start gap-0.5",
                                  game.status === 'completed' && "bg-purple-50 text-purple-700 border-purple-300 hover:bg-purple-100 hover:border-purple-400",
                                  game.status === 'scheduled' && "bg-indigo-50 text-indigo-700 border-indigo-300 hover:bg-indigo-100 hover:border-indigo-400",
                                  game.status === 'in-progress' && "bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100 hover:border-blue-400",
                                  game.status === 'pending' && "bg-gray-50 text-gray-500 border-gray-300 hover:bg-gray-100"
                                )}
                                disabled={game.status === 'pending' && !canScheduleTest}
                                onClick={() => setShowGamePopup(true)}
                              >
                                <div className="flex items-center w-full ">
                                  <div className="flex items-center">
                                    {game.status === 'completed' ? (
                                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                                    ) : game.status === 'in-progress' ? (
                                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                    ) : game.status === 'scheduled' ? (
                                      <CalendarClock className="w-3.5 h-3.5 mr-1.5" />
                                    ) : (
                                      <Circle className="w-3.5 h-3.5 mr-1.5" />
                                    )}
                                    <span>Game {game.status === 'completed' && game.text}</span>
                                  </div>
                                  <Gamepad2 className="w-3 h-3 ml-1.5 opacity-50 flex-shrink-0" />
                                </div>
                                {hasAnyDate && (
                                  <span className="text-[10px] opacity-60 ml-5 min-h-[12px]">
                                    {gameDate ? formatDateTime(gameDate) : '\u00A0'}
                                  </span>
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{tooltipText}</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })()}

                      {/* Aptitude */}
                      {(() => {
                        const { test, isClickable, hasRecording, tooltipText } = getRecordingUiState('aptitude');

                        return (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className={cn(
                                  "h-auto transition-all px-2.5 py-1 text-xs font-medium min-w-[120px]",
                                  hasAnyDate && "flex-col items-start gap-0.5",
                                  test.status === 'completed' && "bg-green-50 text-green-700 border-green-300 hover:bg-green-100 hover:border-green-400",
                                  test.status === 'scheduled' && "bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100 hover:border-amber-400",
                                  test.status === 'pending' && "bg-gray-50 text-gray-500 border-gray-300 cursor-not-allowed opacity-60",
                                  isClickable && "cursor-pointer hover:shadow-md"
                                )}
                                onClick={() => isClickable && handleScreenRecording('aptitude')}
                                disabled={!isClickable}
                              >
                                <div className="flex items-center w-full justify-between">
                                  <div className="flex items-center">
                                    {test.status === 'completed' ? (
                                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                                    ) : test.status === 'scheduled' ? (
                                      <CalendarClock className="w-3.5 h-3.5 mr-1.5" />
                                    ) : (
                                      <Circle className="w-3.5 h-3.5 mr-1.5" />
                                    )}
                                    <span>Aptitude {test.status === 'completed' && `${test.value}%`}</span>
                                  </div>
                                  {hasRecording && isClickable ? (
                                    <Video className="w-3 h-3 ml-1.5 opacity-50 flex-shrink-0" />
                                  ) : (
                                    <div className="w-3 h-3 ml-1.5 flex-shrink-0" />
                                  )}
                                </div>
                                {hasAnyDate && (
                                  <span className="text-[10px] opacity-60 ml-5 min-h-[12px]">
                                    {test.date || '\u00A0'}
                                  </span>
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{tooltipText}</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })()}
                    </div>
                  </TooltipProvider>
                </div>
              );
            })()}

          {/* Primary Actions - Ultra Compact */}
          <div className="w-full sm:w-auto grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-1 sm:self-end">
            {hasTestResults(candidate) && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 sm:h-7 text-xs px-2 w-full sm:w-auto"
                onClick={() => handleViewResult(candidate)}
              >
                <FileText className="w-3 h-3 mr-1" />
                Results Summary
              </Button>
            )}
            {canScheduleTest && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 sm:h-7 text-xs px-2 w-full sm:w-auto"
              onClick={() => setShowSchedulePopup(true)}
            >
              <Calendar className="w-3 h-3 mr-1" />
              AI Interview
            </Button>
            )}
            {canScheduleInterview && (
              <HumanInterviewTrigger
                variant="full"
                onOpen={() => openHumanInterview('scheduling')}
              />
            )}

            {canRescore && (!candidate.ai_processed ? (
              <Button
                size="sm"
                variant="outline"
                disabled={evaluating[candidate.id]}
                onClick={() => handleEvaluate(candidate.id, false)}
                className="flex items-center gap-1.5 text-primary border-primary/40"
              >
                {evaluating[candidate.id]
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Sparkles className="w-3.5 h-3.5" />
                }
                Evaluate -{costPerResume} credits
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                disabled={evaluating[candidate.id]}
                onClick={() => handleEvaluate(candidate.id, true)}
                className="flex items-center gap-1.5 text-muted-foreground border-primary/40"
              >
                {evaluating[candidate.id]
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <RefreshCw className="w-3.5 h-3.5" />
                }
                Re-evaluate -{costPerResume} credits
              </Button>
            ))}

            {canChangeStatus && (
            <Button
              variant={candidate.isShortlisted ? 'default' : 'outline'}
              size="sm"
              className={cn(
                "h-8 sm:h-7 text-xs px-2 w-full sm:w-auto",
                !candidate.isShortlisted && "hover:border-success/60 hover:bg-success-foreground/10 hover:text-success",
                candidate.isShortlisted && "bg-success-foreground hover:bg-success-foreground/80 text-success-text"
              )}
              onClick={() => handleAction('shortlist')}
              disabled={isUpdating}
            >
              {candidate.isShortlisted ? (
                <>
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Shortlisted
                </>
              ) : (
                <>
                  <UserCheck className="w-3 h-3 mr-1" />
                  Shortlist
                </>
              )}
            </Button>
            )}

            {canChangeStatus && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={candidate.isRejected ? 'destructive' : 'outline'}
                    size="sm"
                    className={cn(
                      "h-8 sm:h-7 text-xs px-2 w-full sm:w-auto",
                      candidate.isRejected && "bg-error-foreground hover:bg-error-foreground/80 text-error-text",
                      !candidate.isRejected && "hover:bg-error-foreground/50 hover:text-error hover:border-error/30"
                    )}
                    onClick={openRejectFlow}
                    disabled={isUpdating}
                  >
                    {candidate.isRejected ? (
                      <>
                        <XCircle className="w-3 h-3 mr-1" />
                        Rejected
                      </>
                    ) : (
                      <>
                        <X className="w-3 h-3 mr-1" />
                        Reject
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  {candidate.isRejected && storedRejectReason ? (
                    <p>Reason: {storedRejectReason}</p>
                  ) : (
                    rejectHoverText
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            )}

            {/* More Actions Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 sm:h-7 w-full sm:w-7 p-0">
                  <MoreHorizontal className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                {canSelect && (
                <DropdownMenuItem
                  onClick={handleMarkSelected}
                  disabled={markedSelected || isSelecting}
                  className={`text-xs py-1.5 ${markedSelected ? 'text-green-600 font-medium' : 'text-green-700 focus:text-green-800'}`}
                >
                  {markedSelected ? (
                    <><CheckCircle2 className="w-3 h-3 mr-2" />Selected</>
                  ) : (
                    <><UserCheck className="w-3 h-3 mr-2" />{isSelecting ? 'Marking…' : 'Mark as Selected'}</>
                  )}
                </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleDownloadCV} className="text-xs py-1.5">
                  <Download className="w-3 h-3 mr-2" />
                  Resume
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => hasTestResults(candidate) && handleViewResult(candidate)}
                  disabled={!hasTestResults(candidate)}
                  className="text-xs py-1.5"
                >
                  <FileText className="w-3 h-3 mr-2" />
                  Test Results
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowContactPopup(true)} className="text-xs py-1.5">
                  <Phone className="w-3 h-3 mr-2" />
                  Contact Info
                </DropdownMenuItem>
                {canPhoneScreen && (
                <DropdownMenuItem onClick={() => setShowVoiceScreenPopup(true)} className="text-xs py-1.5">
                  <PhoneCall className="w-3 h-3 mr-2" />
                  AI Phone Screen
                </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          </div>
        </CardContent>
      </Card>

      {/* All the existing dialogs remain the same */}
      {/* Contact Information Popup */}
      <Dialog open={showContactPopup} onOpenChange={(open) => { 
        if (!open) {
          setShowContactPopup(false);
          handleCancelEdit();
        }
      }}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Contact Information</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {isEditingContact ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="contact-name">Name</Label>
                  <Input
                    id="contact-name"
                    type="text"
                    value={editedContact.name}
                    onChange={(e) => setEditedContact({ ...editedContact, name: e.target.value })}
                    placeholder="Enter name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-email">Email</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={editedContact.email}
                    onChange={(e) => setEditedContact({ ...editedContact, email: e.target.value })}
                    placeholder="Enter email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-phone">Phone</Label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    value={editedContact.phone}
                    onChange={(e) => setEditedContact({ ...editedContact, phone: e.target.value })}
                    placeholder="Enter phone number"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between py-2">
                  <Text size="sm" className="font-medium text-muted-foreground">Name:</Text>
                  <Text size="sm">{candidate.name || 'Anonymous'}</Text>
                </div>
                <Separator />
                <div className="flex items-center justify-between py-2">
                  <Text size="sm" className="font-medium text-muted-foreground">Email:</Text>
                  <Text size="sm">{canSeeContact ? candidate.candidate_email : 'Hidden for your role'}</Text>
                </div>
                <Separator />
                <div className="flex items-center justify-between py-2">
                  <Text size="sm" className="font-medium text-muted-foreground">Phone:</Text>
                  <Text size="sm">{candidate.phone || 'Not available'}</Text>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            {isEditingContact ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleCancelEdit}
                  disabled={isSavingContact}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveContact}
                  disabled={isSavingContact}
                  style={{
                    backgroundColor: getColor('primary'),
                    color: getColor('primaryForeground'),
                  }}
                >
                  {isSavingContact ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </>
                  )}
                </Button>
              </>
            ) : canChangeStatus ? (
              <Button
                onClick={handleEditContact}
                style={{
                  backgroundColor: getColor('primary'),
                  color: getColor('primaryForeground'),
                }}
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Interview Scheduling Popup */}
      {showSchedulePopup && (
        <InterviewSchedulePopup
          candidate={candidate}
          onClose={() => setShowSchedulePopup(false)}
          jobDetails={jobDetails}
        />
      )}

      {/* AI telephonic screening (Exotel voice agent) */}
      {showVoiceScreenPopup && (
        <VoiceScreenPopup
          open
          candidate={candidate}
          jobId={candidate?.job_id || jobDetails?.job_id}
          onClose={() => setShowVoiceScreenPopup(false)}
        />
      )}

      {renderHumanInterviewSuite()}

      {showGamePopup && (
        <GameAssessmentPopup 
          candidate={candidate}
          jobDetails={jobDetails}
          onClose={() => setShowGamePopup(false)}
          onSuccess={() => window.location.reload()}
        />
      )}

      {/* Video Resume Popup */}
      <Dialog open={showVideoPopup && !!videoData} onOpenChange={setShowVideoPopup}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>🎥 Video Resume - {candidate.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {videoData?.videoUrl && (
              <video 
                controls 
                autoPlay
                className="w-full max-h-[70vh] rounded-lg"
              >
                <source
                    src={videoData.videoUrl}
                    type={videoData.filename?.endsWith('.mp4') ? 'video/mp4' : 'video/webm'}
                  />
                Your browser does not support the video tag.
              </video>
            )}
            <VideoAiSummary analysis={videoData?.analysis} />
            {videoData?.filename && (
              <Text size="xs" variant="muted" className="text-center">
                📄 {videoData.filename}
              </Text>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* No Video Resume Available Popup */}
      <Dialog open={showNoVideoPopup} onOpenChange={setShowNoVideoPopup}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Video Resume Not Available</DialogTitle>
            <DialogDescription>
              {candidate.name} has not uploaded a video resume yet.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-6 space-y-4">
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center">
              <Video className="w-10 h-10 text-muted-foreground" />
            </div>
            <Text size="sm" variant="muted" className="text-center">
              {videoRequestState === 'sent'
                ? 'Request sent! The candidate got an email with a no-login recording link (valid 7 days). The icon turns green once they upload.'
                : videoRequestState === 'cooldown' || priorRequestAt
                ? 'A video request was already sent to this candidate recently. To avoid spamming them, a new one can be sent after the 72-hour window.'
                : videoRequestState === 'already_has_video'
                ? 'Good news — this candidate uploaded a video just now. Close and reopen to view it.'
                : videoRequestState === 'limit'
                ? videoLimitMessage
                : videoRequestState === 'error'
                ? 'Could not send the request. Please try again in a moment.'
                : 'Send them a one-tap recording link by email — they can record from their phone in 2 minutes, no login needed, with AI coaching included.'}
            </Text>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNoVideoPopup(false)}>
              Close
            </Button>
            {canRequestVideo && videoRequestState !== 'sent' && videoRequestState !== 'already_has_video' && !priorRequestAt && videoRequestState !== 'cooldown' && videoRequestState !== 'limit' && (
              <Button
                onClick={handleRequestVideoResume}
                disabled={videoRequestState === 'sending'}
                className="gap-2"
              >
                {videoRequestState === 'sending'
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Video className="w-4 h-4" />}
                Request video resume
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Screen Recording Popup */}
      <Dialog open={showScreenRecordingPopup && !!screenRecordingData} onOpenChange={setShowScreenRecordingPopup}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {screenRecordingData?.testType === 'interview'
                ? '📹 Interview Recording'
                : `📹 Screen Recording - ${screenRecordingData?.testType?.charAt(0).toUpperCase() + screenRecordingData?.testType?.slice(1)} Test`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {screenRecordingData?.videoUrl && (
              <video 
                controls 
                autoPlay
                className="w-full max-h-[70vh] rounded-lg"
              >
                <source
                    src={screenRecordingData.videoUrl}
                    type={screenRecordingData.filename?.endsWith('.mp4') ? 'video/mp4' : 'video/webm'}
                  />
                Your browser does not support the video tag.
              </video>
            )}
            <div className="space-y-1 text-center">
              {screenRecordingData?.filename && (
                <Text size="xs" variant="muted">
                  📄 {screenRecordingData.filename}
                </Text>
              )}
              {screenRecordingData?.testId && (
                <Text size="xs" variant="muted">
                  Test ID: {screenRecordingData.testId}
                </Text>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// AIInsightCardExpandable is imported from ./components/AIInsightCardExpandable
// at the top of this file. The inline definition that used to live here
// was extracted so the company-side ResumeDetailsModal can render the
// same card style without duplicating the variant config / expand
// logic — single source of truth across both surfaces.

const MEETING_INSIGHT_PROMPTS = [
  'Summarize this interview',
  'Candidate strengths',
  'Weaknesses',
  'Hiring recommendation',
];

const formatMeetingInsightStatus = (status) => {
  const value = String(status || 'pending').trim().toLowerCase();
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const getMeetingInsightStatusTone = (status) => {
  const value = String(status || 'pending').trim().toLowerCase();
  if (value === 'completed') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (value === 'failed') return 'border-red-200 bg-red-50 text-red-700';
  if (value === 'processing') return 'border-sky-200 bg-sky-50 text-sky-700';
  return 'border-amber-200 bg-amber-50 text-amber-700';
};

const EMPTY_MEETING_INSIGHTS_STATE = {
  has_booking: false,
  booking: null,
  meeting_completed: false,
  transcript_status: 'pending',
  transcript_segments: [],
  summary_status: 'pending',
  summary: {},
  chat_history: [],
  last_error: null,
  transcript_dummy_mode: false,
};

// Patterns that mean "transcript hasn't been generated yet" — these are
// normal early-state, NOT errors. We strip them so HR never sees raw S3 /
// file-store internals like "NoSuchKey" or "GetObject operation".
const MEETING_INSIGHTS_PENDING_ERROR_SIGNALS = [
  'no transcript segments',
  'nosuchkey',
  'the specified key does not exist',
  'transcript is not available',
  'not available yet',
  'getobject operation',
  'failed to read transcript json',
  'no transcript segments in file store',
];

const isMeetingInsightsPendingError = (message) => {
  if (!message) return false;
  const text = String(message).toLowerCase();
  return MEETING_INSIGHTS_PENDING_ERROR_SIGNALS.some((signal) => text.includes(signal));
};

// Translate a backend error into something HR can read. Anything that's
// just "transcript not generated yet" gets suppressed entirely. Anything
// else is passed through unchanged so genuine failures still surface.
const sanitizeMeetingInsightsError = (message) => {
  if (!message) return '';
  if (isMeetingInsightsPendingError(message)) return '';
  return String(message).trim();
};

function MeetingInsightsDialog({
  candidate,
  jobDetails,
  onClose,
  embedded = false,
  isPanelActive = true,
  activeBooking = null,
  bookingRefreshKey = '',
}) {
  const candidateEmail = candidate?.candidate_email || candidate?.email || '';
  const candidateName = candidate?.name || candidate?.full_name || candidateEmail || 'Candidate';
  const urlJobId = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return String(new URLSearchParams(window.location.search).get('jobId') || '').trim();
  }, []);
  const jobId = String(candidate?.job_id || jobDetails?.job_id || urlJobId || '').trim();
  const [activeTab, setActiveTab] = useState('summary');
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingTranscript, setIsFetchingTranscript] = useState(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [isAskingChat, setIsAskingChat] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [question, setQuestion] = useState('');
  const chatScrollRef = useRef(null);
  const autoSummaryRef = useRef('');
  const [insightsState, setInsightsState] = useState(EMPTY_MEETING_INSIGHTS_STATE);
  const [isOpeningTranscriptPdf, setIsOpeningTranscriptPdf] = useState(false);
  const [interviewerFeedback, setInterviewerFeedback] = useState(null);
  const autoFetchAttemptedRef = useRef(false);
  const [liveMeetApplications, setLiveMeetApplications] = useState([]);
  const [liveMeetApplicationStatus, setLiveMeetApplicationStatus] = useState('idle');
  const [selectedLiveMeetApplicationId, setSelectedLiveMeetApplicationId] = useState('');
  const [liveMeetFinalSummary, setLiveMeetFinalSummary] = useState(null);
  // True once we've successfully loaded insights at least once. Used to keep
  // transient network blips (e.g. a backend restart, or the connection being
  // briefly saturated) from replacing already-good data with a scary
  // "Cannot reach API" panel + repeated error toasts on every poll.
  const hasLoadedInsightsRef = useRef(false);

  const loadInsights = async ({ silent = false } = {}) => {
    if (!candidateEmail || !jobId) {
      setLoadError('Candidate email or job id is missing.');
      setIsLoading(false);
      return;
    }

    try {
      if (!silent) setIsLoading(true);
      setLoadError('');
      const response = await axios.get(getApiUrl('/api/meeting/post-interview'), {
        params: { candidate_email: candidateEmail, job_id: jobId },
      });
      // Belt-and-suspenders: even if a legacy DB row still has the raw
      // S3/NoSuchKey message persisted, never let it reach the UI.
      const data = response.data || {};
      if (isMeetingInsightsPendingError(data.last_error)) {
        data.last_error = null;
      }
      setInsightsState(data);
      hasLoadedInsightsRef.current = true;
    } catch (error) {
      const isNetworkBlip = error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED';
      // Transient network blip AND we already have good data on screen →
      // swallow it. Don't blank the panel, don't toast. The next poll (or the
      // user reopening) will refresh once the backend is reachable again.
      if (isNetworkBlip && hasLoadedInsightsRef.current) {
        return;
      }
      const details = error.response?.data?.details;
      const message =
        error.response?.data?.error ||
        (isNetworkBlip
          ? `Cannot reach API at ${getApiUrl('/api/meeting/post-interview')}. Check backend is on port 5000 and REACT_APP_API_URL.`
          : 'Failed to load meeting insights.');
      const fullMessage = details ? `${message} (${details})` : message;
      setLoadError(fullMessage);
      if (!silent) toast.error(message);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    autoFetchAttemptedRef.current = false;
    autoSummaryRef.current = '';
    // New candidate/job → forget the previous load so a real connectivity
    // failure for THIS candidate still surfaces (instead of being swallowed
    // as a "transient blip" against stale data from the prior candidate).
    hasLoadedInsightsRef.current = false;
    setQuestion('');
    setLoadError('');
    setInsightsState(EMPTY_MEETING_INSIGHTS_STATE);
    loadInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateEmail, jobId, bookingRefreshKey]);

  useEffect(() => {
    if (!isPanelActive) return;
    // Panel re-opened — refresh quietly. If it's just a transient blip we keep
    // whatever we already showed rather than flashing an error.
    loadInsights({ silent: hasLoadedInsightsRef.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPanelActive, candidateEmail, jobId]);

  const insightsBooking = insightsState?.booking || null;
  const booking = activeBooking || insightsBooking;
  const activeBookingId = String(booking?.booking_id || '').trim();
  const insightsBookingId = String(insightsBooking?.booking_id || '').trim();
  const insightsBookingAligned =
    !activeBookingId || !insightsBookingId || activeBookingId === insightsBookingId;
  // Activity derives a completed interview from the same booking's room state
  // and session evidence. Reuse that outcome here so Insights cannot remain
  // locked when Activity already correctly shows the interview as completed.
  const bookingStatus = resolveInterviewRoomStatus(booking);
  const meetingHadSession = Boolean(
    booking?.meeting_had_session || insightsState?.meeting_had_session
  );
  const meetingCompleted = Boolean(
    (
      booking?.meeting_completed ||
      insightsState?.meeting_completed ||
      bookingStatus.key === 'completed'
    ) && meetingHadSession
  );
  const meetingNoShow =
    String(booking?.meeting_outcome || '').trim().toLowerCase() === 'expired_no_show' ||
    bookingStatus.key === 'not_joined';
  const liveRoomId = String(
    insightsState?.live_room_id || booking?.live_room_id || activeBooking?.live_room_id || ''
  ).trim();
  const normalizedMeetingProvider = String(booking?.meeting_provider || '').trim().toLowerCase();
  const isLiveMeetBooking =
    normalizedMeetingProvider === 'live_meet' || normalizedMeetingProvider === 'search_job_meet';
  const insightsUnlocked =
    insightsState?.transcript_dummy_mode ||
    (isLiveMeetBooking
      ? meetingCompleted && insightsBookingAligned
      : meetingCompleted);
  const displayTranscriptStatus = insightsUnlocked
    ? insightsState?.transcript_status
    : 'pending';
  const displaySummaryStatus = insightsUnlocked ? insightsState?.summary_status : 'pending';
  const transcriptReady =
    insightsUnlocked &&
    insightsState?.transcript_status === 'completed' &&
    (insightsState?.transcript_segments || []).length > 0;
  // Meet final summaries independently revalidate server-attested worker-final
  // segments in their own staff-only endpoint.  Do not block that action on
  // the legacy Activity transcript projection, which can lag the room store.
  const liveMeetSummaryEvidenceAvailable = Boolean(
    isLiveMeetBooking && insightsUnlocked && liveRoomId
  );
  const summaryActionAvailable = isLiveMeetBooking
    ? liveMeetSummaryEvidenceAvailable
    : transcriptReady;
  const meetingStatusLabel = meetingNoShow
    ? booking?.meeting_outcome_label || bookingStatus.label || 'Not joined'
    : meetingCompleted
      ? 'Completed'
      : bookingStatus.label || 'Pending';
  const canFetchTranscript = Boolean(
    insightsState?.has_booking &&
    insightsUnlocked &&
    (isLiveMeetBooking ? liveRoomId : true)
  );
  const liveMeetSummaryScope = `${liveRoomId}::${activeBookingId}`;

  useEffect(() => {
    setLiveMeetApplications([]);
    setLiveMeetApplicationStatus('idle');
    setSelectedLiveMeetApplicationId('');
    setLiveMeetFinalSummary(null);
  }, [liveMeetSummaryScope]);

  useEffect(() => {
    if (!isLiveMeetBooking || !insightsUnlocked || !liveRoomId) return undefined;
    let cancelled = false;
    setLiveMeetApplicationStatus('loading');
    getLiveInterviewEligibleApplications(liveRoomId)
      .then((payload) => {
        if (cancelled) return;
        const applications = Array.isArray(payload?.applications) ? payload.applications : [];
        setLiveMeetApplications(applications);
        setLiveMeetApplicationStatus(applications.length ? 'ready' : 'unavailable');
        // The existing endpoint has already verified the room, booking,
        // candidate, job, and tenant. Auto-select only an unambiguous match.
        setSelectedLiveMeetApplicationId(applications.length === 1 ? String(applications[0].application_id || '') : '');
      })
      .catch(() => {
        if (!cancelled) {
          setLiveMeetApplications([]);
          setLiveMeetApplicationStatus('unavailable');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isLiveMeetBooking, insightsUnlocked, liveRoomId, liveMeetSummaryScope]);

  const resolvedInterviewerFeedback =
    interviewerFeedback || booking?.interviewer_feedback || null;

  const feedbackInsightStatus = (() => {
    if (!insightsUnlocked) {
      return { key: 'pending', label: 'Pending' };
    }
    const status = String(resolvedInterviewerFeedback?.status || '').trim().toLowerCase();
    if (status === 'submitted') {
      return { key: 'completed', label: 'Submitted' };
    }
    if (status === 'deferred') {
      return { key: 'pending', label: 'Pending' };
    }
    if (status === 'pending') {
      return { key: 'processing', label: 'In progress' };
    }
    return { key: 'pending', label: 'Pending' };
  })();

  useEffect(() => {
    setInterviewerFeedback(null);
  }, [candidateEmail, jobId, bookingRefreshKey, activeBookingId]);

  useEffect(() => {
    if (!isPanelActive || !insightsUnlocked || !activeBookingId) return undefined;
    let cancelled = false;
    const loadFeedback = async () => {
      try {
        const data = await fetchInterviewerFeedbackByBooking(
          activeBookingId,
          getInterviewerFeedbackActorEmail()
        );
        if (cancelled) return;
        setInterviewerFeedback(data?.feedback || null);
      } catch {
        if (!cancelled) {
          setInterviewerFeedback(booking?.interviewer_feedback || null);
        }
      }
    };
    loadFeedback();
    return () => {
      cancelled = true;
    };
  }, [
    isPanelActive,
    insightsUnlocked,
    activeBookingId,
    bookingRefreshKey,
    booking?.interviewer_feedback,
  ]);

  useEffect(() => {
    if (!chatScrollRef.current) return;
    chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [insightsState?.chat_history, isAskingChat]);

  const callInsightsAction = async ({ url, body, setBusy, successMessage }) => {
    try {
      setBusy(true);
      setLoadError('');
      const response = await axios.post(getApiUrl(url), body);
      const data = response.data || {};
      if (isMeetingInsightsPendingError(data.last_error)) {
        data.last_error = null;
      }
      setInsightsState(data);
      const rawActionError = data.error || data.last_error;
      const actionError = sanitizeMeetingInsightsError(rawActionError);
      if (data.success === false || actionError) {
        const message = actionError || 'Meeting insights action failed.';
        // Only surface as an error banner when the action actually failed.
        // A pending transcript is a normal state, not a failure.
        if (data.success === false || actionError) {
          setLoadError(message);
          toast.error(message);
          return false;
        }
      }
      if (successMessage) toast.success(successMessage);
      return true;
    } catch (error) {
      const details = error.response?.data?.details;
      const rawMessage =
        error.response?.data?.error ||
        error.response?.data?.last_error ||
        'Meeting insights action failed.';
      const message = sanitizeMeetingInsightsError(rawMessage) || 'Meeting insights action failed.';
      const fullMessage = details ? `${message} (${details})` : message;
      setLoadError(fullMessage);
      toast.error(message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleFetchTranscript = (forceRefresh = false) =>
    callInsightsAction({
      url: '/api/meeting/post-interview/fetch-transcript',
      body: { candidate_email: candidateEmail, job_id: jobId, force_refresh: forceRefresh },
      setBusy: setIsFetchingTranscript,
      successMessage: 'Transcript fetched successfully.',
    });

  useEffect(() => {
    if (isLoading || isFetchingTranscript || transcriptReady || !canFetchTranscript) {
      return;
    }
    if (autoFetchAttemptedRef.current) {
      return;
    }
    autoFetchAttemptedRef.current = true;
    handleFetchTranscript(false);
  }, [isLoading, isFetchingTranscript, transcriptReady, canFetchTranscript, candidateEmail, jobId]);

  const handleGenerateLiveMeetSummary = async () => {
    if (!liveRoomId || !selectedLiveMeetApplicationId) {
      setLoadError('A verified application context is required before generating the final summary.');
      return false;
    }
    try {
      setIsGeneratingSummary(true);
      setLoadError('');
      const payload = await generateLiveInterviewFinalSummary(
        liveRoomId,
        selectedLiveMeetApplicationId,
      );
      if (payload?.analysis_state !== 'complete' || !payload?.summary) {
        setLoadError('Final interview summary is unavailable.');
        return false;
      }
      setLiveMeetFinalSummary({ scope: liveMeetSummaryScope, summary: payload.summary });
      toast.success('Final interview summary generated.');
      return true;
    } catch {
      setLoadError('Final interview summary is unavailable.');
      return false;
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const handleGenerateSummary = (forceRefresh = false) =>
    isLiveMeetBooking
      ? handleGenerateLiveMeetSummary()
      : callInsightsAction({
          url: '/api/meeting/post-interview/generate-summary',
          body: { candidate_email: candidateEmail, job_id: jobId, force_refresh: forceRefresh },
          setBusy: setIsGeneratingSummary,
          successMessage: 'Interview summary generated.',
        });

  useEffect(() => {
    if (isLiveMeetBooking) return;
    const autoKey = `${candidateEmail}-${jobId}-${insightsState?.summary_status}-${transcriptReady}`;
    if (!transcriptReady) return;
    if (isGeneratingSummary) return;
    if (!['pending', 'failed', 'processing'].includes(insightsState?.summary_status)) return;
    if (autoSummaryRef.current === autoKey) return;
    autoSummaryRef.current = autoKey;
    handleGenerateSummary(false);
  }, [transcriptReady, isGeneratingSummary, insightsState?.summary_status, candidateEmail, jobId, isLiveMeetBooking]);

  const handleAskQuestion = async (nextQuestion) => {
    const normalizedQuestion = String(nextQuestion || question).trim();
    if (!normalizedQuestion) return;
    const success = await callInsightsAction({
      url: '/api/meeting/post-interview/chat',
      body: { candidate_email: candidateEmail, job_id: jobId, question: normalizedQuestion },
      setBusy: setIsAskingChat,
    });
    if (success) setQuestion('');
  };

  const handleViewTranscriptPdf = async () => {
    if (!liveRoomId) {
      toast.error('Transcript PDF is not available for this booking yet.');
      return;
    }
    try {
      setIsOpeningTranscriptPdf(true);
      const payload = await fetchLiveMeetTranscriptPdfUrl({ liveRoomId });
      const pdfUrl = String(payload?.pdf_url || payload?.transcript_pdf?.url || '').trim();
      if (!pdfUrl) {
        throw new Error('Transcript PDF URL is not available.');
      }
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      const message = error?.message || 'Failed to open transcript PDF.';
      setLoadError(message);
      toast.error(message);
    } finally {
      setIsOpeningTranscriptPdf(false);
    }
  };

  const transcriptSourceDescription = isLiveMeetBooking
    ? 'Search Job Meet transcript is loaded from the linked room transcript for this booking.'
    : 'Drive transcript is normalized into anonymized speaker segments.';
  const transcriptEmptyDescription = isLiveMeetBooking
    ? 'Once transcript is captured in the Search Job Meet room for this booking, fetch it here.'
    : 'Once the meeting is completed and the transcript is available in Drive, fetch it here.';
  const resolvedLiveMeetSummary =
    liveMeetFinalSummary?.scope === liveMeetSummaryScope ? liveMeetFinalSummary.summary : null;
  const liveMeetSkillCoverage = Array.isArray(resolvedLiveMeetSummary?.skill_coverage)
    ? resolvedLiveMeetSummary.skill_coverage
    : [];
  const liveMeetCoverageCounts = liveMeetSkillCoverage.reduce(
    (counts, skill) => {
      const status = String(skill?.status || '').toLowerCase();
      if (status === 'covered' || status === 'needs_review' || status === 'not_covered') {
        counts[status] += 1;
      }
      return counts;
    },
    { covered: 0, needs_review: 0, not_covered: 0 }
  );
  const liveMeetCoverageTotal = liveMeetSkillCoverage.length;

  const insightsPanel = (
    <>
        {!embedded ? (
          <DialogHeader>
            <DialogTitle>Insights</DialogTitle>
            <DialogDescription>
              Review post-interview transcript, summary, and AI chat for <strong>{candidateName}</strong>.
            </DialogDescription>
          </DialogHeader>
        ) : null}

        <div
          className={cn(
            'space-y-4 overflow-y-auto pr-1',
            embedded ? 'max-h-[calc(92vh-220px)]' : 'max-h-[calc(90vh-120px)]'
          )}
        >
          {/* Inline status strip — replaces the redundant candidate identity
              card (the Hub header already shows name / email / role). Only
              shows pipeline state + scheduled slot, the two pieces of context
              insights actually need. */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={getMeetingInsightStatusTone(displayTranscriptStatus)}>
                  Transcript · {formatMeetingInsightStatus(displayTranscriptStatus)}
                </Badge>
                <Badge variant="outline" className={getMeetingInsightStatusTone(displaySummaryStatus)}>
                  Summary · {formatMeetingInsightStatus(displaySummaryStatus)}
                </Badge>
                <button
                  type="button"
                  onClick={() => setActiveTab('feedback')}
                  className={cn(
                    'inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors hover:opacity-90',
                    getMeetingInsightStatusTone(feedbackInsightStatus.key)
                  )}
                  title="Open interviewer feedback"
                >
                  Feedback · {feedbackInsightStatus.label}
                </button>
                <Badge
                  variant="outline"
                  className={
                    meetingNoShow
                      ? 'border-orange-200 bg-orange-50 text-orange-700'
                      : meetingCompleted
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : 'border-amber-200 bg-amber-50 text-amber-700'
                  }
                >
                  Meeting · {meetingStatusLabel}
                </Badge>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={loadInsights}
                disabled={isLoading || isFetchingTranscript || isGeneratingSummary || isAskingChat}
                className="h-8 px-2 text-slate-500 hover:text-slate-900"
                title="Refresh insights"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </div>
            {booking ? (
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                <span>
                  <span className="font-semibold text-slate-700">Interviewer:</span>{' '}
                  {booking?.interviewer_name || 'Interviewer'}
                </span>
                <span className="text-slate-300">·</span>
                <span>
                  <span className="font-semibold text-slate-700">Slot:</span>{' '}
                  {booking?.slot_label || 'Unavailable'}
                </span>
              </div>
            ) : null}
          </div>

          {insightsState?.booking_job_mismatch ? (
            <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3">
              <Text size="sm" className="font-medium text-sky-900">Interview found on a different job</Text>
              <Text size="sm" className="mt-1 text-sky-800">
                Search Job Meet for this candidate is linked to job {booking?.job_id}. You opened job {jobId}.
                Transcript and AI insights use the linked interview below.
              </Text>
            </div>
          ) : null}

          {!insightsState?.has_booking ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <Text size="sm" className="font-medium text-amber-900">Interview not booked yet</Text>
              <Text size="sm" className="mt-1 text-amber-800">
                No Search Job Meet booking exists for this candidate on job {jobId || 'this job'}.
                If the interview was on another job (e.g. 842633), open Insights from that job&apos;s candidate card.
              </Text>
            </div>
          ) : !insightsUnlocked ? (
            <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3">
              <Text size="sm" className="font-medium text-sky-900">
                {isLiveMeetBooking && !insightsBookingAligned
                  ? 'Waiting for this interview to finish'
                  : 'Meeting is still pending completion'}
              </Text>
              <Text size="sm" className="mt-1 text-sky-800">
                {isLiveMeetBooking && !insightsBookingAligned
                  ? 'Transcript, summary, and AI chat unlock after this scheduled Search Job Meet ends. Earlier interview data is hidden until then.'
                  : 'Transcript, summary, and AI chat unlock after the scheduled interview has completed.'}
              </Text>
            </div>
          ) : null}

          {/* Last-line defense: only render the load error banner when it's
              an actual failure, not a pending-transcript signal. */}
          {sanitizeMeetingInsightsError(loadError) && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
              <Text size="sm" className="text-red-700">
                {sanitizeMeetingInsightsError(loadError)}
              </Text>
            </div>
          )}

          {/* Underline tab nav — matches the HumanInterviewHub style instead
              of the old button-style toggle so the two layers feel cohesive. */}
          <div
            role="tablist"
            aria-label="Insights sections"
            className="flex gap-1 border-b border-slate-200"
          >
            {[
              { key: 'summary', label: 'Summary', icon: Sparkles },
              { key: 'feedback', label: 'Feedback', icon: ClipboardList },
              { key: 'chat', label: 'AI Chat', icon: MessageSquare },
              { key: 'transcript', label: 'Transcript', icon: FileText },
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(tab.key)}
                  className={cn(
                    'relative -mb-px inline-flex items-center gap-1.5 border-b-2 px-3 pb-2.5 pt-1.5 text-sm font-semibold transition-colors',
                    isActive
                      ? 'border-emerald-600 text-slate-900'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  )}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {isLoading ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-12 text-center">
              <Loader2 className="w-6 h-6 mx-auto mb-3 animate-spin text-slate-500" />
              <Text className="font-medium">Loading meeting insights...</Text>
            </div>
          ) : (
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                {activeTab === 'summary' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <Text className="font-semibold text-slate-900">AI Summary</Text>
                        <Text size="sm" variant="muted">Leadership-ready summary generated from the interview transcript.</Text>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleGenerateSummary(false)}
                        disabled={
                          !summaryActionAvailable ||
                          isGeneratingSummary ||
                          (isLiveMeetBooking && (!selectedLiveMeetApplicationId || Boolean(resolvedLiveMeetSummary)))
                        }
                      >
                        {isGeneratingSummary ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                        {isLiveMeetBooking
                          ? resolvedLiveMeetSummary
                            ? 'Final Summary Generated'
                            : 'Generate Final Summary'
                          : insightsState?.summary_status === 'completed'
                            ? 'Regenerate Summary'
                            : 'Generate Summary'}
                      </Button>
                    </div>

                    {isLiveMeetBooking && summaryActionAvailable ? (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                        {liveMeetApplicationStatus === 'loading' ? (
                          <Text size="sm" variant="muted">Verifying interview context…</Text>
                        ) : liveMeetApplicationStatus === 'ready' && liveMeetApplications.length === 1 ? (
                          <Text size="sm" className="text-slate-700">Verified interview context is ready.</Text>
                        ) : liveMeetApplicationStatus === 'ready' && liveMeetApplications.length > 1 ? (
                          <div className="space-y-2">
                            <Label htmlFor="live-meet-summary-application">Select the interview application</Label>
                            <select
                              id="live-meet-summary-application"
                              className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900"
                              value={selectedLiveMeetApplicationId}
                              onChange={(event) => setSelectedLiveMeetApplicationId(event.target.value)}
                            >
                              <option value="">Select an application</option>
                              {liveMeetApplications.map((application, index) => (
                                <option key={application.application_id} value={application.application_id}>
                                  Application {index + 1}{application.application_status ? ` · ${application.application_status}` : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <Text size="sm" className="text-slate-700">
                            Verified application context is unavailable for this interview.
                          </Text>
                        )}
                      </div>
                    ) : null}

                    {!summaryActionAvailable ? (
                      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
                        <Text className="font-medium">Transcript required first</Text>
                        <Text size="sm" variant="muted" className="mt-1">
                          {isLiveMeetBooking
                            ? 'The completed Meet room must have verified worker-final conversation evidence.'
                            : 'Fetch the transcript before generating the summary.'}
                        </Text>
                      </div>
                    ) : isGeneratingSummary || insightsState?.summary_status === 'processing' ? (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-8 text-center">
                        <Loader2 className="w-6 h-6 mx-auto mb-3 animate-spin text-slate-500" />
                        <Text className="font-medium">Generating AI summary…</Text>
                      </div>
                    ) : insightsState?.summary_status === 'failed' &&
                      sanitizeMeetingInsightsError(insightsState?.last_error) ? (
                      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                        <Text size="sm" className="text-red-700">
                          {sanitizeMeetingInsightsError(insightsState.last_error)}
                        </Text>
                      </div>
                    ) : null}

                    {summaryActionAvailable && !isGeneratingSummary && (isLiveMeetBooking || insightsState?.summary_status !== 'processing') ? (
                      isLiveMeetBooking ? (
                        resolvedLiveMeetSummary ? (
                          <div className="grid gap-4 lg:grid-cols-2">
                            <Card className="border-slate-200 lg:col-span-2"><CardContent className="p-4"><Text className="font-medium text-slate-900">Interview overview</Text><Text size="sm" className="mt-2 leading-7 text-slate-700">{resolvedLiveMeetSummary.overview}</Text></CardContent></Card>
                            <Card className="border-slate-200 lg:col-span-2"><CardContent className="p-4"><Text className="font-medium text-slate-900">Candidate answer assessment</Text><Text size="sm" className="mt-2 leading-7 text-slate-700">{resolvedLiveMeetSummary.candidate_answer_assessment}</Text></CardContent></Card>
                            <Card className="border-slate-200 lg:col-span-2">
                              <CardContent className="p-4">
                                <div className="flex flex-wrap items-center justify-between gap-2"><Text className="font-medium text-slate-900">JD skill coverage</Text><Text size="sm" variant="muted">{liveMeetCoverageCounts.covered} covered · {liveMeetCoverageCounts.needs_review} needs review · {liveMeetCoverageCounts.not_covered} not covered</Text></div>
                                <div className="mt-3 flex h-2 overflow-hidden rounded-full bg-slate-100" role="img" aria-label={`${liveMeetCoverageCounts.covered} covered, ${liveMeetCoverageCounts.needs_review} needs review, and ${liveMeetCoverageCounts.not_covered} not covered skills`}>
                                  {liveMeetCoverageTotal ? <><div className="bg-emerald-600" style={{ width: `${(liveMeetCoverageCounts.covered / liveMeetCoverageTotal) * 100}%` }} /><div className="bg-amber-400" style={{ width: `${(liveMeetCoverageCounts.needs_review / liveMeetCoverageTotal) * 100}%` }} /><div className="bg-slate-300" style={{ width: `${(liveMeetCoverageCounts.not_covered / liveMeetCoverageTotal) * 100}%` }} /></> : null}
                                </div>
                                <div className="mt-4 space-y-2">{liveMeetSkillCoverage.map((skill) => <div key={skill.skill_id} className="rounded-lg bg-slate-50 px-3 py-2"><div className="flex flex-wrap items-center justify-between gap-2"><Text size="sm" className="font-medium text-slate-800">{skill.label}</Text><Text size="sm" className="capitalize text-slate-600">{String(skill.status || '').replace('_', ' ')}</Text></div><Text size="sm" variant="muted" className="mt-1">{skill.cv_claimed ? 'CV mentions this skill.' : 'CV does not mention this skill.'} {skill.evidence_summary}</Text></div>)}</div>
                              </CardContent>
                            </Card>
                            <Card className="border-slate-200"><CardContent className="p-4"><Text className="font-medium text-slate-900">Strengths</Text><div className="mt-2 space-y-2">{resolvedLiveMeetSummary.strengths.map((item, index) => <div key={index} className="rounded-lg bg-slate-50 px-3 py-2"><Text size="sm" className="text-slate-700">{item}</Text></div>)}</div></CardContent></Card>
                            <Card className="border-slate-200"><CardContent className="p-4"><Text className="font-medium text-slate-900">Gaps and areas for review</Text><div className="mt-2 space-y-2">{resolvedLiveMeetSummary.gaps.map((item, index) => <div key={index} className="rounded-lg bg-slate-50 px-3 py-2"><Text size="sm" className="text-slate-700">{item}</Text></div>)}</div></CardContent></Card>
                            <Card className="border-slate-200 lg:col-span-2"><CardContent className="p-4"><Text className="font-medium text-slate-900">Recommendations</Text><div className="mt-2 space-y-2">{resolvedLiveMeetSummary.recommendations.map((item, index) => <div key={index} className="rounded-lg bg-slate-50 px-3 py-2"><Text size="sm" className="text-slate-700">{item}</Text></div>)}</div></CardContent></Card>
                          </div>
                        ) : (
                          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center"><Text className="font-medium">Generate the final interview summary</Text><Text size="sm" variant="muted" className="mt-1">The summary uses only verified worker-final conversation evidence and the selected application context.</Text></div>
                        )
                      ) : (
                        <div className="grid gap-4 lg:grid-cols-2">
                          <Card className="border-slate-200"><CardContent className="p-4"><Text className="font-medium text-slate-900">Overview</Text><Text size="sm" className="mt-2 leading-7 text-slate-700">{insightsState?.summary?.overview || 'No summary generated yet.'}</Text></CardContent></Card>
                          <Card className="border-slate-200"><CardContent className="p-4"><Text className="font-medium text-slate-900">Communication Style</Text><Text size="sm" className="mt-2 leading-7 text-slate-700">{insightsState?.summary?.communication_style || 'No communication review available yet.'}</Text></CardContent></Card>
                          <Card className="border-slate-200"><CardContent className="p-4"><Text className="font-medium text-slate-900">Recommendation</Text><Text size="sm" className="mt-2 leading-7 text-slate-700">{insightsState?.summary?.recommendation || 'No recommendation generated yet.'}</Text></CardContent></Card>
                          <Card className="border-slate-200"><CardContent className="p-4"><Text className="font-medium text-slate-900">Strengths</Text><div className="mt-2 space-y-2">{(insightsState?.summary?.strengths || []).length ? insightsState.summary.strengths.map((item, index) => <div key={index} className="rounded-lg bg-slate-50 px-3 py-2"><Text size="sm" className="text-slate-700">{item}</Text></div>) : <Text size="sm" variant="muted">No strengths generated yet.</Text>}</div></CardContent></Card>
                          <Card className="border-slate-200"><CardContent className="p-4"><Text className="font-medium text-slate-900">Weaknesses</Text><div className="mt-2 space-y-2">{(insightsState?.summary?.weaknesses || []).length ? insightsState.summary.weaknesses.map((item, index) => <div key={index} className="rounded-lg bg-slate-50 px-3 py-2"><Text size="sm" className="text-slate-700">{item}</Text></div>) : <Text size="sm" variant="muted">No weaknesses generated yet.</Text>}</div></CardContent></Card>
                          <Card className="border-slate-200"><CardContent className="p-4"><Text className="font-medium text-slate-900">Technical Observations</Text><div className="mt-2 space-y-2">{(insightsState?.summary?.technical_observations || []).length ? insightsState.summary.technical_observations.map((item, index) => <div key={index} className="rounded-lg bg-slate-50 px-3 py-2"><Text size="sm" className="text-slate-700">{item}</Text></div>) : <Text size="sm" variant="muted">No technical observations generated yet.</Text>}</div></CardContent></Card>
                        </div>
                      )
                    ) : null}
                  </div>
                )}

                {activeTab === 'feedback' && (
                  <div className="space-y-4">
                    {!insightsUnlocked ? (
                      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
                        <ClipboardList className="mx-auto mb-3 h-8 w-8 text-slate-400" aria-hidden />
                        <Text className="font-medium text-slate-900">Feedback not available yet</Text>
                        <Text size="sm" variant="muted" className="mt-1">
                          Interviewer feedback unlocks after the Search Job Meet interview has ended.
                        </Text>
                      </div>
                    ) : resolvedInterviewerFeedback ? (
                      <InterviewerFeedbackSummary
                        feedback={resolvedInterviewerFeedback}
                        compact
                        insightsMode
                      />
                    ) : (
                      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
                        <ClipboardList className="mx-auto mb-3 h-8 w-8 text-slate-400" aria-hidden />
                        <Text className="font-medium text-slate-900">No interviewer feedback yet</Text>
                        <Text size="sm" variant="muted" className="mt-1">
                          Feedback appears here after the interviewer submits it or chooses to complete it later.
                        </Text>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'chat' && (
                  <div className="space-y-4">
                    <div>
                      <Text className="font-semibold text-slate-900">AI Chat</Text>
                      <Text size="sm" variant="muted">Ask focused questions grounded in the summary and transcript.</Text>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {MEETING_INSIGHT_PROMPTS.map((prompt) => (
                        <Button key={prompt} type="button" variant="outline" size="sm" onClick={() => handleAskQuestion(prompt)} disabled={!transcriptReady || isAskingChat}>
                          {prompt}
                        </Button>
                      ))}
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <div ref={chatScrollRef} className="max-h-[320px] space-y-3 overflow-y-auto pr-1">
                        {transcriptReady && (insightsState?.chat_history || []).length ? (
                          insightsState.chat_history.map((item, index) => (
                            <div key={`${item.timestamp || index}-${index}`} className="space-y-2">
                              <div className="rounded-xl border border-sky-100 bg-sky-50 px-4 py-3">
                                <Text size="xs" className="font-semibold uppercase tracking-wide text-sky-700">Question</Text>
                                <Text size="sm" className="mt-1 font-medium text-slate-800">{item.question}</Text>
                              </div>
                              <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                                <Text size="xs" className="font-semibold uppercase tracking-wide text-slate-500">Answer</Text>
                                <Text size="sm" className="mt-1 leading-7 text-slate-700">{item.answer}</Text>
                              </div>
                            </div>
                          ))
                        ) : (
                          <Text size="sm" variant="muted">No chat history yet. Ask a question once the transcript is ready.</Text>
                        )}
                        {isAskingChat && (
                          <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">
                            <div className="flex items-center gap-2 text-emerald-700">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <Text size="sm" className="font-medium text-emerald-800">Generating grounded answer...</Text>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <Input
                          value={question}
                          onChange={(event) => setQuestion(event.target.value)}
                          placeholder={transcriptReady ? 'Ask about strengths, communication, weaknesses, or recommendation...' : 'Transcript required before AI chat'}
                          disabled={!transcriptReady || isAskingChat}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                              event.preventDefault();
                              handleAskQuestion();
                            }
                          }}
                        />
                        <Button onClick={() => handleAskQuestion()} disabled={!transcriptReady || isAskingChat || !question.trim()}>
                          {isAskingChat ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                          Ask
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'transcript' && (
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <Text className="font-semibold text-slate-900">Transcript</Text>
                        <Text size="sm" variant="muted">{transcriptSourceDescription}</Text>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleFetchTranscript(transcriptReady)} disabled={isFetchingTranscript || !canFetchTranscript}>
                          {isFetchingTranscript ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                          {transcriptReady ? 'Refresh Transcript' : 'Fetch Transcript'}
                        </Button>
                        {isLiveMeetBooking && liveRoomId ? (
                          <Button variant="outline" size="sm" onClick={handleViewTranscriptPdf} disabled={isOpeningTranscriptPdf}>
                            {isOpeningTranscriptPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                            View PDF
                          </Button>
                        ) : null}
                      </div>
                    </div>

                    {transcriptReady && (insightsState?.transcript_segments || []).length ? (
                      <div className="max-h-[420px] space-y-3 overflow-y-auto pr-1">
                        {insightsState.transcript_segments.map((segment, index) => (
                          <Card key={`${segment.speaker_label || 'speaker'}-${index}`} className="border-slate-200 shadow-sm">
                            <CardContent className="p-4 space-y-2">
                              <div className="flex items-center justify-between gap-3">
                                <Badge variant="outline" className="border-slate-300 bg-white text-slate-700">{segment.speaker_label || 'Speaker'}</Badge>
                                {(segment.start_time || segment.end_time) && (
                                  <Text size="xs" variant="muted">{[segment.start_time, segment.end_time].filter(Boolean).join(' - ')}</Text>
                                )}
                              </div>
                              <Text size="sm" className="leading-7 text-slate-700">{segment.transcript_text}</Text>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
                        <Text className="font-medium">No transcript available yet</Text>
                        <Text size="sm" variant="muted" className="mt-1">{transcriptEmptyDescription}</Text>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Footer:
            - When embedded inside HumanInterviewHub, the Refresh button is
              already in the status strip and Close is at the dialog level —
              skip the footer entirely so the panel ends flush.
            - When standalone, keep the Close button. */}
        {!embedded ? (
          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              variant="outline"
              onClick={loadInsights}
              disabled={isLoading || isFetchingTranscript || isGeneratingSummary || isAskingChat}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </DialogFooter>
        ) : null}
    </>
  );

  if (embedded) {
    // Match the gutter of the other two tabs inside HumanInterviewHub so
    // switching tabs doesn't visibly shift content.
    return <div className="px-6 pb-6 pt-5">{insightsPanel}</div>;
  }

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        {insightsPanel}
      </DialogContent>
    </Dialog>
  );
}

function MeetingInterviewerDialog({ candidate, onClose, onContinue }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isSendingMagicLink, setIsSendingMagicLink] = useState(false);
  const [isSavingAvailability, setIsSavingAvailability] = useState(false);
  const [isSavingLeave, setIsSavingLeave] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isEditingAvailability, setIsEditingAvailability] = useState(false);
  const [interviewerEmail, setInterviewerEmail] = useState(getStoredMeetingOwnerEmail);
  const [interviewerDetails, setInterviewerDetails] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [availabilityState, setAvailabilityState] = useState({
    exists: false,
    active: false,
    data: null,
  });
  const [leaveConfigured, setLeaveConfigured] = useState(false);
  const [availabilityForm, setAvailabilityForm] = useState({
    timezone: 'Asia/Calcutta',
    monday_enabled: true,
    tuesday_enabled: true,
    wednesday_enabled: true,
    thursday_enabled: true,
    friday_enabled: true,
    saturday_enabled: false,
    sunday_enabled: false,
    start_time: '10:00',
    end_time: '18:00',
    slot_duration_minutes: 30,
    buffer_minutes: 10,
    time_format: '12h',
    is_active: true,
    interviewer_id: '',
  });
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const populateAvailabilityForm = (availability, interviewer) => {
    setAvailabilityForm({
      timezone: availability?.timezone || 'Asia/Calcutta',
      monday_enabled: Boolean(availability?.monday_enabled ?? true),
      tuesday_enabled: Boolean(availability?.tuesday_enabled ?? true),
      wednesday_enabled: Boolean(availability?.wednesday_enabled ?? true),
      thursday_enabled: Boolean(availability?.thursday_enabled ?? true),
      friday_enabled: Boolean(availability?.friday_enabled ?? true),
      saturday_enabled: Boolean(availability?.saturday_enabled ?? false),
      sunday_enabled: Boolean(availability?.sunday_enabled ?? false),
      start_time: availability?.start_time || '10:00',
      end_time: availability?.end_time || '18:00',
      slot_duration_minutes: Number(availability?.slot_duration_minutes || 30),
      buffer_minutes: Number(availability?.buffer_minutes || 10),
      time_format: availability?.time_format || '12h',
      is_active: availability?.is_active ?? true,
      interviewer_id: availability?.interviewer_id || interviewer?.email || interviewerEmail || '',
    });
  };

  const fetchLeaveState = async (email) => {
    const normalizedEmail = (email || '').trim().toLowerCase();
    if (!normalizedEmail) {
      setLeaveConfigured(false);
      return;
    }

    try {
      const response = await axios.get(getApiUrl('/api/meeting/interviewer/leave'), {
        params: { email: normalizedEmail },
      });
      setLeaveConfigured(Boolean(response.data?.leave_configured));
    } catch (leaveError) {
      setLeaveConfigured(false);
    }
  };

  const fetchStatus = async (email, options = {}) => {
    const nextEmail = (email || '').trim().toLowerCase();
    const loadingState = options.loading ?? false;
    const refreshingState = options.refreshing ?? false;

    if (!nextEmail) {
      setInterviewerDetails(null);
      setAvailableSlots([]);
      setIsEditingAvailability(false);
      setStatus('unsynced');
      setMagicLinkSent(false);
      setError('');
      setIsLoading(false);
      return;
    }

    if (loadingState) setIsLoading(true);
    if (refreshingState) setIsRefreshing(true);
    setError('');

    try {
      const response = await axios.get(getApiUrl('/api/meeting/interviewer/status'), {
        params: { email: nextEmail },
      });

      const data = response.data || {};
      setInterviewerEmail(nextEmail);
      setInterviewerDetails(data.interviewer || null);
      setAvailableSlots(Array.isArray(data.slots) ? data.slots : []);
      setAvailabilityState({
        exists: Boolean(data.availability_exists),
        active: Boolean(data.availability_active),
        data: data.availability || null,
      });
      setIsEditingAvailability(false);
      if (data.synced && data.availability_active) {
        await fetchLeaveState(nextEmail);
      } else {
        setLeaveConfigured(false);
      }
      if (data.interviewer) {
        populateAvailabilityForm(data.availability, data.interviewer);
      }

      if (data.synced) {
        setStatus('verified');
        setMagicLinkSent(false);
      } else if (magicLinkSent || options.keepPending) {
        setStatus('pending');
      } else {
        setStatus('unsynced');
      }
    } catch (fetchError) {
      setStatus('error');
      setError(fetchError.response?.data?.error || 'Failed to check interviewer meeting status.');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStatus(interviewerEmail, { loading: true });
  }, []);

  useEffect(() => {
    const normalizedEmail = (interviewerEmail || '').trim().toLowerCase();
    if (!normalizedEmail) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      fetchStatus(normalizedEmail, { loading: false });
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [interviewerEmail]);

  useEffect(() => {
    if (status !== 'pending' || !interviewerEmail) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      fetchStatus(interviewerEmail, { keepPending: true });
    }, 10000);

    return () => window.clearInterval(intervalId);
  }, [status, interviewerEmail]);

  const handleSendMagicLink = async () => {
    const normalizedEmail = interviewerEmail.trim().toLowerCase();

    if (!normalizedEmail) {
      setError('Interviewer email is required.');
      return;
    }

    setIsSendingMagicLink(true);
    setError('');

    try {
      await axios.post(getApiUrl('/api/meeting/interviewer/send-magic-link'), {
        interviewer_email: normalizedEmail,
      });

      setMagicLinkSent(true);
      setStatus('pending');
      toast.success('Magic link sent to interviewer email.');
    } catch (sendError) {
      const message = sendError.response?.data?.error || 'Failed to send magic link.';
      setError(message);
      toast.error(message);
    } finally {
      setIsSendingMagicLink(false);
    }
  };

  const handleRefresh = () => {
    fetchStatus(interviewerEmail, { refreshing: true, keepPending: status === 'pending' });
  };

  const updateAvailabilityField = (field, value) => {
    setAvailabilityForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleSaveAvailability = async () => {
    const workingDaySelected = [
      availabilityForm.monday_enabled,
      availabilityForm.tuesday_enabled,
      availabilityForm.wednesday_enabled,
      availabilityForm.thursday_enabled,
      availabilityForm.friday_enabled,
      availabilityForm.saturday_enabled,
      availabilityForm.sunday_enabled,
    ].some(Boolean);

    if (!availabilityForm.timezone.trim()) {
      setError('Timezone is required.');
      return;
    }
    if (!workingDaySelected) {
      setError('Select at least one working day.');
      return;
    }
    if (!availabilityForm.start_time || !availabilityForm.end_time) {
      setError('Start time and end time are required.');
      return;
    }
    if (availabilityForm.end_time <= availabilityForm.start_time) {
      setError('End time must be later than start time.');
      return;
    }
    if (Number(availabilityForm.slot_duration_minutes) <= 0) {
      setError('Slot duration must be greater than zero.');
      return;
    }
    if (Number(availabilityForm.buffer_minutes) < 0) {
      setError('Buffer minutes cannot be negative.');
      return;
    }

    setIsSavingAvailability(true);
    setError('');

    try {
      const response = await axios.post(getApiUrl('/api/meeting/interviewer/availability'), {
        interviewer_email: interviewerEmail.trim().toLowerCase(),
        interviewer_id: availabilityForm.interviewer_id || interviewerDetails?.email || interviewerEmail,
        timezone: availabilityForm.timezone.trim(),
        monday_enabled: availabilityForm.monday_enabled,
        tuesday_enabled: availabilityForm.tuesday_enabled,
        wednesday_enabled: availabilityForm.wednesday_enabled,
        thursday_enabled: availabilityForm.thursday_enabled,
        friday_enabled: availabilityForm.friday_enabled,
        saturday_enabled: availabilityForm.saturday_enabled,
        sunday_enabled: availabilityForm.sunday_enabled,
        start_time: availabilityForm.start_time,
        end_time: availabilityForm.end_time,
        slot_duration_minutes: Number(availabilityForm.slot_duration_minutes),
        buffer_minutes: Number(availabilityForm.buffer_minutes),
        time_format: availabilityForm.time_format,
        is_active: availabilityForm.is_active,
      });

      const data = response.data || {};
      setAvailabilityState({
        exists: Boolean(data.availability_exists),
        active: Boolean(data.availability_active),
        data: data.availability || null,
      });
      setAvailableSlots(Array.isArray(data.slots) ? data.slots : []);
      if (data.availability) {
        populateAvailabilityForm(data.availability, interviewerDetails);
      }
      setIsEditingAvailability(false);
      await fetchLeaveState(interviewerEmail);
      toast.success('Interviewer availability saved successfully.');
    } catch (saveError) {
      const message = saveError.response?.data?.error || 'Failed to save interviewer availability.';
      setError(message);
      toast.error(message);
    } finally {
      setIsSavingAvailability(false);
    }
  };

  const handleSaveNoLeave = async () => {
    setIsSavingLeave(true);
    setError('');

    try {
      const response = await axios.post(getApiUrl('/api/meeting/interviewer/leave'), {
        interviewer_email: interviewerEmail.trim().toLowerCase(),
        leave_entries: [],
      });

      const data = response.data || {};
      setLeaveConfigured(Boolean(data.leave_configured));
      setAvailableSlots(Array.isArray(data.slots) ? data.slots : []);
      toast.success('Leave settings saved. Slots are ready to review.');
    } catch (leaveError) {
      const message = leaveError.response?.data?.error || 'Failed to save interviewer leave settings.';
      setError(message);
      toast.error(message);
    } finally {
      setIsSavingLeave(false);
    }
  };

  const candidateName = candidate?.name || candidate?.full_name || candidate?.candidate_email || 'Candidate';
  const showVerifiedState = status === 'verified' && interviewerDetails;
  const showAvailabilitySetup = showVerifiedState && (isEditingAvailability || !availabilityState.exists || !availabilityState.active);
  const showLeaveSetup = showVerifiedState && availabilityState.exists && availabilityState.active && !leaveConfigured;
  const availabilityDays = [
    ['monday_enabled', 'Mon'],
    ['tuesday_enabled', 'Tue'],
    ['wednesday_enabled', 'Wed'],
    ['thursday_enabled', 'Thu'],
    ['friday_enabled', 'Fri'],
    ['saturday_enabled', 'Sat'],
    ['sunday_enabled', 'Sun'],
  ];

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Meeting Setup</DialogTitle>
          <DialogDescription>
            Verify interviewer meeting access before sending meeting slots.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-8 flex flex-col items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            <Text size="sm" variant="muted">Checking interviewer calendar sync...</Text>
          </div>
        ) : (
          <div className="space-y-4 max-h-[calc(90vh-190px)] overflow-y-auto pr-1">
            {(status === 'unsynced' || status === 'pending' || status === 'error') && (
              <Card className="p-4 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Text className="font-medium">Interviewer Calendar</Text>
                    <Text size="sm" variant="muted">
                      {status === 'pending'
                        ? 'Verification is pending. Refresh after the interviewer clicks the magic link.'
                        : 'Calendar is not synced yet. Enter interviewer email to send a verification link.'}
                    </Text>
                  </div>
                  <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">
                    {status === 'pending' ? 'Verification Pending' : 'Not Synced'}
                  </Badge>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="meeting-interviewer-email">Interviewer Email</Label>
                  <Input
                    id="meeting-interviewer-email"
                    type="email"
                    value={interviewerEmail}
                    onChange={(e) => setInterviewerEmail(e.target.value)}
                    placeholder="name@company.com"
                    disabled={isSendingMagicLink}
                  />
                </div>

                {magicLinkSent && (
                  <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
                    <Text size="sm" className="text-emerald-700">
                      Magic link sent successfully. Ask the interviewer to verify, then refresh this popup.
                    </Text>
                  </div>
                )}

                {error && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
                    <Text size="sm" className="text-red-700">{error}</Text>
                  </div>
                )}
              </Card>
            )}

            {showVerifiedState && (
              <Card className="p-4 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <Text className="font-medium">{interviewerDetails.name}</Text>
                    <Text size="sm" variant="muted">{interviewerDetails.email}</Text>
                  </div>
                  <div className="flex gap-2 flex-wrap justify-end">
                    <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50">
                      Verified
                    </Badge>
                    <Badge variant="outline" className="text-sky-700 border-sky-200 bg-sky-50">
                      Calendar Synced
                    </Badge>
                  </div>
                </div>

                {availabilityState.exists && (
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        populateAvailabilityForm(availabilityState.data, interviewerDetails);
                        setIsEditingAvailability(true);
                        setError('');
                      }}
                      disabled={isSavingAvailability || isSavingLeave}
                    >
                      Edit Availability
                    </Button>
                  </div>
                )}

                {showAvailabilitySetup ? (
                  <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50/70 p-4">
                    <div className="space-y-1">
                      <Text size="sm" className="font-medium">
                        {availabilityState.exists ? 'Edit Interviewer Availability' : 'Set Interviewer Availability'}
                      </Text>
                      <Text size="sm" variant="muted">
                        {availabilityState.exists
                          ? 'Update the interviewer availability and save the changes to refresh the available slots.'
                          : 'This interviewer is connected, but availability is not configured yet. Save availability first to generate slots.'}
                      </Text>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Timezone</Label>
                        <Input
                          value={availabilityForm.timezone}
                          onChange={(e) => updateAvailabilityField('timezone', e.target.value)}
                          placeholder="Asia/Calcutta"
                          disabled={isSavingAvailability}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Interviewer ID</Label>
                        <Input
                          value={availabilityForm.interviewer_id}
                          onChange={(e) => updateAvailabilityField('interviewer_id', e.target.value)}
                          placeholder="interviewer identifier"
                          disabled={isSavingAvailability}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Working Days</Label>
                      <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
                        {availabilityDays.map(([field, label]) => (
                          <label key={field} className="flex items-center gap-2 rounded-md border bg-white px-3 py-2 text-sm">
                            <Checkbox
                              checked={availabilityForm[field]}
                              onCheckedChange={(checked) => updateAvailabilityField(field, Boolean(checked))}
                              disabled={isSavingAvailability}
                            />
                            <span>{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Start Time</Label>
                        <Input
                          type="time"
                          value={availabilityForm.start_time}
                          onChange={(e) => updateAvailabilityField('start_time', e.target.value)}
                          disabled={isSavingAvailability}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>End Time</Label>
                        <Input
                          type="time"
                          value={availabilityForm.end_time}
                          onChange={(e) => updateAvailabilityField('end_time', e.target.value)}
                          disabled={isSavingAvailability}
                        />
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Slot Duration (minutes)</Label>
                        <Input
                          type="number"
                          min="1"
                          value={availabilityForm.slot_duration_minutes}
                          onChange={(e) => updateAvailabilityField('slot_duration_minutes', e.target.value)}
                          disabled={isSavingAvailability}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Buffer (minutes)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={availabilityForm.buffer_minutes}
                          onChange={(e) => updateAvailabilityField('buffer_minutes', e.target.value)}
                          disabled={isSavingAvailability}
                        />
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Time Format</Label>
                        <Select
                          value={availabilityForm.time_format}
                          onValueChange={(value) => updateAvailabilityField('time_format', value)}
                          disabled={isSavingAvailability}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose format" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="12h">12h</SelectItem>
                            <SelectItem value="24h">24h</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Availability Status</Label>
                        <Select
                          value={availabilityForm.is_active ? 'active' : 'inactive'}
                          onValueChange={(value) => updateAvailabilityField('is_active', value === 'active')}
                          disabled={isSavingAvailability}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={handleSaveAvailability}
                        disabled={isSavingAvailability}
                        style={{
                          backgroundColor: getColor('primary'),
                          color: getColor('primaryForeground'),
                        }}
                      >
                        {isSavingAvailability ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Saving Availability...
                          </>
                        ) : (
                          availabilityState.exists ? 'Update Availability' : 'Save Availability'
                        )}
                      </Button>
                      {availabilityState.exists && (
                        <Button
                          variant="outline"
                          onClick={() => {
                            populateAvailabilityForm(availabilityState.data, interviewerDetails);
                            setIsEditingAvailability(false);
                            setError('');
                          }}
                          disabled={isSavingAvailability}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>

                    {error && (
                      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
                        <Text size="sm" className="text-red-700">{error}</Text>
                      </div>
                    )}
                  </div>
                ) : showLeaveSetup ? (
                  <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50/70 p-4">
                    <div className="space-y-1">
                      <Text size="sm" className="font-medium">Confirm Leave Settings</Text>
                      <Text size="sm" variant="muted">
                        Availability is saved. Complete the leave step once so the backend can generate slots for this interviewer.
                      </Text>
                    </div>

                    <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-3">
                      <Text size="sm" className="text-sky-800">
                        If there is no leave to block right now, continue with no leave and generate slots.
                      </Text>
                    </div>

                    <Button
                      onClick={handleSaveNoLeave}
                      disabled={isSavingLeave}
                      style={{
                        backgroundColor: getColor('primary'),
                        color: getColor('primaryForeground'),
                      }}
                    >
                      {isSavingLeave ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving Leave...
                        </>
                      ) : (
                        'No Leave For Now, Generate Slots'
                      )}
                    </Button>

                    {error && (
                      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
                        <Text size="sm" className="text-red-700">{error}</Text>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Text size="sm" className="font-medium">Available Interviewer Slots</Text>
                    {availableSlots.length ? (
                      <div className="grid gap-2 max-h-56 overflow-y-auto">
                        {availableSlots.map((slot) => (
                          <div
                            key={slot.value}
                            className="rounded-md border bg-muted/30 px-3 py-2 text-sm"
                          >
                            {slot.label}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3">
                        <Text size="sm" className="text-amber-800">
                          No future slots are available right now. Refresh or update the interviewer availability to generate new slots.
                        </Text>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            {!isLoading && (
              <Button
                variant="outline"
                onClick={handleRefresh}
                disabled={isRefreshing || !interviewerEmail}
              >
                {isRefreshing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </>
                )}
              </Button>
            )}
          </div>

          {showVerifiedState ? (
            <Button
              onClick={() => onContinue({
                interviewer: interviewerDetails,
                slots: availableSlots,
              })}
              disabled={!availableSlots.length || showAvailabilitySetup || showLeaveSetup}
              style={{
                backgroundColor: getColor('primary'),
                color: getColor('primaryForeground'),
              }}
            >
              Continue with {candidateName}
            </Button>
          ) : (
            <Button
              onClick={handleSendMagicLink}
              disabled={isLoading || isSendingMagicLink}
              style={{
                backgroundColor: getColor('primary'),
                color: getColor('primaryForeground'),
              }}
            >
              {isSendingMagicLink ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Magic Link'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Legacy inline automated scheduling UI (date-grouped slots). Prefer ./MeetingSchedulingDialog.jsx when onBack is not required. */
function LegacyInlineMeetingSchedulingDialog({ candidate, jobDetails, meetingSetup, nextRoundCandidates = [], onBack, onClose }) {
  const normalizeScheduleSlots = (slotList = []) => slotList.map((slot) => {
    const label = String(slot?.label || '');
    const match = label.match(/^([A-Za-z]{3},\s+\d{2}\s+[A-Za-z]{3}\s+\d{4})\s+(.*)$/);
    const dateLabel = match?.[1] || 'Available Date';
    const timeLabel = match?.[2] || label;
    const dateValueMatch = label.match(/^[A-Za-z]{3},\s+(\d{2})\s+([A-Za-z]{3})\s+(\d{4})/);
    const monthMap = {
      Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
      Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12',
    };
    const dateValue = dateValueMatch
      ? `${dateValueMatch[3]}-${monthMap[dateValueMatch[2]]}-${dateValueMatch[1]}`
      : '';

    return {
      ...slot,
      dateLabel,
      dateValue,
      timeLabel,
    };
  });
  const initialSlots = useMemo(
    () => normalizeScheduleSlots(meetingSetup?.slots || []),
    [meetingSetup?.slots]
  );
  const initialDateValue = initialSlots[0]?.dateValue || '';
  const [selectedDateValue, setSelectedDateValue] = useState(() => initialDateValue);
  const [dateSlots, setDateSlots] = useState(() => initialSlots.filter((slot) => slot.dateValue === initialDateValue));
  const [selectedSlots, setSelectedSlots] = useState(
    () => new Set(initialSlots.filter((slot) => slot.dateValue === initialDateValue).map((slot) => slot.value))
  );
  const [isLoadingDateSlots, setIsLoadingDateSlots] = useState(false);
  const [dateSlotError, setDateSlotError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [meetingProvider, setMeetingProvider] = useState('google_meet');

  useEffect(() => {
    const def = meetingSetup?.availability?.meeting_provider_default;
    if (def === 'live_meet' || def === 'search_job_meet') {
      setMeetingProvider('live_meet');
    } else if (def === 'google_meet') {
      setMeetingProvider('google_meet');
    }
  }, [meetingSetup?.availability?.meeting_provider_default]);

  const candidateName = candidate?.name || candidate?.full_name || candidate?.candidate_email || 'Candidate';
  const candidateEmail = candidate?.candidate_email || candidate?.email || '';
  const primaryCandidate = useMemo(
    () => ({
      candidate_email: candidateEmail,
      candidate_name: candidateName,
      name: candidateName,
      email: candidateEmail,
      job_id: candidate?.job_id || jobDetails?.job_id || '',
    }),
    [candidate?.job_id, candidateEmail, candidateName, jobDetails?.job_id]
  );
  const meetingRecipients = useMemo(() => {
    const merged = [primaryCandidate, ...(nextRoundCandidates || [])];
    const seen = new Set();
    return merged
      .map((item) => {
        const email = String(item?.candidate_email || item?.email || '').trim().toLowerCase();
        if (!email || seen.has(email)) return null;
        seen.add(email);
        return {
          candidate_email: email,
          candidate_name: String(item?.candidate_name || item?.name || email).trim(),
          job_id: String(item?.job_id || candidate?.job_id || jobDetails?.job_id || '').trim(),
          isPrimary: email === primaryCandidate.candidate_email,
        };
      })
      .filter(Boolean);
  }, [candidate?.job_id, jobDetails?.job_id, nextRoundCandidates, primaryCandidate]);
  const interviewer = meetingSetup?.interviewer || null;
  const interviewerEmail = interviewer?.email || '';
  const todayDateValue = new Date().toISOString().slice(0, 10);
  const selectedSlotCount = selectedSlots.size;
  const hasSelectedSlots = selectedSlotCount > 0;
  const [selectedRecipientEmails, setSelectedRecipientEmails] = useState(() =>
    primaryCandidate.candidate_email ? new Set([primaryCandidate.candidate_email]) : new Set()
  );
  const [candidateSearchQuery, setCandidateSearchQuery] = useState('');
  const selectedRecipients = useMemo(
    () => meetingRecipients.filter((item) => selectedRecipientEmails.has(item.candidate_email)),
    [meetingRecipients, selectedRecipientEmails]
  );
  const filteredMeetingRecipients = useMemo(() => {
    const query = candidateSearchQuery.trim().toLowerCase();
    if (!query) return meetingRecipients;
    return meetingRecipients.filter((item) => {
      const haystack = `${item.candidate_name} ${item.candidate_email}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [candidateSearchQuery, meetingRecipients]);
  const selectedRecipientCount = selectedRecipients.length;
  const selectedDateLabel = dateSlots[0]?.dateLabel || (
    selectedDateValue
      ? new Date(`${selectedDateValue}T00:00:00`).toLocaleDateString('en-GB', {
          weekday: 'short',
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : 'Available Date'
  );

  useEffect(() => {
    const nextDateValue = initialSlots[0]?.dateValue || '';
    const nextDateSlots = initialSlots.filter((slot) => slot.dateValue === nextDateValue);
    setSelectedDateValue(nextDateValue);
    setDateSlots(nextDateSlots);
    setSelectedSlots(new Set(nextDateSlots.map((slot) => slot.value)));
    setDateSlotError('');
    setSubmitError('');
    setSubmitSuccess(null);
  }, [initialSlots]);

  useEffect(() => {
    setSelectedRecipientEmails((previous) => {
      const availableEmails = new Set(meetingRecipients.map((item) => item.candidate_email));
      const next = new Set([...previous].filter((email) => availableEmails.has(email)));
      if (!next.size && primaryCandidate.candidate_email) {
        next.add(primaryCandidate.candidate_email);
      }
      return next;
    });
  }, [meetingRecipients, primaryCandidate.candidate_email]);

  useEffect(() => {
    if (!interviewerEmail || !selectedDateValue) {
      setDateSlots([]);
      setSelectedSlots(new Set());
      return;
    }

    let isActive = true;
    const loadSlotsForDate = async () => {
      try {
        setIsLoadingDateSlots(true);
        setDateSlotError('');
        const response = await axios.get(getApiUrl('/api/meeting/interviewer/slots'), {
          params: {
            email: interviewerEmail,
            date: selectedDateValue,
          },
        });
        if (!isActive) return;
        const normalizedSlots = normalizeScheduleSlots(response.data?.slots || []);
        setDateSlots(normalizedSlots);
        setSelectedSlots(new Set(normalizedSlots.map((slot) => slot.value)));
      } catch (error) {
        if (!isActive) return;
        const message = error.response?.data?.error || 'Failed to load slots for the selected date.';
        setDateSlotError(message);
        setDateSlots([]);
        setSelectedSlots(new Set());
      } finally {
        if (isActive) {
          setIsLoadingDateSlots(false);
        }
      }
    };

    loadSlotsForDate();
    return () => {
      isActive = false;
    };
  }, [interviewerEmail, selectedDateValue]);

  const toggleSlot = (slotValue) => {
    setSelectedSlots((previous) => {
      const next = new Set(previous);
      if (next.has(slotValue)) {
        next.delete(slotValue);
      } else {
        next.add(slotValue);
      }
      return next;
    });
  };

  const toggleRecipient = (recipientEmail) => {
    setSelectedRecipientEmails((previous) => {
      const next = new Set(previous);
      if (next.has(recipientEmail)) {
        next.delete(recipientEmail);
      } else {
        next.add(recipientEmail);
      }
      if (!next.size && primaryCandidate.candidate_email) {
        next.add(primaryCandidate.candidate_email);
      }
      return next;
    });
  };

  const handleSendSlots = async () => {
    const chosenSlots = dateSlots.filter((slot) => selectedSlots.has(slot.value));

    if (!selectedRecipientCount) {
      setSubmitError('Select at least one candidate before sending slot options.');
      return;
    }

    if (!chosenSlots.length) {
      setSubmitError('Select at least one slot to send to the candidate.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await axios.post(getApiUrl('/api/meeting/schedule/request'), {
        candidate_email: candidateEmail,
        candidate_name: candidateName,
        job_id: candidate?.job_id || jobDetails?.job_id || '',
        candidates: selectedRecipients.map((item) => ({
          candidate_email: item.candidate_email,
          candidate_name: item.candidate_name,
          job_id: item.job_id,
        })),
        interviewer_email: interviewer?.email,
        interviewer_name: interviewer?.name,
        slot_options: chosenSlots,
        created_by_email: getStoredMeetingOwnerEmail(),
        meeting_provider: meetingProvider,
      });

      setSubmitSuccess(response.data);
      toast.success('Meeting slots sent to candidate.');
    } catch (requestError) {
      const message = requestError.response?.data?.error || 'Failed to send meeting slots.';
      setSubmitError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Candidate Meeting Scheduling</DialogTitle>
          <DialogDescription>
            Review available interview slots and share the selected options with the candidate.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[calc(90vh-190px)] overflow-y-auto pr-1">
          <Card className="p-4 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Text className="font-medium">Candidate Delivery</Text>
                <Text size="sm" variant="muted">
                  {selectedRecipientCount === 1
                    ? selectedRecipients[0]?.candidate_email || candidateEmail || 'Candidate email unavailable'
                    : `${selectedRecipientCount} next-round candidates selected`}
                </Text>
              </div>
              <Badge variant="outline" className="text-sky-700 border-sky-200 bg-sky-50">
                Interviewer: {interviewer?.name || 'Connected'}
              </Badge>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="border-slate-300 bg-white text-slate-700">
                Date: {selectedDateLabel}
              </Badge>
              <Badge variant="outline" className="border-slate-300 bg-white text-slate-700">
                Selected Slots: {selectedSlotCount}
              </Badge>
              <Badge variant="outline" className="border-slate-300 bg-white text-slate-700">
                Candidates: {selectedRecipientCount}
              </Badge>
            </div>
            <Text size="sm" variant="muted">
              Select the slots you want to share. Candidates selected below will receive the same interview options, and the first successful confirmation will reserve the slot automatically.
            </Text>
            {!hasSelectedSlots && !isLoadingDateSlots && dateSlots.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                <Text size="sm" className="text-amber-800">
                  Choose at least one slot before sending the interview options.
                </Text>
              </div>
            )}
            {!selectedRecipientCount && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                <Text size="sm" className="text-amber-800">
                  Choose at least one next-round candidate before sending the interview options.
                </Text>
              </div>
            )}
          </Card>

          <Card className="p-4 space-y-3">
            <Text className="font-medium">Video platform</Text>
            <Text size="sm" variant="muted">
              Choose how interviews are hosted when a candidate confirms a slot.
            </Text>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50">
                <input
                  type="radio"
                  name="meeting-provider-multi"
                  className="h-4 w-4 accent-sky-600"
                  checked={meetingProvider === 'google_meet'}
                  onChange={() => setMeetingProvider('google_meet')}
                />
                <span className="text-sm font-medium text-slate-800">Google Meet</span>
              </label>
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50">
                <input
                  type="radio"
                  name="meeting-provider-multi"
                  className="h-4 w-4 accent-sky-600"
                  checked={meetingProvider === 'live_meet'}
                  onChange={() => setMeetingProvider('live_meet')}
                />
                <span className="text-sm font-medium text-slate-800">Search Job Meet</span>
              </label>
            </div>
            <Text size="xs" variant="muted">
              Interviewer default: {['search_job_meet', 'live_meet'].includes(meetingSetup?.availability?.meeting_provider_default) ? 'Search Job Meet' : 'Google Meet'}
            </Text>
          </Card>

          <div className="space-y-2">
            <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
              <Text size="sm" variant="muted">
                Next-round candidates are shown here when that data is available. If next-round selection is not available yet, active non-rejected candidates are shown so the bulk send flow can still be tested safely.
              </Text>
              <div className="space-y-2">
                <Label htmlFor="meeting-candidate-search">Search Candidates</Label>
                <Input
                  id="meeting-candidate-search"
                  value={candidateSearchQuery}
                  onChange={(event) => setCandidateSearchQuery(event.target.value)}
                  placeholder="Search by candidate name or email"
                />
              </div>
              <div className="grid gap-2 max-h-56 overflow-y-auto">
                {filteredMeetingRecipients.length ? filteredMeetingRecipients.map((recipient) => {
                  const isChecked = selectedRecipientEmails.has(recipient.candidate_email);
                  return (
                    <label
                      key={recipient.candidate_email}
                      className={cn(
                        'flex items-start gap-3 rounded-lg border px-3 py-3 transition-colors cursor-pointer',
                        isChecked ? 'border-primary bg-primary/5' : 'border-slate-200 bg-slate-50/60 hover:bg-slate-50'
                      )}
                    >
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => toggleRecipient(recipient.candidate_email)}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Text size="sm" className="font-medium text-slate-900">{recipient.candidate_name}</Text>
                          {recipient.isPrimary && (
                            <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">Primary</Badge>
                          )}
                        </div>
                        <Text size="sm" variant="muted" className="break-all">{recipient.candidate_email}</Text>
                      </div>
                    </label>
                  );
                }) : (
                  <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3">
                    <Text size="sm" className="text-slate-600">
                      No candidates match your search.
                    </Text>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Text size="sm" className="font-medium">Slot Options</Text>
            {selectedDateValue || initialDateValue ? (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="meeting-slot-date">Select Date</Label>
                  <Input
                    id="meeting-slot-date"
                    type="date"
                    value={selectedDateValue}
                    min={todayDateValue}
                    disabled={isLoadingDateSlots}
                    onChange={(event) => setSelectedDateValue(event.target.value)}
                  />
                  <Text size="xs" variant="muted">
                    Pick any future interviewer date to load the currently open slots for that day.
                  </Text>
                </div>

                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                  <Text size="sm" className="font-medium text-slate-900">{selectedDateLabel}</Text>
                  <Text size="sm" variant="muted">Choose one or more slots from the selected date.</Text>
                </div>

                {isLoadingDateSlots ? (
                  <div className="rounded-md border border-slate-200 bg-white px-3 py-6">
                    <Text size="sm" className="text-slate-600 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading available slots for the selected date...
                    </Text>
                  </div>
                ) : dateSlotError ? (
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-3">
                    <Text size="sm" className="text-red-700">{dateSlotError}</Text>
                  </div>
                ) : dateSlots.length ? (
                  <div className="grid gap-2 max-h-64 overflow-y-auto">
                    {dateSlots.map((slot) => {
                      const isSelected = selectedSlots.has(slot.value);
                      return (
                        <button
                          key={slot.value}
                          type="button"
                          onClick={() => toggleSlot(slot.value)}
                          className={cn(
                            'rounded-md border px-3 py-2 text-left text-sm transition-colors',
                            isSelected
                              ? 'border-primary bg-primary/5 text-foreground'
                              : 'border-border bg-background hover:bg-muted/40'
                          )}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span>{slot.timeLabel}</span>
                            {isSelected && <Check className="w-4 h-4 text-primary" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3">
                    <Text size="sm" className="text-amber-800">
                      No open slots are available for the selected date. Already booked slots stay hidden automatically, and you can try another future date.
                    </Text>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3">
                <Text size="sm" className="text-amber-800">
                  No sendable slots are available right now. Already booked slots are hidden automatically, and only open slots remain available.
                </Text>
              </div>
            )}
          </div>

          {submitError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
              <Text size="sm" className="text-red-700">{submitError}</Text>
            </div>
          )}

          {submitSuccess && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 space-y-1">
              <Text size="sm" className="text-emerald-700 font-medium">
                Interview slot options have been shared with {selectedRecipientCount} candidate{selectedRecipientCount === 1 ? '' : 's'}.
              </Text>
              <Text size="sm" className="text-emerald-700">
                Once a candidate confirms a preferred time from the email, that slot is reserved automatically and the final meeting details are sent to the booked participants.
              </Text>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <div className="flex gap-2">
            <Button variant="outline" onClick={onBack || onClose}>
              Back
            </Button>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
          <Button
            onClick={handleSendSlots}
            disabled={isSubmitting || isLoadingDateSlots || !dateSlots.length || !hasSelectedSlots || !selectedRecipientCount}
            style={{
              backgroundColor: getColor('primary'),
              color: getColor('primaryForeground'),
            }}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending Slots...
              </>
            ) : (
              `Send ${selectedSlotCount || 0} Slot${selectedSlotCount === 1 ? '' : 's'} to ${selectedRecipientCount || 0} Candidate${selectedRecipientCount === 1 ? '' : 's'}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GameAssessmentPopup({ candidate, jobDetails, onClose, onSuccess }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [notes, setNotes] = useState('');
  const [gameSetId, setGameSetId] = useState(1);
  const [totalLevels, setTotalLevels] = useState(2);
  const [customDataUrls, setCustomDataUrls] = useState(['', '']);
  const [copied, setCopied] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [latestAssessment, setLatestAssessment] = useState(() => {
    if (!candidate.game_status) return null;
    return {
      status: candidate.game_status,
      score: candidate.game_score,
      total_score: candidate.game_total_score,
      stars: candidate.game_stars,
      share_link: candidate.game_share_link,
      scheduled_at: candidate.game_scheduled_at,
      completed_at: candidate.game_completed_at,
      assessment_id: candidate.game_assessment_id,
    };
  });

  const jobTitle = jobDetails?.job_title || candidate.job_title || 'Technical';
  const shareLink = latestAssessment?.share_link || '';

  const friendlyDateTime = (value) => {
    if (!value) return '—';
    try {
      const date = new Date(value);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return value;
    }
  };

  const prettyStatus = (status) => {
    if (!status) return 'Not Scheduled';
    return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const handleCopyLink = async () => {
    if (!shareLink || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link', error);
    }
  };

  const handleDataUrlChange = (index, value) => {
    setCustomDataUrls((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const getEmployerContext = () => {
    if (typeof window === 'undefined') {
      return { employerEmail: null, companyId: null };
    }
    return {
      employerEmail: sessionStorage.getItem('employer_email') || localStorage.getItem('employer_email'),
      companyId: localStorage.getItem('companyAccountId') || sessionStorage.getItem('companyAccountId'),
    };
  };

  const handleScheduleGame = async () => {
    try {
      setIsSubmitting(true);
      setStatusMessage('');
      const { employerEmail, companyId } = getEmployerContext();

      if (!employerEmail) {
        toast.error('Please log in as an employer to send the assessment.');
        setIsSubmitting(false);
        return;
      }

      if (!companyId) {
        toast.error('Company information is missing. Please log in again.');
        setIsSubmitting(false);
        return;
      }

      const payload = {
        candidate_email: candidate.candidate_email,
        candidate_name: candidate.name || candidate.full_name,
        job_id: candidate.job_id || jobDetails?.job_id,
        job_title: jobTitle,
        employer_email: employerEmail,
        company_id: companyId,
        game_set_id: Number(gameSetId) || 1,
        total_levels: Number(totalLevels) || 2,
        send_email: sendEmail,
        notes: notes.trim() || undefined,
        data_urls: customDataUrls.map((url) => url.trim()).filter(Boolean),
      };

      const shareBaseUrl = resolveGameBaseUrl();
      if (shareBaseUrl) {
        payload.share_base_url = shareBaseUrl;
      }

      const response = await fetch(`${API_BASE_URL}/api/game_assessments/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to schedule assessment');
      }

      const result = await response.json();
      setLatestAssessment(result.assessment || null);
      if (result.assessment) {
        setGameSetId(result.assessment.game_set_id || 1);
        setTotalLevels(result.assessment.total_levels || 2);
      }
      setStatusMessage('Assessment link generated successfully.');
      setCopied(false);
      if (typeof onSuccess === 'function') {
        onSuccess(result.assessment);
      }
    } catch (error) {
      console.error('Failed to schedule game assessment', error);
      setStatusMessage(error.message);
      toast.error(error.message || 'Unable to schedule assessment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scoreSummary = latestAssessment?.score != null && latestAssessment?.total_score != null
    ? `${latestAssessment.score}/${latestAssessment.total_score}`
    : '—';

  const hasExistingAssessment = Boolean(latestAssessment);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{hasExistingAssessment ? 'Manage Game Assessment' : 'Send Game Assessment'}</DialogTitle>
          <DialogDescription>
            Share an interactive skill assessment with <strong>{candidate.name || candidate.candidate_email}</strong> for
            the <strong>{jobTitle}</strong> role. Candidates play on <strong>evaluate.aievaluator.in</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-3 text-center">
            <Text size="xs" variant="muted" className="mb-1">Status</Text>
            <Text className="font-semibold">{prettyStatus(latestAssessment?.status)}</Text>
          </Card>
          <Card className="p-3 text-center">
            <Text size="xs" variant="muted" className="mb-1">Score</Text>
            <Text className="font-semibold">{scoreSummary}</Text>
          </Card>
          <Card className="p-3 text-center">
            <Text size="xs" variant="muted" className="mb-1">Scheduled</Text>
            <Text className="font-semibold">{friendlyDateTime(latestAssessment?.scheduled_at)}</Text>
          </Card>
          <Card className="p-3 text-center">
            <Text size="xs" variant="muted" className="mb-1">Completed</Text>
            <Text className="font-semibold">{friendlyDateTime(latestAssessment?.completed_at)}</Text>
          </Card>
        </div>

        {shareLink && (
          <div className="space-y-2">
            <Label>Candidate Link</Label>
            <div className="flex gap-2">
              <Input type="text" value={shareLink} readOnly className="flex-1" />
              <Button
                variant="outline"
                onClick={handleCopyLink}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open(shareLink, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open
              </Button>
            </div>
            <Text size="xs" variant="muted">
              Share this link with the candidate if you want to send it manually.
            </Text>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="send-email"
              checked={sendEmail}
              onCheckedChange={(checked) => setSendEmail(checked)}
            />
            <Label htmlFor="send-email" className="cursor-pointer">
              Send assessment email from SearchJob AI
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Internal Notes (optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add context for your team. Not shared with the candidate."
              rows={3}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Button
            variant="ghost"
            onClick={() => setAdvancedOpen((prev) => !prev)}
            className="w-full"
          >
            {advancedOpen ? (
              <>
                <ChevronUp className="w-4 h-4 mr-2" />
                Hide Advanced Options
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-2" />
                Show Advanced Options
              </>
            )}
          </Button>

          {advancedOpen && (
            <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg">
              <div className="space-y-2">
                <Label htmlFor="game-set-id">Game Set ID</Label>
                <Input
                  id="game-set-id"
                  type="number"
                  min="1"
                  value={gameSetId}
                  onChange={(e) => setGameSetId(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="total-levels">Total Levels</Label>
                <Input
                  id="total-levels"
                  type="number"
                  min="1"
                  value={totalLevels}
                  onChange={(e) => setTotalLevels(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="data-url-1">Primary Data URL</Label>
                <Input
                  id="data-url-1"
                  type="text"
                  placeholder="https://evaluate.aievaluator.in/data/game_data_set_1.json"
                  value={customDataUrls[0]}
                  onChange={(e) => handleDataUrlChange(0, e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="data-url-2">Secondary Data URL</Label>
                <Input
                  id="data-url-2"
                  type="text"
                  placeholder="https://evaluate.aievaluator.in/data/game_data_set_2.json"
                  value={customDataUrls[1]}
                  onChange={(e) => handleDataUrlChange(1, e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        {statusMessage && (
          <div className={cn(
            "p-3 rounded-lg",
            statusMessage.includes('success') ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"
          )}>
            <Text size="sm">{statusMessage}</Text>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            onClick={handleScheduleGame}
            disabled={isSubmitting}
            style={{
              backgroundColor: getColor('primary'),
              color: getColor('primaryForeground'),
            }}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : hasExistingAssessment ? (
              'Resend Assessment'
            ) : (
              'Send Assessment'
            )}
          </Button>
        </DialogFooter>

        <Text size="xs" variant="muted" className="text-center">
          Candidates receive the link instantly. Results sync back to the candidate card when they finish the game.
        </Text>
      </DialogContent>
    </Dialog>
  );
}

export default CandidateCard;
